import type { MessagePack } from "./en.ts";

/**
 * The Khmer pack.
 *
 * **Needs a native Khmer reviewer before it ships.** POS and accounting
 * terminology — "void", "reverse checkout", "shift handover", "credit order" —
 * has established trade usage in Cambodian restaurants that a
 * plausible-sounding translation will miss, and generating ~2,000 strings
 * unreviewed is not an agent task.
 *
 * So: the complete key set exists, the machinery works, and every value is
 * marked for review rather than declared done. `km.status` is
 * `review_required` in the locale registry and stays there until a reviewer
 * signs it off. This is a soft blocker — it does not stop the build, but the
 * locale is not shippable.
 */
export const km: MessagePack = {
  "app.name": "TOVI",
  "app.description": "ការិយាល័យគ្រប់គ្រងភោជនីយដ្ឋាន",
  "app.operatorName": "TOVI Operator",
  "app.operatorDescription": "ផ្ទាំងគ្រប់គ្រងសម្រាប់ប្រតិបត្តិករវេទិកា",

  "nav.dashboard": "ផ្ទាំងគ្រប់គ្រង",
  "nav.menu": "ម៉ឺនុយ",
  "nav.operations": "ប្រតិបត្តិការ",
  "nav.staff": "បុគ្គលិក",
  "nav.roles": "តួនាទី",
  "nav.stations": "ស្ថានីយ",
  "nav.printers": "ម៉ាស៊ីនបោះពុម្ព",
  "nav.pairTill": "ភ្ជាប់ម៉ាស៊ីនលក់",
  "nav.printLog": "កំណត់ត្រាបោះពុម្ព",
  "nav.receiptTemplates": "ទម្រង់វិក្កយបត្រ",
  "nav.banners": "បដា",
  "nav.notifications": "ការជូនដំណឹង",
  "nav.operationLog": "កំណត់ត្រាប្រតិបត្តិការ",
  "nav.inventory": "ស្តុក",
  "nav.supplies": "សម្ភារៈ",
  "nav.suppliers": "អ្នកផ្គត់ផ្គង់",
  "nav.warehouses": "ឃ្លាំង",
  "nav.units": "ឯកតា",
  "nav.supplyCategories": "ប្រភេទសម្ភារៈ",
  "nav.brands": "ម៉ាក",
  "nav.chain": "ខ្សែសង្វាក់",
  "nav.substores": "ហាង",
  "nav.invoice": "វិក្កយបត្រ",
  "nav.trading": "ការធ្វើអាជីវកម្ម",

  "staff.title": "បុគ្គលិក",
  "staff.description": "នរណាធ្វើការនៅទីនេះ អ្វីដែលពួកគេអាចធ្វើបាន និងនរណាអាចប្រើម៉ាស៊ីនគិតលុយ។",
  "staff.searchPlaceholder": "ស្វែងរកតាមឈ្មោះ ឈ្មោះអ្នកប្រើ ឬលេខ",
  "staff.allRoles": "គ្រប់តួនាទី",
  "staff.noRole": "គ្មានតួនាទី",
  "staff.allStatuses": "គ្រប់ស្ថានភាព",
  "staff.tillAccess": "ការប្រើម៉ាស៊ីនគិតលុយ",
  "staff.allTillAccess": "គ្រប់ការប្រើម៉ាស៊ីនគិតលុយ",
  "staff.tillAllowed": "អាចប្រើម៉ាស៊ីនគិតលុយ",
  "staff.tillDenied": "មិនអាចប្រើម៉ាស៊ីនគិតលុយ",
  "staff.new": "បុគ្គលិកថ្មី",
  "staff.edit": "កែសម្រួលបុគ្គលិក",
  "staff.record": "កំណត់ត្រា",
  "staff.name": "ឈ្មោះ",
  "staff.username": "ឈ្មោះអ្នកប្រើ",
  "staff.number": "លេខបុគ្គលិក",
  "staff.role": "តួនាទី",
  "staff.cashRegister": "អាចប្រើម៉ាស៊ីនគិតលុយ",
  "staff.cashRegisterHint":
    "ដាច់ដោយឡែកពីតួនាទីរបស់ពួកគេ។ នរណាម្នាក់អាចទទួលការបញ្ជាទិញ ដោយមិនអាចទទួលប្រាក់។",
  "staff.posPermissions": "សិទ្ធិលើម៉ាស៊ីនគិតលុយ",
  "staff.inventoryPermissions": "សិទ្ធិលើស្តុក",
  "staff.inventoryHint": "ផ្តល់ដាច់ដោយឡែកពីសិទ្ធិលើម៉ាស៊ីនគិតលុយ។",
  "staff.deleteTitle": "ដកបុគ្គលិកនេះចេញមែនទេ?",
  "staff.deleteBody":
    "ពួកគេនឹងមិនអាចចូលប្រើម៉ាស៊ីនគិតលុយបានទៀតទេ។ ការបញ្ជាទិញដែលពួកគេបានធ្វើនៅតែរក្សាឈ្មោះរបស់ពួកគេ។",
  "staff.photo": "រូបថត",
  "staff.photoHint": "បង្ហាញនៅជាប់ឈ្មោះរបស់ពួកគេនៅក្នុងបញ្ជី។",
  "staff.photoAdd": "បន្ថែមរូបថត",
  "staff.photoReplace": "ប្តូររូបថត",
  "staff.photoRemove": "លុបរូបថត",
  "staff.viewPhoto": "មើលរូបថត",
  "staff.cropTitle": "កាត់រូបថត",
  "staff.cropHint": "អូសដើម្បីផ្លាស់ទី។ មានតែផ្នែកខាងក្នុងរង្វង់ប៉ុណ្ណោះដែលត្រូវរក្សាទុក។",
  "staff.zoom": "ពង្រីក",
  "staff.cropConfirm": "ប្រើរូបថតនេះ",
  "staff.removePhotoTitle": "លុបរូបថតនេះមែនទេ?",
  "staff.removePhotoBody":
    "អក្សរកាត់របស់ពួកគេនឹងបង្ហាញជំនួសវិញ រហូតដល់មាននរណាម្នាក់បន្ថែមរូបថតថ្មី។",

  "staff.setPassword": "កំណត់ពាក្យសម្ងាត់",
  "staff.password": "ពាក្យសម្ងាត់",
  "staff.passwordConfirm": "បញ្ជាក់ពាក្យសម្ងាត់",
  "staff.passwordHint":
    "យ៉ាងតិចប្រាំបីតួអក្សរ។ ពួកគេចូលប្រើកម្មវិធីបុគ្គលិកដោយប្រើឈ្មោះអ្នកប្រើ និងពាក្យសម្ងាត់នេះ។",
  "staff.passwordMismatch": "ពាក្យសម្ងាត់ទាំងពីរមិនត្រូវគ្នាទេ។",
  "staff.passwordSaved":
    "បានកំណត់ពាក្យសម្ងាត់។ ពួកគេត្រូវបានចាកចេញពីគ្រប់ទីកន្លែង ហើយអាចចូលវិញដោយប្រើពាក្យសម្ងាត់ថ្មី។",

  "roles.title": "តួនាទី",
  "roles.description": "អ្វីដែលការងារនីមួយៗត្រូវបានអនុញ្ញាតឲ្យធ្វើ។",
  "roles.new": "តួនាទីថ្មី",
  "roles.members": "មនុស្ស",
  "roles.permissions": "សិទ្ធិ",
  "roles.seeded": "មានស្រាប់",
  "roles.granted": "បានផ្តល់ {count}",
  "roles.record": "តួនាទី",
  "roles.code": "លេខកូដ",
  "roles.note": "កំណត់ចំណាំ",
  "roles.noneGranted": "គ្មានសិទ្ធិណាមួយត្រូវបានផ្តល់ទេ។",
  "roles.catalogueFailed":
    "មិនអាចផ្ទុកបញ្ជីសិទ្ធិបានទេ ដូច្នេះសិទ្ធិនីមួយៗត្រូវបានបង្ហាញតាមកូនសោររបស់វា។",

  "stations.title": "ស្ថានីយ",
  "stations.description":
    "កន្លែងដែលការងារកើតឡើង។ មុខម្ហូបបញ្ជូនទៅស្ថានីយ មិនមែនទៅម៉ាស៊ីនបោះពុម្ពទេ។",
  "stations.new": "ស្ថានីយថ្មី",
  "stations.record": "កំណត់ត្រា",
  "stations.name": "ឈ្មោះ",
  "stations.nameHint": "ឈ្មោះដែលបុគ្គលិកនៅទីនោះហៅវា — ចង្ក្រានក្តៅ បារ បង្អែម។",
  "stations.printers": "ម៉ាស៊ីនបោះពុម្ព",
  "stations.noPrinters": "គ្មានម៉ាស៊ីនបោះពុម្ព",
  "stations.noPrintersHint":
    "ស្ថានីយគ្មានម៉ាស៊ីនបោះពុម្ពនៅតែទទួលការបញ្ជូន ប៉ុន្តែមិនបោះពុម្ពអ្វីទេ។",
  "stations.deleteTitle": "លុបស្ថានីយនេះមែនទេ?",
  "stations.deleteBody":
    "មានតែស្ថានីយដែលគ្មានអ្វីចង្អុលមកទេទើបលុបបាន៖ ម៉ាស៊ីនបោះពុម្ពដែលឈរនៅទីនោះ និងមុខម្ហូបដែលបញ្ជូនមកទីនោះ ត្រូវផ្លាស់ទីជាមុនសិន។",
  "printers.title": "ម៉ាស៊ីនបោះពុម្ព",
  "printers.description": "ឧបករណ៍ដែលបានភ្ជាប់ និងស្ថានីយណាដែលនីមួយៗឈរនៅ។",
  "printers.new": "ម៉ាស៊ីនបោះពុម្ពថ្មី",
  "printers.record": "កំណត់ត្រា",
  "printers.name": "ឈ្មោះ",
  "printers.model": "ម៉ូដែល",
  "printers.connection": "ការតភ្ជាប់",
  "printers.address": "អាសយដ្ឋាន",
  "printers.addressHint": "លេខ IP និងច្រកសម្រាប់ LAN ឬអាសយដ្ឋាន MAC សម្រាប់ប៊្លូធូស។",
  "printers.role": "បោះពុម្ព",
  "printers.station": "ស្ថានីយ",
  "printers.stationHint": "អ្វីគ្រប់យ៉ាងដែលបញ្ជូនទៅស្ថានីយនេះនឹងបោះពុម្ពនៅទីនេះ។",
  "printers.stationNone": "ជ្រើសរើសស្ថានីយ",
  "printers.copies": "ចំនួនច្បាប់",
  "printers.bind": "ភ្ជាប់",
  "printers.unbind": "ផ្តាច់",
  "printers.status": "ស្ថានភាព",
  "printers.deleteTitle": "ដកម៉ាស៊ីនបោះពុម្ពនេះចេញមែនទេ?",
  "printers.deleteBody":
    "អ្វីៗដែលបញ្ជូនមកវានឹងឈប់បោះពុម្ព។ ម៉ាស៊ីនបោះពុម្ពដែលមុខម្ហូបណាមួយកំណត់ជាឧបករណ៍តែមួយគត់នៅស្ថានីយ មិនអាចដកចេញបានទេ។",
  "printerRole.receipt": "វិក្កយបត្រ",
  "printerRole.station": "ប័ណ្ណស្ថានីយ",
  "printerRole.label": "ស្លាក",
  "printerStatus.unbound": "មិនបានភ្ជាប់",
  "printerStatus.online": "កំពុងដំណើរការ",
  "printerStatus.offline": "ផ្តាច់ការតភ្ជាប់",
  "printerStatus.error": "មានបញ្ហា",

  "printLog.title": "កំណត់ត្រាបោះពុម្ព",
  "printLog.description": "រាល់ឯកសារដែលបញ្ជូនទៅម៉ាស៊ីនបោះពុម្ព និងថាតើវាបានទៅដល់ឬអត់។",
  "printLog.document": "ឯកសារ",
  "printLog.printer": "ម៉ាស៊ីនបោះពុម្ព",
  "printLog.queued": "ចូលជួរ",
  "printLog.attempts": "ចំនួនព្យាយាម",
  "printLog.reprint": "បោះពុម្ពម្តងទៀត",
  "printLog.failedHint": "ប័ណ្ណស្ថានីយដែលបោះពុម្ពមិនជោគជ័យ គឺជាការបញ្ជាទិញដែលគ្មាននរណាកំពុងចម្អិន។",
  "printLog.record": "កិច្ចការបោះពុម្ព",
  "printLog.reference": "លេខយោង",
  "printLog.printed": "បានបោះពុម្ព",
  "printLog.failure": "មូលហេតុនៃការបរាជ័យ",
  "printJobStatus.queued": "កំពុងរង់ចាំ",
  "printJobStatus.printing": "កំពុងបោះពុម្ព",
  "printJobStatus.printed": "បានបោះពុម្ព",
  "printJobStatus.failed": "មិនជោគជ័យ",
  "printJobStatus.cancelled": "បានលុបចោល",
  "documentType.customer_receipt": "វិក្កយបត្រអតិថិជន",
  "documentType.station_ticket": "ប័ណ្ណស្ថានីយ",
  "documentType.label": "ស្លាក",
  "documentType.shift_report": "របាយការណ៍វេន",
  "documentType.drawer_kick": "បើកថតប្រាក់",

  "templates.title": "ទម្រង់វិក្កយបត្រ",
  "templates.description": "អ្វីដែលបោះពុម្ព ជាភាសាណា និងរបៀបបញ្ជូនទៅម៉ាស៊ីនបោះពុម្ព។",
  "templates.new": "ទម្រង់ថ្មី",
  "templates.language": "ភាសា",
  "templates.lineWidth": "ចំនួនតួអក្សរក្នុងមួយបន្ទាត់",
  "templates.lineWidthHint": "៣២ លើក្រដាស 58mm និង ៤៨ លើ 80mm។",
  "templates.renderMode": "បញ្ជូនជា",
  "templates.text": "អក្សរ",
  "templates.raster": "រូបភាព",
  "templates.auto": "សម្រេចតាមភាសា",
  "templates.rasterForced": "ភាសានេះបោះពុម្ពជារូបភាព",
  "templates.rasterReason.complexScript":
    "{script} មិនអាចបោះពុម្ពដោយប្រើពុម្ពអក្សរដែលមានស្រាប់ក្នុងម៉ាស៊ីនបោះពុម្ពកម្តៅបានទេ ដូច្នេះវិក្កយបត្រនេះត្រូវបញ្ជូនជារូបភាព។ វាយឺតជាងតាមប៊្លូធូស ធៀបនឹងតាមខ្សែ។",
  "templates.rasterReason.templateChoice": "ទម្រង់នេះបោះពុម្ពជារូបភាពជានិច្ច។",
  "templates.preview": "មើលជាមុន",
  "templates.showLogo": "បង្ហាញនិមិត្តសញ្ញា",
  "templates.headerText": "ក្បាលទំព័រ",
  "templates.footerText": "បាតទំព័រ",
  "templates.showTaxBreakdown": "បង្ហាញការបែងចែកពន្ធ",
  "templates.showTaxRegistration": "បង្ហាញព័ត៌មានចុះបញ្ជីពន្ធដារ",
  "templates.showItemCodes": "បង្ហាញលេខកូដមុខម្ហូប",
  "templates.showServiceCharge": "បង្ហាញថ្លៃសេវា",
  "templates.showSecondCurrency": "បង្ហាញរូបិយប័ណ្ណទីពីរ",
  "templates.showQrCode": "បង្ហាញកូដ QR",

  "banners.title": "បដា",
  "banners.description": "ការផ្សាយពាណិជ្ជកម្មលើម៉ឺនុយ QR។",
  "banners.new": "បដាថ្មី",
  "banners.record": "កំណត់ត្រា",
  "banners.image": "អាសយដ្ឋានរូបភាព",
  "banners.link": "តំណ",
  "banners.schedule": "បង្ហាញ",
  "banners.startsAt": "ចាប់ពី",
  "banners.endsAt": "រហូតដល់",
  "banners.live": "កំពុងបង្ហាញ",
  "banners.notLive": "មិនកំពុងបង្ហាញ",
  "banners.deleteTitle": "លុបបដានេះមែនទេ?",
  "banners.deleteBody": "វានឹងចេញពីម៉ឺនុយ QR ភ្លាមៗ។",

  "alerts.title": "ការជូនដំណឹង",
  "alerts.description": "អ្វីដែលក្រុមការងារត្រូវបានប្រាប់ និងតាមរបៀបណា។",
  "alerts.push": "ការជូនដំណឹងតាមទូរស័ព្ទ",
  "alerts.voice": "អានឮៗនៅម៉ាស៊ីនគិតលុយ",
  "alerts.event": "ព្រឹត្តិការណ៍",
  "alerts.language": "អានជា",
  "alerts.repeat": "អានឡើងវិញ",
  "alerts.repeatHint": "ស្ថានីយគឺជាកន្លែងដែលមានសំឡេងរំខាន។",
  "event.order.new": "មានការបញ្ជាទិញថ្មី",
  "event.order.paid": "ការបញ្ជាទិញត្រូវបានទូទាត់",
  "event.order.cancelled": "ការបញ្ជាទិញត្រូវបានលុបចោល",
  "event.order.refunded": "ការបញ្ជាទិញត្រូវបានសងប្រាក់វិញ",
  "event.print.failed": "ឯកសារបោះពុម្ពមិនជោគជ័យ",
  "event.shift.warning": "វេនត្រូវការការយកចិត្តទុកដាក់",
  "event.stock.sold_out": "មុខម្ហូបអស់ស្តុក",

  "pairTill.title": "ភ្ជាប់ម៉ាស៊ីនលក់",
  "pairTill.description":
    "ម៉ាស៊ីនលក់បង្ហាញលេខកូដប្រាំបីតួអក្សរ។ សូមវាយបញ្ចូលលេខកូដនោះនៅទីនេះ ដើម្បីអនុញ្ញាត ឬបដិសេធវា។",
  "pairTill.codeLabel": "លេខកូដម៉ាស៊ីនលក់",
  "pairTill.lookUp": "ស្វែងរក",
  "pairTill.sessionNotice": "ម៉ាស៊ីនលក់នឹងចូលប្រើគណនីរបស់អ្នក រហូតដល់មាននរណាម្នាក់ចូលនៅទីនោះ។",
  "pairTill.approve": "ភ្ជាប់ម៉ាស៊ីនលក់នេះ",
  "pairTill.deny": "បដិសេធ",
  "pairTill.approved":
    "ម៉ាស៊ីនលក់ត្រូវបានភ្ជាប់ហើយ វានឹងបន្តដំណើរការដោយខ្លួនឯងក្នុងរយៈពេលប៉ុន្មានវិនាទីទៀត។",
  "pairTill.denied": "ម៉ាស៊ីនលក់ត្រូវបានបដិសេធ។",
  "pairTill.pairAnother": "ភ្ជាប់ម៉ាស៊ីនលក់ផ្សេងទៀត",
  "pairTill.notFound": "គ្មានម៉ាស៊ីនលក់ណាមួយកំពុងបង្ហាញលេខកូដនោះទេ។",
  "pairTill.expired": "លេខកូដនោះបានផុតកំណត់ហើយ។",
  "pairTill.forbidden": "អ្នកមិនមានសិទ្ធិភ្ជាប់ម៉ាស៊ីនលក់ទេ។",
  "pairTill.alreadyUsed": "លេខកូដនេះត្រូវបានប្រើរួចហើយ។",
  "pairTill.claimedElsewhere": "មាននរណាម្នាក់ផ្សេងទៀតកំពុងមើលលេខកូដនេះរួចហើយ។",
  "pairTill.identify":
    "លេខកូដនេះសម្គាល់អ្នកម្នាក់ មិនមែនជាម៉ាស៊ីនលក់ទេ។ មិនអាចអនុញ្ញាតបាននៅទីនេះទេ។",

  "operationLog.title": "កំណត់ត្រាប្រតិបត្តិការ",
  "operationLog.description": "នរណាបានផ្លាស់ប្តូរអ្វី និងពេលណា។ កំណត់ត្រានេះមិនអាចកែ ឬលុបបានទេ។",
  "operationLog.when": "ពេលណា",
  "operationLog.who": "នរណា",
  "operationLog.what": "សកម្មភាព",
  "operationLog.entity": "ប៉ះពាល់ដល់",
  "operationLog.fields": "វាលដែលបានផ្លាស់ប្តូរ",
  "operationLog.record": "កំណត់ត្រា",
  "operationLog.actorKind": "ធ្វើក្នុងនាម",
  "operationLog.entityKind": "ប្រភេទកំណត់ត្រា",
  "operationLog.recordId": "លេខសម្គាល់កំណត់ត្រា",
  "operationLog.fieldsHint":
    "តម្លៃមុន និងក្រោយ ត្រូវបានរក្សាទុកជាមួយកំណត់ត្រា ប៉ុន្តែមិនបង្ហាញនៅទីនេះទេ។",
  "actorKind.merchant": "អ្នកជំនួញ",
  "actorKind.operator": "ប្រតិបត្តិករវេទិកា",

  "trading.title": "ការធ្វើអាជីវកម្ម",
  "trading.description": "ម៉ោងរបស់អ្នក របៀបដែលការបញ្ជាទិញដំណើរការ និងអ្វីដែលម៉ាស៊ីនបង្ហាញ។",
  "trading.hours": "ម៉ោង",
  "trading.openTime": "បើក",
  "trading.closeTime": "បិទ",
  "trading.hoursHint":
    "ម៉ោងរបស់អ្នកកំណត់ថាការលក់មួយជាកម្មសិទ្ធិថ្ងៃធ្វើការណា ហើយដូច្នេះវាកំណត់រូបរាងរបាយការណ៍ទាំងអស់។",
  "trading.status": "ស្ថានភាពហាង",
  "trading.hoursLog": "ការផ្លាស់ប្តូរម៉ោងរបស់អ្នក",
  "trading.ordering": "ការបញ្ជាទិញ",
  "trading.payFirst": "អតិថិជនបង់ប្រាក់មុនពេលអាហារមកដល់",
  "trading.requirePax": "សួរថាមានមនុស្សប៉ុន្មាននាក់ញ៉ាំ",
  "trading.cartSharing": "អតិថិជនច្រើននាក់អាចចែករំលែកការបញ្ជាទិញតែមួយ",
  "trading.autoCancel": "លុបចោលការបញ្ជាទិញដែលមិនបានទទួលយកបន្ទាប់ពី",
  "trading.minutes": "នាទី",
  "trading.printing": "ការបោះពុម្ព",
  "trading.autoPrintReceipt": "បោះពុម្ពបង្កាន់ដៃដោយស្វ័យប្រវត្តិពេលបង់ប្រាក់",
  "trading.staffTerminalPrinter": "ម៉ាស៊ីនបុគ្គលិកបោះពុម្ពដោយផ្ទាល់ទៅម៉ាស៊ីនបោះពុម្ព Wi-Fi",
  "trading.staffTerminalPrinterHint":
    "បើកវាតែពេលម៉ាស៊ីនគិតលុយបិទប៉ុណ្ណោះ។ បើភ្ជាប់ទាំងពីរ ម៉ាស៊ីនគិតលុយនឹងឈប់បោះពុម្ពបង្កាន់ដៃ។",
  "trading.display": "អ្វីដែលម៉ាស៊ីនបង្ហាញ",
  "trading.displayTables": "បង្ហាញប្លង់តុ",
  "delivery.title": "វេទិកាដឹកជញ្ជូន",
  "delivery.description": "វេទិកាដែលអ្នកលក់តាម និងអ្វីដែលនីមួយៗយក។",
  "delivery.platforms": "វេទិកា",
  "delivery.name": "បង្ហាញជា",
  "delivery.commission": "កម្រៃជើងសារ",
  "delivery.commissionHint": "ជាភាគរយ។ 15 មានន័យថា 15%។",
  "delivery.noneInMarket": "មិនទាន់មានវេទិកាដឹកជញ្ជូនក្នុងទីផ្សារនេះទេ។",
  "trading.delivers": "អាហារយកទៅអាចដឹកជញ្ជូនបាន",
  "trading.deliveryFee": "ថ្លៃដឹកជញ្ជូន",
  "trading.autoAccept": "ទទួលយកដោយស្វ័យប្រវត្តិ",
  "trading.autoAcceptHint":
    "ការបញ្ជាទិញតាមឆានែលដែលបានធីកនឹងទៅផ្ទាល់ដល់ស្ថានីយ។ ឯផ្សេងទៀតរង់ចាំមនុស្សពិនិត្យ។",
  "trading.showItemSales": "បង្ហាញអតិថិជននូវចំនួនដែលបានលក់",
  "trading.voiceBroadcast": "អានការបញ្ជាទិញថ្មីឮៗនៅម៉ាស៊ីនគិតលុយ",
  "orderSource.cashier": "ម៉ាស៊ីនគិតលុយ",
  "orderSource.staff_app": "កម្មវិធីបុគ្គលិក",
  "orderSource.qr": "ការបញ្ជាទិញតាម QR",
  "orderSource.delivery": "ដឹកជញ្ជូន",
  "orderSource.back_office": "ការិយាល័យខាងក្រោយ",
  "trading.showItemImages": "បង្ហាញរូបថតមុខម្ហូបនៅម៉ាស៊ីនគិតលុយ",
  "trading.showRemainingStock": "បង្ហាញស្តុកនៅសល់",
  "trading.geofence": "ចម្ងាយសម្រាប់ការបញ្ជាទិញ",
  "trading.geofenceHint":
    "អតិថិជនត្រូវនៅក្នុងចម្ងាយប៉ុន្មានម៉ែត្រនេះទើបអាចបញ្ជាទិញបាន។ ចម្ងាយតិចបំផុតដែលអនុញ្ញាតគឺ {min}។",
  "trading.pickupNumbers": "លេខទទួលអាហារ",
  "trading.pickupHint": "វិលជុំរាល់ថ្ងៃរវាងលេខទាំងនេះ។ ទីផ្សាររបស់អ្នកអនុញ្ញាត {min} ដល់ {max}។",
  "chain.title": "ហាង",
  "chain.description": "គ្រប់ទីតាំងទាំងអស់ក្នុងក្រុម និងអ្វីដែលនីមួយៗអាចកំណត់ដោយខ្លួនឯង។",
  "chain.new": "បន្ថែមហាង",
  "chain.headOffice": "ការិយាល័យកណ្តាល",
  "chain.branch": "សាខា",
  "chain.name": "ឈ្មោះ",
  "chain.address": "អាសយដ្ឋាន",
  "chain.slug": "អាសយដ្ឋានគេហទំព័រ",
  "chain.slugHint": "ប្រើដោយគេហទំព័របញ្ជាទិញ QR។ អក្សរតូច លេខ និងសញ្ញាដក។",
  "chain.phone": "ទូរស័ព្ទ",
  "chain.timezone": "ល្វែងម៉ោង",
  "chain.hours": "ម៉ោងបើកទ្វារ",
  "chain.currency": "រូបិយប័ណ្ណ",
  "chain.items": "ទំនិញ",
  "chain.tables": "តុ",
  "chain.staff": "បុគ្គលិក",
  "chain.record": "កំណត់ត្រា",
  "chain.switchTo": "ប្តូរទៅហាងនេះ",
  "chain.capabilities": "អ្វីដែលហាងនេះអាចកំណត់បាន",
  "chain.capabilitiesHint":
    "បើទប់ស្កាត់មួយ ហាងនេះនៅតែមានសិទ្ធិ ប៉ុន្តែមិនអាចធ្វើសកម្មភាពបានទេ។ ការិយាល័យកណ្តាលនឹងកំណត់ជំនួស។",
  "chain.capabilitiesSaved": "បានរក្សាទុក។",

  "shell.switchStore": "ប្តូរហាង",
  "nav.chainMenu": "ផ្សាយម៉ឺនុយ",
  "nav.chainUsers": "សិទ្ធិចូលហាង",
  "action.done": "រួចរាល់",
  "chainMenu.title": "ម៉ឺនុយការិយាល័យកណ្តាល",
  "chainMenu.description":
    "ម៉ឺនុយដែលការិយាល័យកណ្តាលគ្រប់គ្រង។ ពេលផ្សាយទៅទីតាំងណាមួយ ទំនិញទាំងពីរនៅតែភ្ជាប់គ្នា រហូតដល់ទីតាំងនោះកែច្បាប់ចម្លងរបស់ខ្លួន។",
  "chainMenu.sku": "លេខកូដទំនិញ",
  "chainMenu.categories": "ប្រភេទ",
  "chainMenu.price": "តម្លៃ",
  "chainMenu.status": "ស្ថានភាព",
  "chainMenu.pushedTo": "ទីតាំង",
  "chainMenu.record": "កំណត់ត្រា",
  "chainMenu.push": "ផ្សាយទៅហាង",
  "chainMenu.pushCount": "ផ្សាយទំនិញ {count}",
  "chainMenu.pushTitle": "ផ្សាយទំនិញ {count}",
  "chainMenu.pushHint":
    "ជ្រើសរើសទីតាំងដែលត្រូវផ្សាយទៅ។ ទំនិញដែលទីតាំងបង្កើតដោយខ្លួនឯងមិនត្រូវប៉ះពាល់ទេ ហើយតម្លៃផ្សាយបានតែរវាងហាងដែលប្រើរូបិយប័ណ្ណដូចគ្នា។",
  "chainMenu.pushing": "កំពុងផ្សាយ…",
  "chainMenu.noTargets": "បណ្តាញនេះមិនមានទីតាំងផ្សេងក្រៅពីការិយាល័យកណ្តាលទេ។",
  "chainMenu.overwriteLocal": "សរសេរជាន់លើការកែរបស់ទីតាំង",
  "chainMenu.overwriteLocalHint":
    "បិទតាមលំនាំដើម។ ទីតាំងដែលបានកែទំនិញផ្សាយរក្សាកំណែរបស់ខ្លួន ហើយការផ្សាយរាយការណ៍ថាបានរំលង។",
  "chainMenu.result": "អ្វីដែលទីតាំងនីមួយៗបានទទួល",
  "chainMenu.resultLine": "បន្ថែម {created} កែ {updated}។",
  "chainMenu.resultSkipped": "រំលង {count} — បានកែនៅមូលដ្ឋាន។",
  "chainUsers.title": "សិទ្ធិចូលហាង",
  "chainUsers.description":
    "ទីតាំងណាខ្លះដែលគណនីការិយាល័យនីមួយៗអាចធ្វើការបាន។ គណនីថ្មីចូលបានទាំងបណ្តាញ រហូតដល់កំណត់ត្រឹមនៅទីនេះ។",
  "chainUsers.name": "បុគ្គល",
  "chainUsers.username": "ឈ្មោះអ្នកប្រើ",
  "chainUsers.email": "អ៊ីមែល",
  "chainUsers.role": "តួនាទី",
  "chainUsers.record": "កំណត់ត្រា",
  "chainUsers.reach": "ចូលបាន",
  "chainUsers.stores": "ទីតាំង",
  "chainUsers.wholeChain": "ទាំងបណ្តាញ",
  "chainUsers.wholeChainHint": "គ្រប់ទីតាំង រួមទាំងទីតាំងដែលបន្ថែមពេលក្រោយ។",
  "chainUsers.namedStores": "តែទីតាំងទាំងនេះ",
  "chainUsers.namedStoresHint": "គេអាចចូល ប្តូររវាង និងមើលរបាយការណ៍តែទីតាំងទាំងនេះប៉ុណ្ណោះ។",
  "chainUsers.storeCount": "ទីតាំង {count}",
  "chainUsers.you": "អ្នក",
  "capability.configure_coupons": "គូប៉ុងផ្ទាល់ខ្លួន",
  "capability.configure_discount_promos": "កម្មវិធីបញ្ចុះតម្លៃផ្ទាល់ខ្លួន",
  "capability.configure_topup_tiers": "កម្រិតបញ្ចូលទឹកប្រាក់ផ្ទាល់ខ្លួន",
  "capability.custom_topup_amounts": "ចំនួនបញ្ចូលទឹកប្រាក់វាយបញ្ចូលដោយដៃ",

  "settings.invoice.title": "លេខវិក្កយបត្រ",
  "settings.invoice.description": "លេខដែលបោះពុម្ពលើវិក្កយបត្រ និងព័ត៌មានពន្ធដារនៅខាងលើ។",
  "settings.invoice.numbering": "ការដាក់លេខ",
  "settings.invoice.enabled": "បោះពុម្ពលេខវិក្កយបត្រលើបង្កាន់ដៃ",
  "settings.invoice.prefix": "បុព្វបទ",
  "settings.invoice.dateType": "ចាប់ផ្តើមលេខឡើងវិញ",
  "settings.invoice.dateType.none": "កុំចាប់ផ្តើមឡើងវិញ — លំដាប់តែមួយជាបន្តបន្ទាប់",
  "settings.invoice.dateType.year": "រៀងរាល់ឆ្នាំ",
  "settings.invoice.dateType.date": "រៀងរាល់ថ្ងៃ",
  "settings.invoice.nextNumber": "លេខបន្ទាប់",
  "settings.invoice.rangeHint": "ទីផ្សាររបស់អ្នកអនុញ្ញាត {min} ដល់ {max}។",
  "settings.invoice.preview": "វិក្កយបត្របន្ទាប់នឹងមានលេខ",
  "settings.invoice.continuity":
    "លេខបោះពុម្ពជាលំដាប់មិនដាច់ ហើយអាចទៅមុខតែប៉ុណ្ណោះ។ ការផ្លាស់ប្តូរអនុវត្តលើការបញ្ជាទិញថ្មី។ អ្វីដែលបានបោះពុម្ពរួចមិនត្រូវដាក់លេខឡើងវិញទេ។",
  "settings.invoice.taxIdentity": "ព័ត៌មានពន្ធដារលើបង្កាន់ដៃ",
  "settings.invoice.taxIdentityHint": "បោះពុម្ពខាងលើទំនិញ នៅពេលគំរូបង្កាន់ដៃកំណត់ឱ្យបង្ហាញ។",
  "settings.invoice.companyName": "ឈ្មោះក្រុមហ៊ុន",
  "settings.invoice.registrationNumber": "លេខចុះបញ្ជីពន្ធដារ",
  "settings.invoice.companyAddress": "អាសយដ្ឋានក្រុមហ៊ុន",
  "settings.invoice.deviceNumber": "លេខម៉ាស៊ីនបានចុះបញ្ជី",
  "settings.invoice.deviceNumberHint":
    "នៅកន្លែងដែលអាជ្ញាធរពន្ធដារចុះបញ្ជីម៉ាស៊ីនគិតលុយ លេខរបស់វាត្រូវមានលើវិក្កយបត្រគ្រប់ច្បាប់។ ទុកឱ្យទទេ ប្រសិនបើមិនមាន។",

  "tradingStatus.open": "បើក",
  "tradingStatus.temporarily_closed": "បិទបណ្តោះអាសន្ន",
  "tradingStatus.permanently_closed": "បិទជាអចិន្ត្រៃយ៍",

  "nav.items": "មុខម្ហូប",
  "nav.categories": "ប្រភេទ",
  "nav.specifications": "ក្រុមលក្ខណៈ",
  "nav.toppings": "គ្រឿងបន្ថែម",
  "nav.tags": "ស្លាក",
  "nav.tables": "តុ",
  "nav.settings": "ការកំណត់",
  "nav.storeProfile": "ព័ត៌មានហាង",
  "nav.tax": "ពន្ធ",
  "nav.serviceCharge": "ថ្លៃសេវា",
  "nav.discountExclusions": "មិនរួមក្នុងការបញ្ចុះតម្លៃ",
  "nav.paymentMethods": "មធ្យោបាយទូទាត់",
  "nav.quickNotes": "កំណត់ចំណាំរហ័ស",

  "action.add": "បន្ថែម",
  "action.view": "មើល",
  "action.edit": "កែសម្រួល",
  "action.delete": "លុប",
  "action.save": "រក្សាទុក",
  "action.cancel": "បោះបង់",
  "action.saving": "កំពុងរក្សាទុក…",
  "action.select": "ជ្រើសរើសមួយ",
  "action.search": "ស្វែងរក",
  "action.clear": "សម្អាត",
  "action.previous": "មុន",
  "action.next": "បន្ទាប់",
  "action.moveUp": "ផ្លាស់ទីឡើងលើ",
  "action.moveDown": "ផ្លាស់ទីចុះក្រោម",
  "action.regenerate": "បង្កើតឡើងវិញ",
  "action.close": "បិទ",
  "action.confirmDelete": "លុប",

  "list.empty": "មិនទាន់មានអ្វីនៅទីនេះទេ។",
  "list.noMatches": "រកមិនឃើញលទ្ធផលត្រូវនឹងការស្វែងរកនោះទេ។",
  "list.noFilterMatches": "រកមិនឃើញលទ្ធផលត្រូវនឹងតម្រងទាំងនោះទេ។",
  "list.showing": "{from}–{to} ក្នុងចំណោម {total}",
  "list.noResults": "គ្មានលទ្ធផល",
  "list.pagination": "ការបែងចែកទំព័រ",
  "list.page": "ទំព័រ {page}",
  "list.morePages": "ទំព័របន្ថែម",
  "list.perPage": "ចំនួនជួរក្នុងមួយទំព័រ",
  "list.perPageOption": "{count} ក្នុងមួយទំព័រ",
  "list.sortAscending": "តម្រៀបពីតូចទៅធំ",
  "list.sortDescending": "តម្រៀបពីធំទៅតូច",
  "list.loadFailed": "មិនអាចផ្ទុកបញ្ជីនេះបានទេ។",
  "list.saveFailed": "មិនអាចរក្សាទុកបានទេ។",

  "calendar.previousMonth": "ខែមុន",
  "calendar.nextMonth": "ខែបន្ទាប់",

  "dateRange.label": "ចន្លោះកាលបរិច្ឆេទ",
  "dateRange.apply": "អនុវត្ត",
  "dateRange.today": "ថ្ងៃនេះ",
  "dateRange.yesterday": "ម្សិលមិញ",
  "dateRange.last7Days": "៧ ថ្ងៃចុងក្រោយ",
  "dateRange.last30Days": "៣០ ថ្ងៃចុងក្រោយ",
  "dateRange.monthToDate": "ខែនេះដល់ថ្ងៃនេះ",
  "dateRange.lastMonth": "ខែមុន",
  "dateRange.yearToDate": "ឆ្នាំនេះដល់ថ្ងៃនេះ",
  "dateRange.lastYear": "ឆ្នាំមុន",

  "items.title": "មុខម្ហូប",
  "items.description": "អ្វីៗទាំងអស់ក្នុងម៉ឺនុយ ជាមួយតម្លៃ គ្រឿងបន្ថែម និងការបញ្ជូនទៅស្ថានីយ។",
  "items.searchPlaceholder": "ស្វែងរកតាមឈ្មោះ ឬលេខកូដ",
  "items.new": "មុខម្ហូបថ្មី",
  "items.edit": "កែសម្រួលមុខម្ហូប",
  "items.record": "កំណត់ត្រា",
  "items.money": "ប្រាក់",
  "items.name": "ឈ្មោះ",
  "items.code": "លេខកូដមុខម្ហូប",
  "items.codeHint": "លេខកូដ SKU ឬ PLU ដែលបោះពុម្ពលើប័ណ្ណស្ថានីយ។",
  "items.price": "តម្លៃ",
  "items.priceHint": "គិតជា {currency}។",
  "items.category": "ប្រភេទ",
  "items.status": "ស្ថានភាព",
  "items.recipe": "ធ្វើពីអ្វី",
  "items.recipeHint": "សម្ភារៈដែលភ្ជាប់នឹងចេញពីស្តុកនៅពេលបង់ប្រាក់ មិនមែនពេលកត់ការបញ្ជាទិញទេ។",
  "items.soldOut": "អស់ស្តុក",
  "items.autoSoldOut": "ឲ្យស្តុកសម្រេច",
  "items.autoSoldOutHint":
    "អស់ដោយខ្លួនឯងនៅពេលសម្ភារៈក្នុងរូបមន្តរបស់វាអស់ ហើយត្រឡប់មកវិញនៅពេលស្តុកមកដល់។ មុខម្ហូបដែលគ្មានរូបមន្ត នឹងមិនប៉ះពាល់ទេ។",
  "items.available": "មាន",
  "items.madeToOrder": "ធ្វើតាមការបញ្ជាទិញ",
  "items.stations": "ស្ថានីយ",
  "items.stationsHint":
    "កន្លែងដែលធ្វើវា។ វាអាចមានច្រើនកន្លែង ហើយកន្លែងនីមួយៗទទួលបានប័ណ្ណរៀងៗខ្លួន។",
  "items.stationsNone": "គ្មានស្ថានីយ",
  "items.stationPrinters": "ម៉ាស៊ីនបោះពុម្ព",
  "items.stationPrintersAll": "ម៉ាស៊ីនបោះពុម្ពទាំងអស់នៅស្ថានីយនេះ",
  "items.packingFee": "ថ្លៃខ្ចប់",
  "items.kind": "ប្រភេទមុខម្ហូប",
  "items.kindHint":
    "មុខម្ហូបធម្មតា ឈុតដែលផ្សំពីមុខម្ហូបផ្សេង មុខម្ហូបម្ដងម្កាល ឬមុខម្ហូបកម្មង់ជាមុន។",
  "items.kind.normal": "មុខម្ហូបធម្មតា",
  "items.kind.mealSet": "ឈុតអាហារ",
  "items.kind.temporary": "ម្ដងម្កាល",
  "items.kind.preOrder": "កម្មង់ជាមុន",
  "items.pricingMethod": "លក់តាម",
  "items.pricing.unit": "ក្នុងមួយមុខ",
  "items.pricing.weight": "ទម្ងន់",
  "items.wasPrice": "តម្លៃដើម",
  "items.deliveryPrice": "តម្លៃដឹកជញ្ជូន",
  "items.deliveryPriceHint":
    "តម្លៃទំនិញនេះនៅលើវេទិកាដឹកជញ្ជូន។ ទុកឱ្យទទេ ដើម្បីលក់តាមតម្លៃម៉ឺនុយ។ វេទិកាយកកម្រៃជើងសារ ដូច្នេះម៉ឺនុយភាគច្រើនត្រូវបានបូកបន្ថែមនៅទីនេះ។",
  "items.wasPriceHint": "បង្ហាញដោយគូសបន្ទាត់ក្បែរតម្លៃ។ ទុកទទេបើគ្មានការបញ្ចុះតម្លៃ។",
  "items.pricedAtTill": "កំណត់តម្លៃនៅម៉ាស៊ីនគិតលុយរៀងរាល់ថ្ងៃ",
  "items.mealSet": "អ្វីខ្លះនៅក្នុងឈុត",
  "items.mealSetHint":
    "ក្រុមនៃជម្រើស។ ជម្រើសនីមួយៗគឺមុខម្ហូបផ្សេងក្នុងម៉ឺនុយនេះ គិតតម្លៃតាមអ្វីដែលវាបន្ថែម។",
  "items.mealSetGroup": "ជម្រើស",
  "items.mealSetPicks": "ជ្រើស",
  "items.mealSetPick": "ជ្រើសរើសមុខម្ហូប",
  "items.mealSetDelta": "បន្ថែម",
  "items.mealSetAddOption": "បន្ថែមមុខម្ហូប",
  "items.mealSetAddGroup": "បន្ថែមជម្រើស",
  "items.deleteTitle": "លុបមុខម្ហូបនេះមែនទេ?",
  "items.deleteBody":
    "វានឹងបាត់ពីម៉ឺនុយ។ ការបញ្ជាទិញមុនៗនៅតែរក្សាកំណត់ត្រារបស់វា ដូច្នេះប្រវត្តិរបស់អ្នកមិនផ្លាស់ប្តូរទេ។",
  "items.allCategories": "គ្រប់ប្រភេទ",
  "items.allStatuses": "គ្រប់ស្ថានភាព",
  "items.uncategorised": "គ្មានប្រភេទ",
  "items.nameInLocale": "ឈ្មោះជា {locale}",
  "items.translationHint": "អតិថិជនដែលបញ្ជាទិញជា {locale} នឹងឃើញនេះជំនួសវិញ។",
  "items.modifiers": "ការកែប្រែ",
  "items.modifiersHint": "អ្វីដែលអ្នកគិតលុយត្រូវសួរពេលចុចលើមុខម្ហូបនេះ។",
  "items.modifiersNone": "គ្មាន",

  "categories.title": "ប្រភេទ",
  "categories.description": "របៀបដែលម៉ឺនុយត្រូវបានចែកជាក្រុម និងលំដាប់បង្ហាញ។",
  "categories.new": "ប្រភេទថ្មី",
  "categories.edit": "កែសម្រួលប្រភេទ",
  "categories.record": "កំណត់ត្រា",
  "categories.name": "ឈ្មោះ",
  "categories.itemCount": "ចំនួនមុខម្ហូប",
  "categories.allStatuses": "គ្រប់ស្ថានភាព",
  "categories.deleteTitle": "លុបប្រភេទនេះមែនទេ?",
  "categories.deleteBody": "ប្រភេទដែលនៅមានមុខម្ហូបមិនអាចលុបបានទេ។ សូមផ្លាស់ទីវាចេញជាមុនសិន។",

  "modifiers.specifications": "ក្រុមលក្ខណៈ",
  "modifiers.specificationsDescription": "ជម្រើសដែលផ្លាស់ប្តូរអ្វីដែលលក់ — ទំហំ ក្តៅ ឬត្រជាក់។",
  "modifiers.toppings": "គ្រឿងបន្ថែម",
  "modifiers.toppingsDescription": "គ្រឿងបន្ថែមមានតម្លៃដែលអតិថិជនអាចបន្ថែម។",
  "modifiers.tags": "ស្លាក",
  "modifiers.tagsDescription": "សេចក្តីណែនាំដល់ស្ថានីយ។ វាអាចមានតម្លៃ បើអ្នកគិតថ្លៃលើវា។",
  "modifiers.record": "កំណត់ត្រា",
  "modifiers.options": "ជម្រើស",
  "modifiers.optionName": "ឈ្មោះ",
  "modifiers.optionPrice": "ការផ្លាស់ប្តូរតម្លៃ",
  "modifiers.optionStatus": "ស្ថានភាព",
  "modifiers.addOption": "បន្ថែមជម្រើស",
  "modifiers.required": "ត្រូវការជម្រើសមួយ",
  "modifiers.selection": "ការជ្រើសរើស",
  "modifiers.single": "តែមួយប៉ុណ្ណោះ",
  "modifiers.multiple": "ចំនួនប៉ុន្មានក៏បាន",
  "modifiers.optionDefault": "ជ្រើសរើសស្រាប់",
  "modifiers.storeDefault": "បន្ថែមទៅគ្រប់មុខទំនិញថ្មី",
  "modifiers.usedBy": "ប្រើដោយ",
  "modifiers.deleteTitle": "លុបក្រុមនេះមែនទេ?",
  "modifiers.deleteBody":
    "មុខម្ហូបដែលភ្ជាប់នឹងក្រុមនេះនឹងលែងផ្តល់ជម្រើសរបស់វា។ ការបញ្ជាទិញមុនៗនៅតែរក្សាកំណត់ត្រានៃអ្វីដែលបានជ្រើសរើស ដូច្នេះប្រវត្តិរបស់អ្នកមិនផ្លាស់ប្តូរទេ។",

  "tables.title": "តុ",
  "tables.description": "ប្លង់កន្លែងអង្គុយ និងកូដ QR ដែលតុនីមួយៗមាន។",
  "tables.new": "តុថ្មី",
  "tables.edit": "កែសម្រួលតុ",
  "tables.record": "កំណត់ត្រា",
  "tables.name": "តុ",
  "tables.area": "តំបន់",
  "tables.capacity": "ចំនួនកៅអី",
  "tables.qrCode": "កូដ QR",
  "tables.status": "ស្ថានភាព",
  "tables.tabList": "បញ្ជីតុ",
  "tables.tabFloorPlan": "ប្លង់ជាន់",
  "tables.viewsLabel": "ទិដ្ឋភាពតុ",
  "tables.searchPlaceholder": "ស្វែងរកតុ…",
  "tables.count": "តុចំនួន {count}",
  "tables.qrOrdering": "ការបញ្ជាទិញតាមកូដ QR",
  "tables.viewQr": "មើលកូដ QR",
  "tables.qrExplainer": "ភ្ញៀវអាចស្កេនកូដ QR ដើម្បីមើលម៉ឺនុយ និងបញ្ជាទិញបាន។",
  "tables.qrUnavailable":
    "ការបញ្ជាទិញតាម QR មិនទាន់មានអាសយដ្ឋានសាធារណៈ ដូច្នេះគ្មានអ្វីឲ្យទូរសព្ទស្កេនទេ។",
  "tables.qrFor": "កូដ QR សម្រាប់ {table}",
  "tables.printQr": "បោះពុម្ព",
  "tables.deleteTitle": "លុប {name} មែនទេ?",
  "tables.deleteBody":
    "តុ និងកូដ QR របស់វានឹងត្រូវលុបចោល។ ការបញ្ជាទិញពីមុននៅតែរក្សាកំណត់ត្រារបស់វា។",
  "tables.statusHint": "ស្ថានភាពតុត្រូវបានកំណត់ដោយម៉ាស៊ីនគិតលុយ ពេលភ្ញៀវអង្គុយ និងទទួលសេវា។",
  "tables.decreaseCovers": "បន្ថយចំនួនកៅអី",
  "tables.increaseCovers": "បង្កើនចំនួនកៅអី",
  "tables.emptyTitle": "មិនទាន់មានតុនៅឡើយទេ",
  "tables.emptyBody":
    "បង្កើតតុដំបូងរបស់អ្នក ដើម្បីចាប់ផ្ដើមទទួលការបញ្ជាទិញ និងបង្កើតកូដ QR សម្រាប់តុ។",
  "tables.floorPlanTitle": "ប្លង់ជាន់មិនទាន់ត្រូវបានបង្កើតនៅឡើយទេ",
  "tables.floorPlanBody": "តុ និងតំបន់របស់វាត្រូវបានគ្រប់គ្រងតាមរយៈបញ្ជីតុសិនសម្រាប់ពេលនេះ។",
  "tables.areas": "តំបន់",
  "tables.newArea": "តំបន់ថ្មី",
  "tables.defaultArea": "តំបន់លំនាំដើម",
  "tables.regenerateQrTitle": "ផ្តល់កូដ QR ថ្មីដល់តុនេះមែនទេ?",
  "tables.regenerateQrBody":
    "កូដដែលបានបោះពុម្ពសម្រាប់តុនេះនឹងឈប់ដំណើរការភ្លាមៗ។ សូមបោះពុម្ព និងដាក់កូដថ្មីមុនពេលបម្រើអតិថិជនលើកក្រោយ។",
  "tables.tempCode": "លេខកូដសម្រាប់ភ្ញៀវដើរចូល",
  "tables.tempCodeDescription":
    "លេខសម្រាប់ការបញ្ជាទិញដែលគ្មានតុ — កន្លែងគិតលុយ បារ ឬអ្នកដឹកជញ្ជូន។",
  "tables.tempCodeEnabled": "ប្រើលេខកូដសម្រាប់ភ្ញៀវដើរចូល",
  "tables.tempCodePrefix": "បុព្វបទ",
  "tables.tempCodeFrom": "ចាប់ពី",
  "tables.tempCodeTo": "ដល់",

  "settings.store.title": "ព័ត៌មានហាង",
  "settings.store.description": "អ្នកជានរណា នៅទីណា និងថ្ងៃធ្វើការរបស់អ្នកបញ្ចប់ពេលណា។",
  "settings.store.name": "ឈ្មោះហាង",
  "settings.store.address": "អាសយដ្ឋាន",
  "settings.store.phone": "ទូរស័ព្ទ",
  "settings.store.timezone": "តំបន់ពេលវេលា",
  "settings.store.businessDayCutoff": "ថ្ងៃធ្វើការបញ្ចប់នៅម៉ោង",
  "settings.store.businessDayCutoffHint":
    "ចំណូលក្រោយអាធ្រាត្រនឹងរាប់បញ្ចូលថ្ងៃមុន រហូតដល់ម៉ោងនេះ។ ថ្ងៃរបស់ភោជនីយដ្ឋានបញ្ចប់ពេលបម្រើអតិថិជនចប់។",
  "settings.store.statisticsBasis": "របាយការណ៍រាប់ការលក់ចូលថ្ងៃ",
  "settings.store.statisticsBasisHint":
    "ថ្ងៃណាដែលរាល់របាយការណ៍ដាក់ការលក់មួយចូល។ វាដាច់ដោយឡែកពីម៉ោងបញ្ចប់ថ្ងៃខាងលើ៖ ម៉ោងនោះកំណត់ថាវិក្កយបត្រត្រូវបោះត្រាលើថ្ងៃណា ចំណែកនេះកំណត់ថារបាយការណ៍អានថ្ងៃណាក្នុងចំណោមថ្ងៃទាំងពីរនោះ។ ហាងអាចរក្សាម៉ោងបញ្ចប់ថ្ងៃនៅ ៣ ព្រឹក ហើយនៅតែបិទបញ្ជីតាមខែប្រតិទិនបាន។",
  "settings.store.statisticsBasisBusinessDay": "ថ្ងៃធ្វើការ ដែលបញ្ចប់នៅម៉ោងកំណត់",
  "settings.store.statisticsBasisCalendarDay": "ថ្ងៃប្រតិទិន ពីអាធ្រាត្រដល់អាធ្រាត្រ",
  "settings.store.languages": "ភាសា",
  "settings.store.languagesHint":
    "ភាសាដែលអេក្រង់ និងបង្កាន់ដៃរបស់ហាងនេះផ្តល់ជូន។ បង្កាន់ដៃភាសាខ្មែរបោះពុម្ពជារូបភាព ព្រោះម៉ាស៊ីនបោះពុម្ពកម្តៅមិនអាចបង្ហាញអក្សរនេះពីកូដភេជបានទេ។",
  "settings.store.currency": "រូបិយប័ណ្ណ",
  "settings.store.primaryCurrency": "រូបិយប័ណ្ណចម្បង",
  "settings.store.secondCurrency": "រូបិយប័ណ្ណទីពីរ",
  "settings.store.exchangeRate": "អត្រាប្តូរប្រាក់",
  "settings.store.exchangeRateHint":
    "{second} ប៉ុន្មានក្នុងមួយ {primary}។ ការផ្លាស់ប្តូរវាមិនប៉ះពាល់ដល់វិក្កយបត្រដែលបានទូទាត់រួចទេ។",
  "settings.store.rateHistory": "ប្រវត្តិអត្រា",
  "settings.store.changedBy": "ផ្លាស់ប្តូរដោយ",
  "settings.store.effectiveFrom": "ចាប់ពី",

  "settings.tax.title": "ពន្ធ",
  "settings.tax.description": "របៀបគណនាពន្ធលើវិក្កយបត្រ។",
  "settings.tax.rules": "ច្បាប់ពន្ធ",
  "settings.tax.unset": "កំណត់អត្រារបស់អ្នក",
  "settings.tax.newRule": "ច្បាប់ថ្មី",
  "settings.tax.editRule": "កែសម្រួលច្បាប់",
  "settings.tax.code": "លេខកូដ",
  "settings.tax.name": "ឈ្មោះ",
  "settings.tax.rate": "អត្រា",
  "settings.tax.rateHint": "គិតជាភាគរយ។ ទសភាគពីរខ្ទង់ត្រូវបានរក្សាទុកយ៉ាងជាក់លាក់។",
  "settings.tax.inclusive": "រួមបញ្ចូលក្នុងតម្លៃ",
  "settings.tax.inclusiveHint":
    "តម្លៃក្នុងម៉ឺនុយរបស់អ្នកមានពន្ធនេះរួចហើយ។ វាបង្ហាញលើវិក្កយបត្រ ប៉ុន្តែមិនបូកបន្ថែមទេ។",
  "settings.tax.exclusiveHint": "ពន្ធនេះត្រូវបានបូកបន្ថែមលើវិក្កយបត្រនៅពេលគិតលុយ។",
  "settings.tax.sequence": "លំដាប់",
  "settings.tax.appliesToCategories": "តែប្រភេទទាំងនេះ",
  "settings.tax.appliesToCategoriesHint": "ទុកឲ្យទទេ ដើម្បីអនុវត្តលើទាំងអស់។",
  "settings.tax.appliesToDiningStyles": "តែប្រភេទសេវាទាំងនេះ",
  "settings.tax.appliesToServiceCharge": "គិតពន្ធលើថ្លៃសេវាផងដែរ",
  "settings.tax.appliesToTableFee": "គិតពន្ធលើថ្លៃតុផងដែរ",
  "settings.tax.chargeTax": "គិតពន្ធ",
  "settings.tax.chargeTaxHint":
    "បិទវាធ្វើឲ្យផ្អាកការប្រមូលពន្ធ ដោយរក្សាច្បាប់របស់អ្នកទុក។ វាមិនដូចអត្រាសូន្យទេ ដែលនៅតែកត់ត្រាបន្ទាត់ពន្ធ។",
  "settings.tax.label": "អ្វីដែលអ្នកហៅវា",
  "settings.tax.labelHint": "VAT, GST, ពន្ធលើការលក់ — អ្វីក៏ដោយដែលបង្ហាញលើវិក្កយបត្ររបស់អ្នក។",
  "settings.tax.deleteTitle": "លុបច្បាប់ពន្ធនេះមែនទេ?",
  "settings.tax.deleteBody":
    "វានឹងឈប់អនុវត្តលើវិក្កយបត្រថ្មី។ វិក្កយបត្រដែលបានទូទាត់រួចនៅតែរក្សាអត្រាដែលបានគិត។",

  "settings.serviceCharge.title": "ថ្លៃសេវា",
  "settings.serviceCharge.description": "អ្វីដែលអ្នកបូកបន្ថែមសម្រាប់សេវា និងពេលណា។",
  "settings.serviceCharge.newRate": "អត្រាថ្មី",
  "settings.serviceCharge.deleteTitle": "លុបអត្រានេះ?",
  "settings.serviceCharge.deleteBody":
    "វានឹងឈប់បូកបញ្ចូលទៅវិក្កយបត្រថ្មី។ វិក្កយបត្រដែលបានទូទាត់រួចនៅតែរក្សាថ្លៃសេវាដែលបានគិត។",
  "settings.serviceCharge.rate": "អត្រា",
  "settings.serviceCharge.default": "លំនាំដើម",
  "settings.serviceCharge.appliesTo": "អនុវត្តលើ",
  "settings.serviceCharge.calculation": "គណនា",
  "settings.serviceCharge.onSubtotal": "លើវិក្កយបត្រទាំងមូល",
  "settings.serviceCharge.perUnit": "តាមមុខម្ហូបនីមួយៗ",
  "settings.serviceCharge.calculationHint":
    "តាមមុខម្ហូបនីមួយៗ គិតលើឯកតានីមួយៗ ហើយបង្គត់ម្តងមួយៗ ដែលអាចខុសពីការគិតលើវិក្កយបត្រទាំងមូលប៉ុន្មានសេន។",
  "settings.serviceCharge.rounding": "ការបង្គត់",
  "settings.serviceCharge.tableFee": "ថ្លៃតុ",
  "settings.serviceCharge.tableFeeEnabled": "គិតថ្លៃតុ",
  "settings.serviceCharge.tableFeeBasis": "គិតតាម",
  "settings.serviceCharge.perHead": "ក្នុងមួយនាក់",
  "settings.serviceCharge.perTable": "ក្នុងមួយតុ",
  "settings.serviceCharge.amount": "ចំនួនទឹកប្រាក់",
  "settings.tip.title": "ប្រាក់រង្វាន់",
  "settings.tip.description":
    "ប្រាក់រង្វាន់គឺជាប្រាក់របស់ភ្ញៀវដែលឆ្លងទៅបុគ្គលិក។ ថ្លៃសេវាគឺជារបស់អ្នក។ ទាំងពីរខុសគ្នាតាមច្បាប់ ហើយអ្នកអាចប្រើទាំងពីរ។",
  "settings.tip.enabled": "ផ្តល់ប្រាក់រង្វាន់នៅម៉ាស៊ីនគិតលុយ",
  "settings.tip.suggested": "ភាគរយដែលស្នើ",
  "settings.tip.suggestedHint": "ច្រើនបំផុតបួន បំបែកដោយសញ្ញាក្បៀស។ 18, 20, 22 ជាបីធម្មតា។",
  "settings.tip.basis": "គិតភាគរយលើ",
  "settings.tip.basisHint": "ម៉ាស៊ីនកាតជាធម្មតាស្នើលើសរុប។ ទម្លាប់ចាស់គឺលើសរុបរង។",
  "settings.tip.basisTotal": "សរុប បន្ទាប់ពីពន្ធ",
  "settings.tip.basisSubtotal": "សរុបរង មុនពន្ធ",

  "settings.discountExclusions.title": "មិនរួមក្នុងការបញ្ចុះតម្លៃ",
  "settings.discountExclusions.description":
    "មុខម្ហូបដែលការបញ្ចុះតម្លៃលើវិក្កយបត្រទាំងមូលមិនប៉ះពាល់។",
  "settings.discountExclusions.excluded": "មិនរួមបញ្ចូល",
  "settings.discountExclusions.selected": "មិនរួមបញ្ចូល {count}",

  "settings.paymentMethods.title": "មធ្យោបាយទូទាត់",
  "settings.paymentMethods.description": "អ្វីដែលម៉ាស៊ីនគិតលុយផ្តល់ជូន និងតាមលំដាប់ណា។",
  "settings.paymentMethods.enabled": "ផ្តល់ជូន",
  "settings.paymentMethods.provider": "អ្នកផ្តល់សេវា",
  "settings.paymentMethods.noProvider": "គ្មាន",
  "settings.paymentMethods.add": "បន្ថែមមធ្យោបាយបង់ប្រាក់",
  "settings.paymentMethods.newName": "ឈ្មោះដែលម៉ាស៊ីនគិតលុយត្រូវហៅ",
  "settings.paymentMethods.builtIn": "មានស្រាប់",
  "settings.paymentMethods.builtInHint": "មធ្យោបាយមានស្រាប់អាចប្ដូរឈ្មោះ ឬបិទបាន ប៉ុន្តែលុបមិនបាន។",
  "settings.paymentMethods.nameHint":
    "ប្ដូរឈ្មោះមធ្យោបាយណាមួយក៏បាន — ម៉ាស៊ីនគិតលុយ និងរបាយការណ៍ទាំងអស់នឹងតាម។",
  "settings.paymentMethods.deleteConfirm":
    "លុបមធ្យោបាយបង់ប្រាក់នេះ? វិក្កយបត្រចាស់នៅរក្សាឈ្មោះដើមរបស់វា។",

  "settings.quickNotes.title": "កំណត់ចំណាំរហ័ស",
  "settings.quickNotes.description": "ផ្លូវកាត់ដែលអ្នកគិតលុយចុច ជំនួសឲ្យការវាយបញ្ចូល។",
  "settings.quickNotes.new": "កំណត់ចំណាំថ្មី",
  "settings.quickNotes.record": "កំណត់ត្រា",
  "settings.quickNotes.deleteTitle": "លុបកំណត់ចំណាំនេះមែនទេ?",
  "settings.quickNotes.deleteBody":
    "អ្នកគិតលុយនឹងលែងឃើញវា។ ការបញ្ជាទិញដែលបានដាក់វារួចហើយ នៅរក្សាអត្ថបទដដែល។",

  "diningStyle.dine_in": "ញ៉ាំនៅហាង",
  "diningStyle.take_away": "យកទៅផ្ទះ",
  "diningStyle.pre_order": "កម្មង់ជាមុន",
  "diningStyle.delivery": "ដឹកជញ្ជូន",

  "status.enabled": "សកម្ម",
  "status.disabled": "លាក់",
  "tableStatus.free": "ទំនេរ",
  "tableStatus.seated": "មានភ្ញៀវ",
  "tableStatus.ordered": "បានកម្មង់",
  "tableStatus.bill_requested": "សុំវិក្កយបត្រ",

  "rounding.half_up": "ពាក់កណ្តាលឡើងលើ",
  "rounding.half_even": "ពាក់កណ្តាលទៅគូ",
  "rounding.half_down": "ពាក់កណ្តាលចុះក្រោម",
  "rounding.up": "ឡើងលើជានិច្ច",
  "rounding.down": "ចុះក្រោមជានិច្ច",
  "rounding.ceiling": "ឡើងលើ",
  "rounding.floor": "ចុះក្រោម",

  "inventory.supplies.title": "សម្ភារៈ",
  "inventory.supplies.description": "អ្វីដែលស្ថានីយទិញ រក្សាទុក និងប្រើប្រាស់។",
  "inventory.supplies.new": "សម្ភារៈថ្មី",
  "inventory.supplies.deleteTitle": "លុបសម្ភារៈនេះ?",
  "inventory.supplies.deleteBody":
    "សម្ភារៈដែលនៅមានស្តុកមិនអាចលុបបានទេ — សូមកាត់ស្តុកចេញជាមុនសិន។ " +
    "ឡូតិ៍ និងចលនាទាំងអស់របស់វានៅដដែល។",
  "inventory.number": "លេខ",
  "inventory.record": "កំណត់ត្រា",
  "inventory.name": "ឈ្មោះ",
  "inventory.otherName": "ស្គាល់ផងដែរថា",
  "inventory.category": "ប្រភេទ",
  "inventory.brand": "ម៉ាក",
  "inventory.barcode": "បាកូដ",
  "inventory.barcodeHint": "វាយ ឬស្កេន។ បាកូដមួយសម្រាប់សម្ភារៈមួយ។",
  "inventory.stockUnit": "ឯកតាស្តុក",
  "inventory.stockUnitHint": "អ្វីដែលរាប់វា។ មិនអាចប្តូរបានទេ ពេលមានស្តុករួច។",
  "inventory.purchaseUnit": "ឯកតាទិញ",
  "inventory.salesUnit": "ឯកតាលក់",
  "inventory.minimumStock": "ព្រមានពេលតិចជាង",
  "inventory.inStock": "ក្នុងស្តុក",
  "inventory.belowMinimum": "តិច",
  "inventory.suppliers.title": "អ្នកផ្គត់ផ្គង់",
  "inventory.suppliers.description": "អ្នកដែលអ្នកទិញពី និងតម្លៃរបស់ពួកគេ។",
  "inventory.suppliers.new": "អ្នកផ្គត់ផ្គង់ថ្មី",
  "inventory.contactPerson": "អ្នកទំនាក់ទំនង",
  "inventory.contactNumber": "ទូរស័ព្ទ",
  "inventory.address": "អាសយដ្ឋាន",
  "inventory.nickname": "ឈ្មោះខ្លី",
  "inventory.supplierPrices": "ផ្គត់ផ្គង់",
  "inventory.warehouses.title": "ឃ្លាំង",
  "inventory.warehouses.description": "កន្លែងដាក់ស្តុក និងជុំណាមួយចេញមុន។",
  "inventory.warehouses.new": "ឃ្លាំងថ្មី",
  "inventory.isDefault": "កាត់ស្តុកលក់ពីទីនេះ",
  "inventory.valuationPolicy": "ជុំណាមួយចេញមុន",
  "inventory.valuation.fifo": "ចាស់មុន (FIFO)",
  "inventory.valuation.lifo": "ថ្មីមុន (LIFO)",
  "inventory.valuationHint": "វាកំណត់តម្លៃដើមនៃការចេញ មិនមែនលំដាប់បញ្ជីទេ។",
  "inventory.units.title": "ឯកតា",
  "inventory.units.description": "របៀបវាស់សម្ភារៈ និងការប្តូរពីឯកតាមួយទៅមួយ។",
  "inventory.units.new": "ឯកតាថ្មី",
  "inventory.dimension": "វាស់",
  "inventory.dimension.mass": "ទម្ងន់",
  "inventory.dimension.volume": "មាឌ",
  "inventory.dimension.count": "ចំនួន",
  "inventory.seeded": "មានស្រាប់",
  "inventory.conversions": "ការប្តូរឯកតា",
  "inventory.conversions.new": "ការប្តូរថ្មី",
  "inventory.conversionFrom": "មួយនៃ",
  "inventory.conversionTo": "ស្មើនឹង",
  "inventory.factor": "ចំនួនប៉ុន្មាន",
  "inventory.appliesTo": "អនុវត្តលើ",
  "inventory.everySupply": "សម្ភារៈទាំងអស់",
  "inventory.categories.title": "ប្រភេទសម្ភារៈ",
  "inventory.categories.description": "ពីរកម្រិត៖ ប្រភេទ និងប្រភេទរងខាងក្រោម។",
  "inventory.categories.new": "ប្រភេទថ្មី",
  "inventory.categories.deleteTitle": "លុបប្រភេទនេះ?",
  "inventory.categories.deleteBody":
    "ប្រភេទដែលមានសម្ភារៈ ឬប្រភេទរងមិនអាចលុបបានទេ។ សូមផ្លាស់ទីពួកវាជាមុនសិន។",
  "inventory.parent": "ស្ថិតក្រោម",
  "inventory.topLevel": "គ្មាន — វាជាកម្រិតលើគេ",
  "inventory.subCategories": "ប្រភេទរង",
  "inventory.supplyCount": "សម្ភារៈ",
  "inventory.brands.title": "ម៉ាក",
  "inventory.brands.description": "ផលិតផលរបស់នរណា នៅកន្លែងដែលវាសំខាន់។",
  "inventory.brands.new": "ម៉ាកថ្មី",
  "inventory.brands.deleteTitle": "លុបម៉ាកនេះ?",
  "inventory.brands.deleteBody":
    "ម៉ាកដែលនៅលើសម្ភារៈមិនអាចលុបបានទេ។ សូមដកវាចេញពីសម្ភារៈទាំងនោះជាមុនសិន។",
  "inventory.description": "កំណត់ចំណាំ",

  "nav.stock": "ស្តុក",
  "nav.purchases": "ការទិញ",
  "nav.inbound": "ចូលឃ្លាំង",
  "nav.outbound": "ចេញឃ្លាំង",
  "nav.stockCounts": "ការរាប់ស្តុក",
  "stock.title": "ស្តុក",
  "stock.description": "អ្វីដែលមាន តម្លៃរបស់វា និងអ្វីដែលជិតអស់។",
  "stock.warehouse": "ឃ្លាំង",
  "stock.quantity": "មានក្នុងស្តុក",
  "stock.value": "តម្លៃ",
  "stock.onlyLow": "តែអ្វីដែលជិតអស់",
  "stock.batches": "ជុំ",
  "stock.usedIn": "ប្រើក្នុង",
  "stock.batchNo": "ជុំ",
  "stock.received": "បានទទួល",
  "stock.remaining": "នៅសល់",
  "stock.unitCost": "តម្លៃដើមក្នុងមួយ",
  "stock.receivedAt": "បានយកចូល",
  "stock.expiresAt": "ប្រើមុនថ្ងៃ",
  "stock.drawOrder": "ចេញតាមលំដាប់នេះ",
  "stock.drawOrderReverse": "ចេញតាមលំដាប់បញ្ច្រាស",
  "purchases.title": "ការទិញ",
  "purchases.description": "អ្វីដែលបានបញ្ជាទិញ និងអ្វីដែលបានមកដល់។",
  "purchases.new": "ការទិញថ្មី",
  "purchases.type": "ទិញពី",
  "purchases.type.supplier": "អ្នកផ្គត់ផ្គង់",
  "purchases.type.market": "ផ្សារ",
  "purchases.type.head_office": "ការិយាល័យកណ្តាល",
  "purchases.supplier": "អ្នកផ្គត់ផ្គង់",
  "purchases.total": "សរុប",
  "purchases.notifyInbound": "ផ្ញើទៅឃ្លាំង",
  "purchases.receiveNow": "យកចូលឥឡូវ",
  "purchases.void": "លុបចោល",
  "purchases.voidReason": "មូលហេតុ",
  "inbound.title": "ចូលឃ្លាំង",
  "inbound.description": "ការដឹកជញ្ជូនរង់ចាំពិនិត្យ និងអ្វីដែលបានយកចូល។",
  "inbound.new": "ការចូលថ្មី",
  "inbound.type": "មូលហេតុ",
  "inbound.type.supplier": "ពីអ្នកផ្គត់ផ្គង់",
  "inbound.type.market": "ពីផ្សារ",
  "inbound.type.item_return": "បានប្រគល់មកវិញ",
  "inbound.type.overstock": "រកឃើញក្នុងស្តុក",
  "inbound.type.transfer": "ផ្ទេរចូល",
  "inbound.approve": "យកចូល",
  "inbound.reject": "បដិសេធ",
  "inbound.rejectReason": "មូលហេតុ",
  "inbound.batchNo": "ជុំ",
  "inbound.unitCost": "តម្លៃដើមក្នុងមួយ",
  "inbound.freeQuantity": "ឥតគិតថ្លៃ",
  "outbound.title": "ចេញឃ្លាំង",
  "outbound.description": "អ្វីដែលបានចេញ មូលហេតុ និងតម្លៃដើម។",
  "outbound.new": "ការចេញថ្មី",
  "outbound.type": "មូលហេតុ",
  "outbound.type.loss": "ខូចខាត",
  "outbound.type.sales": "លក់បន្ត",
  "outbound.type.transfer": "ផ្ទេរ",
  "outbound.type.item_sales": "លក់ជាម្ហូប",
  "outbound.type.shortage": "ខ្វះ",
  "outbound.destination": "ទៅឃ្លាំង",
  "outbound.cost": "តម្លៃដើម",
  "outbound.shortfall": "ខ្វះ",
  "outbound.customer": "អតិថិជន",
  "outbound.salesAmount": "លក់ក្នុងតម្លៃ",
  "counts.title": "ការរាប់ស្តុក",
  "counts.description": "អ្វីដែលនៅលើធ្នើ ធៀបនឹងអ្វីដែលក្នុងសៀវភៅ។",
  "counts.new": "ការរាប់ថ្មី",
  "counts.scope": "រាប់",
  "counts.scope.all": "ទាំងអស់",
  "counts.scope.picked": "តែអ្វីដែលខ្ញុំជ្រើស",
  "counts.counter": "រាប់ដោយ",
  "counts.expected": "សៀវភៅថា",
  "counts.counted": "ធ្នើថា",
  "counts.difference": "ភាពខុសគ្នា",
  "counts.record": "កត់ត្រាការរាប់",
  "counts.adjust": "កត់ត្រាភាពខុសគ្នា",
  "counts.adjustHint": "វាផ្លាស់ប្តូរស្តុកដោយគ្មានការដឹកជញ្ជូន ដូច្នេះវាកត់ត្រាតាមឈ្មោះអ្នក។",
  "document.number": "លេខ",
  "document.status": "ស្ថានភាព",
  "document.date": "បានបង្កើត",
  "document.lines": "បន្ទាត់",
  "document.addLine": "បន្ថែមបន្ទាត់",
  "document.quantity": "បរិមាណ",
  "document.unitPrice": "តម្លៃក្នុងមួយ",
  "document.remarks": "កំណត់ចំណាំ",
  "status.purchasing": "កំពុងទិញ",
  "status.head_office_reviewing": "នៅការិយាល័យកណ្តាល",
  "status.head_office_rejected": "ការិយាល័យកណ្តាលបដិសេធ",
  "status.pending_inbound": "រង់ចាំយកចូល",
  "status.ended": "រួចរាល់",
  "status.voided": "បានលុបចោល",
  "status.draft": "សេចក្តីព្រាង",
  "status.waiting_review": "រង់ចាំពិនិត្យ",
  "status.approving": "កំពុងពិនិត្យ",
  "status.received": "បានយកចូល",
  "status.rejected": "បានបដិសេធ",
  "status.issued": "បានចេញ",
  "status.transferring": "កំពុងផ្ទេរ",
  "status.blocked": "ស្តុកមិនគ្រប់",
  "status.counting": "កំពុងរាប់",
  "status.adjusted": "បានកត់ត្រា",

  "nav.ordersSection": "ការបញ្ជាទិញ",
  "nav.orderList": "ការបញ្ជាទិញទាំងអស់",
  "nav.reports": "របាយការណ៍",
  "nav.costs": "ចំណាយរបស់ហាង",
  "nav.deliveryProviders": "វេទិកាដឹកជញ្ជូន",
  "nav.billing": "ការជាវ",
  "nav.campaigns": "យុទ្ធនាការ",
  "billing.title": "ការជាវរបស់អ្នក",
  "billing.description": "គម្រោងដែលអ្នកកំពុងប្រើ អ្វីដែលវារួមបញ្ចូល និងអ្វីដែលអ្នកបានបង់។",
  "billing.plan": "គម្រោង",
  "billing.perMonth": "{amount} ក្នុងមួយខែ",
  "billing.permanent": "គម្រោងនេះមិនផុតកំណត់ទេ។",
  "billing.expires": "ដំណើរការដល់ {date} — នៅសល់ {days} ថ្ងៃ។",
  "billing.lapsed": "បានផុតកំណត់នៅ {date}។",
  "billing.allowances": "អ្វីដែលវាអនុញ្ញាត",
  "billing.unmetered": "{used} — គ្មានដែនកំណត់",
  "billing.renew": "បន្តរយៈពេល",
  "billing.renewHint":
    "បន្តគម្រោងដែលអ្នកកំពុងប្រើ។ ការប្ដូរគម្រោងត្រូវការកិច្ចសន្យាថ្មី — សូមទាក់ទងមកយើង។",
  "billing.months": "ចំនួនខែ",
  "billing.cycle": "គិតថ្លៃ",
  "billing.monthly": "ប្រចាំខែ",
  "billing.yearly": "ប្រចាំឆ្នាំ",
  "billing.payments": "ការទូទាត់",
  "billing.noPayments": "មិនទាន់មានការគិតថ្លៃទេ។",
  "subscriptionStatus.normal": "កំពុងដំណើរការ",
  "subscriptionStatus.expired": "ផុតកំណត់",
  "subscriptionStatus.invalidated": "ព្យួរ",
  "subscriptionStatus.cancelled": "បានលុបចោល",
  "subscriptionStatus.trialing": "សាកល្បង",
  "quota.item": "មុខទំនិញ",
  "quota.employee": "បុគ្គលិក",
  "quota.table": "តុ",
  "quota.customer": "អតិថិជន",
  "quota.supplier": "អ្នកផ្គត់ផ្គង់",
  "quota.store": "ទីតាំង",
  "campaigns.title": "យុទ្ធនាការ",
  "campaigns.description": "រៀងរាល់មុខទី N បញ្ចុះតម្លៃ។ គិតលើវិក្កយបត្រ មិនមែនវាយដោយដៃទេ។",
  "campaigns.add": "បន្ថែមយុទ្ធនាការ",
  "campaigns.name": "ឈ្មោះ",
  "campaigns.kind": "ការផ្ដល់ជូន",
  "campaigns.kind.half_price_second": "មុខទីពីរតម្លៃពាក់កណ្ដាល",
  "campaigns.kind.buy_one_free_one": "ទិញមួយ ថែមមួយ",
  "campaigns.kind.nth_item": "មុខទី N បញ្ចុះតម្លៃ",
  "campaigns.nthItem": "មុខទីប៉ុន្មាន",
  "campaigns.discount": "បញ្ចុះប៉ុន្មាន",
  "campaigns.discountHint": "ជាភាគរយ។ 100 មានន័យថាឥតគិតថ្លៃ។",
  "campaigns.active": "កំពុងដំណើរការ",
  "campaigns.consecutive": "ធ្វើម្ដងទៀតពេញកន្ត្រក",
  "campaigns.consecutiveHint":
    "បើក៖ បួនមុខទទួលបានពីរមុខតាមការផ្ដល់ជូន។ បិទ៖ វាធ្វើតែម្ដងក្នុងមួយវិក្កយបត្រ។",
  "campaigns.reads": "រៀងរាល់មុខទី {nth} បញ្ចុះ {percent}%",
  "campaigns.deleteTitle": "លុបយុទ្ធនាការនេះមែនទេ?",
  "campaigns.deleteBody":
    "យុទ្ធនាការនេះនឹងបាត់ជាអចិន្ត្រៃយ៍ មិនមែនផ្អាកទេ — ការបិទវាបញ្ឈប់ការផ្ដល់ជូន ហើយរក្សាទុកសម្រាប់ពេលក្រោយ។ វិក្កយបត្រដែលបានគិតតម្លៃរួចហើយមិនផ្លាស់ប្ដូរទេ។",
  "campaigns.record": "កំណត់ត្រា",
  "campaigns.label": "ស្លាកនៅលើវិក្កយបត្រ",
  "campaigns.items": "មុខទំនិញដែលរាប់បញ្ចូល",
  "campaigns.itemsAll": "ម៉ឺនុយទាំងមូល",
  "campaigns.window": "ពេលដំណើរការ",
  "campaigns.validFrom": "ចាប់ពី",
  "campaigns.validTo": "រហូតដល់",
  "nav.exports": "ការនាំចេញ",
  "nav.chainOrders": "ការបញ្ជាទិញបណ្តាញហាង",
  "nav.insights": "ការវិភាគអាជីវកម្ម",
  "nav.chainReports": "របាយការណ៍ខ្សែសង្វាក់",
  "orders.title": "ការបញ្ជាទិញ",
  "orders.description": "វិក្កយបត្រនីមួយៗ អ្វីដែលមាននៅលើវា និងអ្វីដែលបានកើតឡើង។",
  "orders.number": "លេខបញ្ជាទិញ",
  "orders.table": "តុ",
  "orders.covers": "ចំនួនភ្ញៀវ",
  "orders.total": "សរុប",
  "orders.paid": "បានបង់",
  "orders.status": "ស្ថានភាព",
  "orders.date": "ពេលទទួល",
  "orders.diningStyle": "ប្រភេទសេវា",
  "orders.employee": "កត់ដោយ",
  "orders.refund": "សងប្រាក់វិញ",
  "orders.tip": "ប្រាក់រង្វាន់",
  "orders.adjustTip": "កែប្រាក់រង្វាន់",
  "orders.adjustTipTitle": "កែប្រាក់រង្វាន់លើការទូទាត់នេះ",
  "orders.adjustTipHint":
    "ចំនួនប្រាក់រង្វាន់ថ្មី មិនមែនផលសងទេ។ បានតែមុនពេលថ្ងៃជួញដូរនេះត្រូវបានរាប់។",
  "orders.tipAdjusted": "បានកែប្រាក់រង្វាន់។",
  "orders.refundTitle": "សងប្រាក់វិក្កយបត្រនេះវិញ",
  "orders.refundAmount": "ចំនួនទឹកប្រាក់",
  "orders.refundAmountHint": "ទុកឱ្យទទេ ដើម្បីសងវិញនូវអ្វីៗដែលនៅសល់ទាំងអស់។",
  "orders.refundReason": "មូលហេតុ",
  "orders.refundable": "នៅអាចសងវិញបាន",
  "orders.refunded": "បានសងប្រាក់វិញ។",
  "orders.reverse": "បញ្ច្រាស",
  "orders.reverseTitle": "បញ្ច្រាសការទូទាត់នេះ",
  "orders.reverseWarning":
    "វិក្កយបត្រនឹងត្រឡប់ទៅជាបើកវិញ ហើយប្រាក់ដែលបានទទួលនឹងត្រូវសងវិញ។ តុរបស់វានឹងមានភ្ញៀវឡើងវិញ។",
  "orders.reversed": "បានបញ្ច្រាសការទូទាត់។",
  "orders.items": "ទំនិញ",
  "orders.taxLines": "ពន្ធ",
  "orders.payments": "ការទូទាត់",
  "orders.history": "ប្រវត្តិ",
  "orders.reprint": "បោះពុម្ពបង្កាន់ដៃឡើងវិញ",
  "orders.reprinted": "បានដាក់ជួរបោះពុម្ព។",
  "orders.allStatuses": "គ្រប់ស្ថានភាព",
  "orders.allStyles": "គ្រប់ប្រភេទសេវា",
  "receipt.taxIncluded": "រួមហើយ",
  "orders.inclusiveNote": "រួមបញ្ចូលក្នុងតម្លៃរួចហើយ",
  "payStatus.unpaid": "នៅបើក",
  "payStatus.partly_paid": "បង់មួយផ្នែក",
  "payStatus.paid": "បានបង់",
  "payStatus.credit": "ជំពាក់",
  "payStatus.refunded": "បានសងវិញ",
  "payStatus.cancelled": "បានលុបចោល",
  "orderItem.ordered": "បានកម្មង់",
  "orderItem.fired": "នៅស្ថានីយ",
  "orderItem.served": "បានបម្រើ",
  "orderItem.returned": "បានប្រគល់មកវិញ",
  "orderItem.voided": "បានលុប",
  "event.created": "បានទទួលការបញ្ជាទិញ",
  "event.accepted": "បានទទួលយក",
  "event.item_added": "បានកម្មង់បន្ថែម",
  "event.item_returned": "បានប្រគល់ទំនិញមកវិញ",
  "event.item_voided": "បានលុបទំនិញ",
  "event.item_fired": "បានផ្ញើទៅស្ថានីយ",
  "event.table_changed": "បានប្តូរតុ",
  "event.covers_changed": "បានប្តូរចំនួនភ្ញៀវ",
  "event.held": "បានផ្អាក",
  "event.resumed": "បានបន្ត",
  "event.merged": "បានបញ្ចូលគ្នា",
  "event.split": "បានបំបែក",
  "event.discounted": "បានបញ្ចុះតម្លៃ",
  "event.tax_exempted": "បានលើកលែងពន្ធ",
  "event.tax_reinstated": "បានដាក់ពន្ធវិញ",
  "event.tip_adjusted": "បានកែប្រាក់រង្វាន់",
  "event.paid": "បានបង់ប្រាក់",
  "event.refunded": "បានសងវិញ",
  "event.reversed": "បានដកការទូទាត់វិញ",
  "event.cancelled": "បានលុបចោល",
  "event.member_attached": "បានកំណត់សមាជិក",
  "event.points_earned": "បានទទួលពិន្ទុ",
  "event.supplies_deducted": "បានកាត់ស្តុក",
  "creditStatus.open": "នៅជំពាក់",
  "creditStatus.settled": "បានសង",
  "creditStatus.written_off": "លុបចោល",
  "insights.title": "ការវិភាគអាជីវកម្ម",
  "insights.description": "ស្ថានភាពបណ្តាញហាង និងអ្វីដែលមិនប្រក្រតី។",
  "insights.overview": "ទិដ្ឋភាពរួម",
  "insights.trend": "និន្នាការ",
  "insights.priceDistribution": "ការបែងចែកតម្លៃ",
  "insights.anomalies": "អ្វីដែលមិនប្រក្រតី",
  "insights.rules": "កម្រិតកំណត់",
  "insights.storeCount": "ហាង",
  "insights.allStores": "គ្រប់ហាង",
  "insights.comparedWith": "ធៀបនឹង {from} ដល់ {to}",
  "insights.metric": "រង្វាស់",
  "insights.metric.net": "លក់សុទ្ធ",
  "insights.metric.orders": "ការបញ្ជាទិញ",
  "insights.metric.covers": "ភ្ញៀវ",
  "insights.metric.discount": "បញ្ចុះតម្លៃ",
  "insights.metric.perCover": "ចំណាយក្នុងម្នាក់",
  "insights.figure.net": "លក់សុទ្ធ",
  "insights.figure.gross": "លក់សរុប",
  "insights.figure.discount": "បញ្ចុះតម្លៃ",
  "insights.figure.refunded": "សងប្រាក់វិញ",
  "insights.figure.orders": "ការបញ្ជាទិញ",
  "insights.figure.covers": "ភ្ញៀវ",
  "insights.column.store": "ហាង",
  "insights.column.item": "មុខម្ហូប",
  "insights.column.lowest": "ថោកជាងគេនៅ",
  "insights.column.highest": "ថ្លៃជាងគេនៅ",
  "insights.column.spread": "គម្លាត",
  "insights.column.median": "មធ្យម",
  "insights.column.stores": "ហាង",
  "insights.column.perCover": "ក្នុងម្នាក់",
  "insights.column.rule": "ច្បាប់",
  "insights.priceScope":
    "តម្លៃម៉ឺនុយបច្ចុប្បន្ននៅគ្រប់ទីតាំង ទោះបីជ្រើសរើសហាងណាខាងលើក៏ដោយ។ ជួរកាលបរិច្ឆេទមិនត្រូវបានអនុវត្តទេ ព្រោះគ្មានប្រវត្តិតម្លៃសម្រាប់អានរយៈពេលកន្លងមក។",
  "insights.noSpread": "គ្មានមុខម្ហូបណាមានតម្លៃខុសគ្នារវាងហាងទេ។",
  "insights.priceTruncated": "កំពុងបង្ហាញ {shown} ក្នុងចំណោម {total} ដែលមានគម្លាតតម្លៃធំបំផុត។",
  "insights.column.reading": "តម្លៃវាស់",
  "insights.column.threshold": "កម្រិតកំណត់",
  "insights.column.evidence": "ភស្តុតាង",
  "insights.showEvidence": "បង្ហាញ",
  "insights.rule.discountRate": "អត្រាបញ្ចុះតម្លៃ",
  "insights.rule.voidRate": "អត្រាលុបចោល",
  "insights.rule.refundRate": "អត្រាសងប្រាក់វិញ",
  "insights.rule.coverSpend": "ចំណាយក្នុងម្នាក់",
  "insights.direction.above": "លើស",
  "insights.direction.below": "ទាបជាងមធ្យមបណ្តាញហាង",
  "insights.clear": "គ្មានអ្វីលើសកម្រិតកំណត់ក្នុងអំឡុងនេះទេ។",
  "insights.enabled": "តាមដាន",
  "insights.saved": "បានរក្សាទុក។",
  "chainOrders.title": "ការបញ្ជាទិញបណ្តាញហាង",
  "chainOrders.description": "ការបញ្ជាទិញទាំងអស់ក្នុងបណ្តាញហាង។",
  "chainOrders.store": "ហាង",
  "chainOrders.record": "កំណត់ត្រា",
  "nav.members": "សមាជិក",
  "nav.memberList": "បញ្ជីអតិថិជន",
  "nav.memberBalance": "សមតុល្យហាង",
  "nav.loyalty": "ភាពស្មោះត្រង់",
  "members.title": "សមាជិក",
  "members.description": "អ្នកដែលត្រឡប់មកវិញ អ្វីដែលពួកគេមាន និងអ្វីដែលពួកគេទទួលបាន។",
  "members.number": "លេខសមាជិក",
  "members.name": "ឈ្មោះ",
  "members.phone": "ទូរស័ព្ទ",
  "members.balance": "សមតុល្យ",
  "members.points": "ពិន្ទុ",
  "members.tier": "ថ្នាក់",
  "members.joined": "ចូលរួម",
  "members.enrol": "ចុះឈ្មោះសមាជិក",
  "members.classification": "សមាជិករបស់",
  "members.anyClassification": "គ្រប់ទីកន្លែងក្នុងបណ្តាញហាង",
  "members.classification.our_store": "ហាងនេះ",
  "members.classification.chain_store": "ហាងផ្សេង",
  "members.classification.non_chain": "ក្រៅបណ្តាញហាង",
  "members.record": "កំណត់ត្រា",
  "members.registeredAt": "ចុះឈ្មោះនៅ",
  "members.sharedWith": "សមតុល្យរួមជាមួយ",
  "members.noStoredValue": "មិនមាននៅក្នុងគម្រោងនេះ",
  "members.topUp": "បញ្ចូលទឹកប្រាក់",
  "members.adjust": "កែសមតុល្យ",
  "members.setPin": "កំណត់លេខសម្ងាត់",
  "members.pin": "លេខសម្ងាត់",
  "members.pinSet": "បានកំណត់",
  "members.pinUnset": "មិនទាន់កំណត់",
  "members.amount": "ចំនួនទឹកប្រាក់",
  "members.topUpHint": "ប្រាក់រង្វាន់តាមថ្នាក់ត្រូវបានបន្ថែមដោយស្វ័យប្រវត្តិ។",
  "members.adjustPoints": "កែពិន្ទុ",
  "members.balanceHistory": "ប្រវត្តិសមតុល្យ",
  "members.pointsHistory": "ប្រវត្តិពិន្ទុ",
  "members.noMovements": "មិនទាន់មានចលនាទឹកប្រាក់ទេ។",
  "members.noPoints": "មិនទាន់មានពិន្ទុទេ។",
  "members.movement": "ចលនា",
  "members.balanceAfter": "សមតុល្យបន្ទាប់",
  "members.whereStore": "កន្លែង",
  "members.movement.topup": "បញ្ចូលទឹកប្រាក់",
  "members.movement.spend": "បានចំណាយ",
  "members.movement.refund": "បានសងវិញ",
  "members.movement.adjust": "ការកែតម្រូវ",
  "members.points.earn": "បានទទួល",
  "members.points.redeem": "បានប្តូរយក",
  "members.points.adjust": "ការកែតម្រូវ",
  "members.points.expire": "ផុតកំណត់",
  "balance.title": "សមតុល្យហាង",
  "balance.description": "អ្វីដែលអ្នកជំពាក់សមាជិក និងហាងណាដែលទទួលបាន។",
  "balance.liability": "ជំពាក់សមាជិក",
  "balance.holders": "សមាជិកដែលមានសមតុល្យ",
  "balance.byStore": "តាមហាង",
  "balance.toppedUp": "បានទទួល",
  "balance.spent": "បានបម្រើ",
  "balance.held": "នៅសល់",
  "balance.share": "ចំណែក",
  "loyalty.title": "ភាពស្មោះត្រង់",
  "loyalty.description": "ប្រាក់រង្វាន់បញ្ចូលទឹកប្រាក់ និងអ្វីដែលវិក្កយបត្រទទួលបាន។",
  "loyalty.tiers": "ថ្នាក់បញ្ចូលទឹកប្រាក់",
  "loyalty.noTiers": "មិនទាន់មានថ្នាក់ទេ — គ្មានប្រាក់រង្វាន់។",
  "loyalty.tierReads": "បង់ {pay} ទទួល {get}",
  "loyalty.pay": "បង់",
  "loyalty.bonus": "រង្វាន់",
  "loyalty.points": "ពិន្ទុ",
  "loyalty.pointsEnabled": "ផ្តល់ពិន្ទុលើវិក្កយបត្របានទូទាត់",
  "loyalty.perPoint": "ចំណាយក្នុងមួយពិន្ទុ",
  "loyalty.pointWorth": "ពិន្ទុមួយមានតម្លៃ",
  "loyalty.minimumRedeemable": "ពិន្ទុតិចបំផុតដែលអាចប្តូរ",
  "action.yes": "បាទ/ចាស",
  "action.no": "ទេ",
  "weekday.0": "អាទិត្យ",
  "weekday.1": "ចន្ទ",
  "weekday.2": "អង្គារ",
  "weekday.3": "ពុធ",
  "weekday.4": "ព្រហស្បតិ៍",
  "weekday.5": "សុក្រ",
  "weekday.6": "សៅរ៍",
  "nav.marketing": "ទីផ្សារ",
  "nav.coupons": "គូប៉ុង",
  "nav.promos": "ប្រូម៉ូសិន",
  "coupons.title": "គូប៉ុង",
  "coupons.description": "អ្វីដែលអ្នកផ្តល់ ជូនអ្នកណា និងតម្លៃប៉ុន្មាន។",
  "coupons.name": "គូប៉ុង",
  "coupons.worth": "តម្លៃ",
  "coupons.minSpend": "ចំណាយអប្បបរមា",
  "coupons.issued": "បានចេញ",
  "coupons.used": "បានប្រើ",
  "coupons.given": "បានផ្តល់",
  "coupons.status": "ស្ថានភាព",
  "coupons.live": "ដំណើរការ",
  "coupons.off": "បិទ",
  "coupons.design": "បង្កើតគូប៉ុង",
  "coupons.copy": "ចម្លង",
  "coupons.send": "ផ្ញើ",
  "coupons.switchOn": "បើក",
  "coupons.switchOff": "បិទ",
  "coupons.kind": "ប្រភេទ",
  "coupons.kind.amount": "បញ្ចុះជាទឹកប្រាក់",
  "coupons.kind.percent": "បញ្ចុះជាភាគរយ",
  "coupons.amountOff": "ទឹកប្រាក់បញ្ចុះ",
  "coupons.percentOff": "ភាគរយបញ្ចុះ គិតជាចំណុចមូលដ្ឋាន",
  "coupons.maxDiscount": "ច្រើនបំផុត",
  "coupons.maxDiscountHint": "ទុកទទេ បើគ្មានដែនកំណត់។",
  "coupons.record": "កំណត់ត្រា",
  "coupons.limits": "ចំនួនដែលចេញ",
  "coupons.issueLimit": "សរុប",
  "coupons.perMemberLimit": "ក្នុងសមាជិកម្នាក់",
  "coupons.window": "ពេលដំណើរការ",
  "coupons.validFrom": "ចាប់ពី",
  "coupons.validTo": "រហូតដល់",
  "coupons.performance": "លទ្ធផលដែលទទួលបាន",
  "promos.title": "ប្រូម៉ូសិន",
  "promos.description": "ចំណាយ និងសន្សំ តាមលំដាប់ដែលពិចារណា។",
  "promos.name": "ប្រូម៉ូសិន",
  "promos.offer": "ការផ្តល់ជូន",
  "promos.when": "ពេលណា",
  "promos.stacks": "អាចបន្ថែម",
  "promos.add": "បន្ថែមប្រូម៉ូសិន",
  "promos.reads": "ចំណាយ {threshold} សន្សំ {reduction}",
  "promos.threshold": "ចំណាយយ៉ាងតិច",
  "promos.reduction": "បញ្ចុះ",
  "promos.days": "ថ្ងៃ",
  "promos.everyDay": "រាល់ថ្ងៃ",
  "promos.deleteTitle": "លុបប្រូម៉ូសិននេះមែនទេ?",
  "promos.deleteBody":
    "ប្រូម៉ូសិននេះនឹងបាត់ជាអចិន្ត្រៃយ៍ មិនមែនផ្អាកទេ — ការបិទវាបញ្ឈប់ការផ្ដល់ជូន ហើយរក្សាទុកសម្រាប់ពេលក្រោយ។ វិក្កយបត្រដែលបានគិតតម្លៃរួចហើយមិនផ្លាស់ប្ដូរទេ។",
  "promos.record": "កំណត់ត្រា",
  "promos.order": "លំដាប់ពិចារណា",
  "promos.hours": "ម៉ោង",
  "promos.allDay": "ពេញមួយថ្ងៃ",
  "promos.validFrom": "ចាប់ពី",
  "promos.validTo": "រហូតដល់",
  "pos.offline.ordering": "ការទទួលការបញ្ជាទិញ",
  "pos.offline.payment": "សាច់ប្រាក់ និងការទូទាត់ក្រៅបណ្តាញ",
  "pos.offline.printing": "ការបោះពុម្ពវិក្កយបត្រ",
  "pos.offline.noIncomingOrders": "ការបញ្ជាទិញពីម៉ាស៊ីនផ្សេងនឹងមិនមកដល់ទេ",
  "pos.offline.noQrOrders": "ការបញ្ជាទិញតាម QR ពេលក្រៅបណ្តាញនឹងមិនបង្ហាញទេ",
  "pos.offline.noOnlinePayment": "ការទូទាត់តាមអនឡាញ និងសមតុល្យសមាជិកមិនអាចប្រើបានទេ",
  "pos.offline.noReports": "របាយការណ៍ និងស្តុកនឹងមិនទាន់ថ្មី",
  "pos.offline.willSync": "អ្វីដែលបានធ្វើក្រៅបណ្តាញនឹងត្រូវផ្ញើឥឡូវនេះ — អាចចំណាយពេលបន្តិច",
  "pos.sync.syncing": "កំពុងធ្វើសមកាលកម្មទិន្នន័យ…",
  "pos.sync.waiting": "{count} កំពុងរង់ចាំផ្ញើ",
  "pos.sync.needsAttention": "{count} មិនអាចផ្ញើបាន",
  "pos.failures.title": "មិនអាចផ្ញើបាន",
  "pos.failures.intro":
    "ម៉ាស៊ីនមេបានបដិសេធទាំងនេះ ហើយនឹងបដិសេធជានិច្ច។ នីមួយៗត្រូវការការសម្រេចចិត្ត — ជាពិសេសការទូទាត់ ដែលជាប្រាក់របស់អតិថិជន។",
  "pos.failures.none": "គ្មានអ្វីរង់ចាំអ្នកទេ។",
  "pos.failures.money": "ការទូទាត់",
  "pos.failures.change": "ការផ្លាស់ប្តូរ",
  "pos.failures.queuedAt": "បានធ្វើនៅ {at}",
  "pos.failures.dismiss": "ខ្ញុំបានដោះស្រាយរួចហើយ",
  "pos.failures.close": "បិទ",

  "pos.bill.startTitle":
    "\u1794\u17be\u1780\u179c\u17b7\u1780\u17d2\u1780\u1799\u1794\u178f\u17d2\u179a",
  "pos.bill.startBody":
    "\u179f\u17bc\u1798\u1787\u17d2\u179a\u17be\u179f\u179a\u17be\u179f\u178f\u17bb \u1783\u17be\u1794\u17be\u1780\u179c\u17b7\u1780\u17d2\u1780\u1799\u1794\u178f\u17d2\u179a\u1799\u1780\u1791\u17c5\u1795\u17d2\u1791\u17c7 \u1798\u17bb\u1793\u1796\u17c1\u1798\u1794\u1789\u17d2\u1785\u17bc\u179b\u1798\u17bb\u1781\u1798\u17d2\u17a0\u17bc\u1794\u17d4",
  "pos.bill.chooseTable": "\u1787\u17d2\u179a\u17be\u179f\u179a\u17be\u179f\u178f\u17bb",
  "pos.bill.heldLabel": "{name} — កំពុងផ្អាក",
  "pos.bill.newTakeAway": "\u1799\u1780\u1791\u17c5\u1795\u17d2\u1791\u17c7",
  "pos.bill.itemsBlocked":
    "\u179f\u17bc\u1798\u1794\u17be\u1780\u179c\u17b7\u1780\u17d2\u1780\u1799\u1794\u178f\u17d2\u179a\u1798\u17bb\u1793\u1796\u17c1\u1798\u1794\u1789\u17d2\u1785\u17bc\u179b\u1798\u17bb\u1781\u1798\u17d2\u17a0\u17bc\u1794",
  "pos.bill.open":
    "\u179c\u17b7\u1780\u17d2\u1780\u1799\u1794\u178f\u17d2\u179a\u1794\u17be\u1780\u1785\u17c6\u17a0",
  "pos.bill.opening": "\u1780\u17c6\u1796\u17bb\u1784\u1794\u17be\u1780\u2026",
  "pos.bill.notSent":
    "\u1798\u17b7\u1793\u1791\u17b6\u1793\u1794\u17b6\u1793\u1795\u17d2\u1789\u17be",
  "pos.bill.failed":
    "\u1798\u17b7\u1793\u17a2\u17b6\u1785\u1794\u17be\u1780\u179c\u17b7\u1780\u17d2\u1780\u1799\u1794\u178f\u17d2\u179a\u1793\u17c1\u17c7\u1794\u17b6\u1793\u1791\u17c1",
  "pos.bill.clearTable": "\u179f\u1798\u17d2\u17a2\u17b6\u178f\u178f\u17bb",
  "pos.bill.clearTableConfirm":
    "\u178a\u17c4\u17c7\u179b\u17c2\u1784 {table} \u1783? \u1798\u17b7\u1793\u1791\u17b6\u1793\u1798\u17b6\u1793\u1780\u17b6\u179a\u1794\u1789\u17d2\u1787\u17b6\u1791\u17b7\u1789\u17a2\u17d2\u179c\u17b8\u1793\u17c5\u179b\u17be\u179c\u17b6\u1791\u17c1\u17d4",
  "pos.bill.clearTableKeep": "\u179a\u1780\u17d2\u179f\u17b6\u1791\u17bb\u1780",
  "pos.bill.discardBill":
    "\u1794\u17c4\u17c7\u1794\u1784\u17cb\u179c\u17b7\u1780\u17d2\u1780\u1799\u1794\u178f\u17d2\u179a",
  "pos.bill.discardBillConfirm":
    "\u1794\u17c4\u17c7\u1794\u1784\u17cb\u179c\u17b7\u1780\u17d2\u1780\u1799\u1794\u178f\u17d2\u179a\u1793\u17c1\u17c7? \u1798\u17b7\u1793\u1791\u17b6\u1793\u17cb\u1798\u17b6\u1793\u1780\u17b6\u179a\u1794\u1789\u17d2\u1787\u17b6\u1791\u17b7\u1789\u17a2\u17d2\u179c\u17b8\u1793\u17c5\u179b\u17be\u179c\u17b6\u1791\u17c1\u17d4",
  "pos.bill.discard": "\u1794\u17c4\u17c7\u1794\u1784\u17cb",
  "pos.bill.release": "\u178a\u17c4\u17c7\u179b\u17c2\u1784",
  "pos.bill.occupied": "\u1798\u17b6\u1793\u1797\u17d2\u1789\u17c0\u179c",
  "pos.bill.openThisBill":
    "\u1794\u17be\u1780\u179c\u17b7\u1780\u17d2\u1780\u1799\u1794\u178f\u17d2\u179a\u1793\u17c1\u17c7",
  "pos.bill.resumeOffline":
    "\u179c\u17b7\u1780\u17d2\u1780\u1799\u1794\u178f\u17d2\u179a\u1793\u17c1\u17c7\u178f\u17d2\u179a\u17bc\u179c\u1794\u17b6\u1793\u1794\u17be\u1780\u1793\u17c5\u1798\u17c9\u17b6\u179f\u17ca\u17b8\u1793\u1795\u17d2\u179f\u17c1\u1784\u17d4 \u179f\u17bc\u1798\u1797\u17d2\u1787\u17b6\u1794\u17cb\u17a2\u17ca\u17b8\u1793\u1792\u17ba\u178e\u17b7\u178f\u178a\u17be\u1798\u17d2\u1794\u17b8\u1794\u17be\u1780\u179c\u17b6\u1793\u17c5\u1791\u17b8\u1793\u17c1\u17c7\u17d4",
  "pos.cart.table": "តុ",
  "pos.cart.takeAway": "យកទៅ",
  "pos.cart.covers": "{count} នាក់",
  "pos.cart.editCovers": "ចុចឱ្យជាប់ដើម្បីប្តូរចំនួនភ្ញៀវ",
  "pos.cart.empty": "មិនទាន់មានអ្វីនៅលើវិក្កយបត្រនេះទេ។",
  "pos.cart.ordered": "បានបញ្ជាទិញ ({count})",
  "pos.cart.latestOrder": "ការបញ្ជាទិញចុងក្រោយ ({count})",
  "pos.cart.subtotal": "សរុបរង",
  "pos.cart.discount": "បញ្ចុះតម្លៃ",
  "pos.cart.addDiscount": "បន្ថែម",
  "pos.cart.service": "សេវា",
  "pos.cart.total": "សរុប",
  "pos.cart.taxFree": "លើកលែងពន្ធ",
  "pos.cart.taxFreeApply": "អនុវត្ត",
  "pos.cart.taxFreeRemove": "ដកចេញ",
  "pos.cart.hold": "ផ្អាក",
  "pos.cart.resume": "បន្ត",
  "pos.cart.sendToStation": "ផ្ញើទៅស្ថានីយ",
  "pos.cart.settle": "ទូទាត់",
  "pos.sync.busy": "កំពុងធ្វើសមកាលកម្ម…",
  "pos.sync.failed": "{count} បរាជ័យ",
  "pos.sync.queued": "{count} ក្នុងជួរ",
  "pos.menu.all": "ទាំងអស់",
  "pos.menu.loading": "កំពុងផ្ទុកម៉ឺនុយ…",
  "pos.menu.soldOut": "អស់ស្តុក",
  "pos.menu.searchPlaceholder": "ស្វែងរកតាមឈ្មោះ ឬលេខកូដ",
  "pos.menu.result": "{count} លទ្ធផល",
  "pos.menu.results": "{count} លទ្ធផល",
  "pos.menu.inCart": "{count} ក្នុងកន្ត្រក",
  "pos.item.quantity": "ចំនួន",
  "pos.item.note": "កំណត់សម្គាល់សម្រាប់ស្ថានីយ",
  "pos.item.notePlaceholder": "កុំដាក់ទឹកកក ហឹរបន្ថែម…",
  "pos.item.chooseOne": "ជ្រើសរើសមួយ",
  "pos.item.chooseAny": "ជ្រើសរើសយ៉ាងតិចមួយ",
  "pos.item.optional": "ស្រេចចិត្ត",
  "pos.item.cancel": "បោះបង់",
  "pos.item.choose": "ជ្រើសរើស {group}",
  "pos.item.add": "បន្ថែម {total}",
  "pos.mode.online": "លើបណ្តាញ",
  "pos.mode.offline": "ក្រៅបណ្តាញ",
  "pos.mode.goOffline": "ចេញទៅក្រៅបណ្តាញ?",
  "pos.mode.comeOnline": "ត្រឡប់មកលើបណ្តាញវិញ?",
  "pos.mode.stillAble": "ម៉ាស៊ីនគិតលុយនេះនៅតែអាច៖",
  "pos.mode.butNot": "ប៉ុន្តែមិនអាច៖",
  "pos.mode.allSent": "អ្វីៗត្រូវបានផ្ញើរួចហើយ។",
  "pos.mode.stayOnline": "នៅលើបណ្តាញ",
  "pos.mode.stayOffline": "នៅក្រៅបណ្តាញ",
  "pos.mode.switchToOnline": "ទៅលើបណ្តាញ",
  "pos.mode.switchToOffline": "ទៅក្រៅបណ្តាញ",
  "pos.identify.heading": "ស្កេនដើម្បីចូល",
  "pos.identify.instruction":
    "សូមបែរកាមេរ៉ាទូរស័ព្ទរបស់អ្នកមកកាន់កូដនេះ រួចបញ្ជាក់នៅក្នុងកម្មវិធីបុគ្គលិក។",
  "pos.identify.codeLabel": "ឬវាយបញ្ចូលកូដនេះ:",
  "pos.identify.denied": "ការចូលនេះត្រូវបានបដិសេធ។ សូមស្កេនម្តងទៀត។",
  "pos.identify.notConfigured":
    "ម៉ាស៊ីនគិតលុយនេះមិនទាន់កំណត់អាសយដ្ឋានកម្មវិធីបុគ្គលិកនៅឡើយទេ ដូច្នេះគ្មាននរណាម្នាក់អាចចូលនៅទីនេះបានទេ។ សូមសួរអ្នកដែលបានដំឡើងម៉ាស៊ីននេះ។",
  "pos.identify.unavailable":
    "ម៉ាស៊ីនគិតលុយនេះមិនអាចបង្ហាញកូដចូលបានឥឡូវនេះទេ។ សូមពិនិត្យការតភ្ជាប់។",
  "pos.identify.retry": "សាកល្បងម្តងទៀត",
  "pos.identify.choice.checkIn": "ចូលបញ្ជី",
  "pos.identify.choice.take": "យកម៉ាស៊ីនគិតលុយ",
  "pos.identify.choice.checkInAndTake": "ចូលបញ្ជី និងយកម៉ាស៊ីនគិតលុយ",
  "pos.identify.choice.checkOut": "ចេញបញ្ជី",
  "pos.identify.choice.cancel": "បោះបង់",
  "pos.identify.choice.jobHeading": "ការងារមួយណា?",
  "pos.identify.choice.noJobs": "ហាងនេះមិនទាន់បានកំណត់ការងារណាមួយនៅឡើយទេ។",
  "pos.identify.choice.elsewhere": "កំពុងចូលបញ្ជីនៅហាងផ្សេងរួចហើយ។",
  "pos.identify.choice.checkedIn": "ចូលបញ្ជីរួចរាល់ហើយ។",
  "pos.identify.choice.checkedOut": "ចេញបញ្ជីរួចរាល់ហើយ។",
  "pos.shift.title": "ថតប្រាក់",
  "pos.shift.failed": "មិនបានសម្រេចទេ។ សូមព្យាយាមម្តងទៀត។",
  "pos.shift.handOver": "ប្រគល់វេន",
  "pos.shift.noneOpen": "គ្មានវេនបើកទេ។ រាប់ប្រាក់ដើម រួចបើកវេនដើម្បីចាប់ផ្តើមទទួលប្រាក់។",
  "pos.shift.openingFloat": "ប្រាក់ដើម",
  "pos.shift.open": "បើកវេន",
  "pos.shift.openNow": "កំពុងបើក",
  "pos.shift.number": "វេន {number}",
  "pos.shift.openedBy": "បើកដោយ",
  "pos.shift.bills": "វិក្កយបត្របានទូទាត់",
  "pos.shift.byMethod": "បានទទួល តាមវិធីទូទាត់",
  "pos.shift.nothingYet": "មិនទាន់មានការទទួលប្រាក់ទេ។",
  "pos.shift.expectedInDrawer": "គួរមាននៅក្នុងថត",
  "pos.shift.railCounts": "អ្វីដែលបណ្ដាញនីមួយៗរាយការណ៍",
  "pos.shift.railCountsHint": "ពិនិត្យកម្មវិធីធនាគារ ឬម៉ាស៊ីន។ ទុកទទេប្រសិនបើអ្នកមិនបានពិនិត្យ។",
  "pos.shift.cardNotInDrawer": "ការទូទាត់តាមកាត និងអនឡាញមិននៅក្នុងថតទេ ដូច្នេះមិនរាប់នៅទីនេះ។",
  "pos.shift.counted": "បានរាប់",
  "pos.shift.balances": "ត្រឹមត្រូវ។",
  "pos.shift.short": "ខ្វះ {amount}",
  "pos.shift.over": "លើស {amount}",
  "pos.shift.close": "បិទវេន",
  "pos.settle.title": "ទូទាត់",
  "pos.settle.total": "សរុប",
  "pos.settle.stillToPay": "នៅសល់ត្រូវបង់",
  "pos.settle.change": "ប្រាក់អាប់",
  "pos.settle.tip": "ប្រាក់រង្វាន់",
  "pos.settle.noTip": "គ្មានប្រាក់រង្វាន់",
  "pos.settle.tipOther": "ចំនួនផ្សេង",
  "pos.settle.due": "ត្រូវបង់",
  "pos.station.title": "កន្លែងចម្អិន",
  "pos.station.backToTill": "ម៉ាស៊ីនគិតលុយ",
  "pos.station.all": "គ្រប់ស្ថានីយ",
  "pos.station.empty": "គ្មានអ្វីត្រូវធ្វើទេ។",
  "pos.station.waiting": "{minutes} នាទី",
  "pos.station.course": "វគ្គទី {number}",
  "pos.settle.amountTendered": "ចំនួនប្រាក់ដែលបានទទួល",
  "pos.settle.back": "ថយក្រោយ",
  "pos.settle.done": "រួចរាល់",
  "pos.billActions.title": "សកម្មភាពវិក្កយបត្រ",
  "pos.billActions.merge": "បញ្ចូលគ្នា",
  "pos.billActions.mergeHint": "បញ្ចូលវិក្កយបត្របើកមួយទៀតទៅក្នុងវិក្កយបត្រនេះ។ វានឹងបិទ។",
  "pos.billActions.noOthers": "មិនមានវិក្កយបត្រផ្សេងបើកទេ។",
  "pos.billActions.split": "បំបែក",
  "pos.billActions.splitHint": "ផ្លាស់ទីធាតុទៅវិក្កយបត្រថ្មី។",
  "pos.billActions.splitDo": "ផ្លាស់ទីទៅវិក្កយបត្រថ្មី",
  "pos.billActions.noFired": "មានតែធាតុដែលស្ថានីយទទួលរួចហើយទេដែលអាចផ្លាស់ទីបាន។",
  "pos.billActions.needsConnection": "ការបញ្ចូលគ្នា និងការបំបែក ត្រូវការការតភ្ជាប់។",
  "pos.billActions.back": "ថយក្រោយ",
  "pos.line.title": "កែប្រែធាតុ",
  "pos.line.void": "លុបចោល",
  "pos.line.return": "ប្រគល់មកវិញ",
  "pos.line.back": "ថយក្រោយ",
  "pos.discount.title": "បញ្ចុះតម្លៃ",
  "pos.discount.amount": "ចំនួនបញ្ចុះតម្លៃ",
  "pos.discount.tooLarge": "ការបញ្ចុះតម្លៃមិនអាចលើសពីវិក្កយបត្រទេ។",
  "pos.discount.back": "ថយក្រោយ",
  "pos.discount.clear": "លុបចោល",
  "pos.discount.apply": "អនុវត្ត",
  "pos.table.title": "តុ",
  "pos.table.seats": "កៅអី {count}",
  "pos.table.howMany": "{table} — ប៉ុន្មាននាក់?",
  "pos.table.status.free": "ទំនេរ",
  "pos.table.status.seated": "មានភ្ញៀវ",
  "pos.table.status.ordered": "បានបញ្ជាទិញ",
  "pos.table.status.billRequested": "សុំវិក្កយបត្រ",
  "pos.signIn.title": "រៀបចំម៉ាស៊ីនគិតលុយនេះ",
  "pos.signIn.subtitle": "អនុម័តពីការិយាល័យខាងក្រោយ។ បុគ្គលិកចូលដោយស្កេនបន្ទាប់ពីនេះ។",
  "pos.signIn.expiredTitle": "ចូលម៉ាស៊ីនគិតលុយនេះឡើងវិញ",
  "pos.signIn.expiredSubtitle":
    "វគ្គរបស់ម៉ាស៊ីននេះបានបញ្ចប់។ គ្មានអ្វីបាត់បង់ទេ — អ្វីដែលកំពុងរង់ចាំនឹងផ្ញើចេញ ពេលអ្នកចូលវិញ។",
  "pos.signIn.codeLabel": "កូដភ្ជាប់",
  "pos.signIn.instruction": "សុំឱ្យអ្នកគ្រប់គ្រងបើក {uri} ហើយបញ្ចូលកូដនេះ ឬស្កេនវា។",
  "pos.signIn.waiting": "កំពុងរង់ចាំការអនុម័ត…",
  "pos.signIn.retrying": "កំពុងសាកល្បងម្តងទៀត — កំពុងពិនិត្យការតភ្ជាប់…",
  "pos.signIn.denied": "សំណើភ្ជាប់ត្រូវបានបដិសេធ។",
  "pos.signIn.failed": "មានបញ្ហាកើតឡើង។",
  "pos.signIn.newCode": "បង្ហាញកូដថ្មី",

  "staff.signIn.title": "ចូលប្រើ",
  "staff.signIn.subtitle": "ប្រើ passkey របស់អ្នក ឬឈ្មោះអ្នកប្រើ និងពាក្យសម្ងាត់របស់អ្នក។",
  "staff.signIn.passkey": "ចូលប្រើដោយ passkey",
  "staff.signIn.or": "ឬ",
  "staff.signIn.username": "ឈ្មោះអ្នកប្រើ",
  "staff.signIn.password": "ពាក្យសម្ងាត់",
  "staff.signIn.connect": "ចូលប្រើ",
  "staff.signIn.rejected": "ព័ត៌មានទាំងនេះមិនត្រឹមត្រូវទេ។",
  "staff.signIn.passkeyRejected": "Passkey នោះមិនដំណើរការទេ។",
  "staff.signIn.unreachable": "មិនអាចភ្ជាប់ទៅម៉ាស៊ីនមេបានទេ។",
  "staff.home.greeting": "បានចូលក្នុងនាម",
  "staff.home.signOut": "ចាកចេញ",
  "staff.home.scan": "ស្កេនកូដម៉ាស៊ីនគិតលុយ",
  "staff.home.scanRefused":
    "នោះមិនមែនជាកូដម៉ាស៊ីនគិតលុយត្រឹមត្រូវទេ។ សូមបញ្ចូលវានៅខាងក្រោមជំនួសវិញ។",
  "staff.home.enterCode": "បញ្ចូលកូដម៉ាស៊ីនគិតលុយ",
  "staff.home.enterCodePlaceholder": "ឧ. ABCD-1234",
  "staff.home.enterCodeSubmit": "បន្ត",
  "staff.home.passkeyOffer": "ចូលប្រើលឿនជាងមុននៅពេលក្រោយ ដោយប្រើ passkey នៅលើទូរស័ព្ទនេះ។",
  "staff.home.passkeyAdd": "បន្ថែម passkey",
  "staff.home.passkeyAdded": "បានបន្ថែម passkey រួចរាល់។ លើកក្រោយ សូមជ្រើសរើសចូលប្រើដោយ passkey។",
  "staff.home.passkeyStale":
    "ដើម្បីសុវត្ថិភាពរបស់អ្នក សូមចាកចេញ រួចចូលប្រើម្តងទៀត បន្ទាប់មកបន្ថែម passkey។",
  "staff.home.passkeyFailed": "មិនអាចបន្ថែម passkey នៅលើឧបករណ៍នេះបានទេ។",

  "staff.approve.continueAfterSignIn": "ការចូលប្រើនឹងបន្តទៅសំណើររបស់ម៉ាស៊ីនគិតលុយ។",
  "staff.approve.checking": "កំពុងពិនិត្យកូដ…",
  "staff.approve.instruction": "ពិនិត្យមើលថាវាត្រូវនឹងកូដដែលបង្ហាញនៅលើម៉ាស៊ីនគិតលុយ រួចបញ្ជាក់។",
  "staff.approve.take": "នេះជាខ្ញុំ",
  "staff.approve.notMe": "មិនមែនខ្ញុំទេ",
  "staff.approve.approved": "បន្តធ្វើការនៅម៉ាស៊ីនគិតលុយ។",
  "staff.approve.backHome": "ត្រឡប់ទៅទំព័រដើម",
  "staff.approve.notForTill":
    "កូដនេះសម្រាប់តភ្ជាប់ម៉ាស៊ីនគិតលុយ មិនមែនសម្រាប់បញ្ជាក់អ្នកឈរនៅមុខវាទេ។",
  "staff.approve.taken": "អ្នកផ្សេងកំពុងអនុម័តកូដនេះរួចហើយ។",
  "staff.approve.used": "កូដនេះត្រូវបានប្រើរួចហើយ។",
  "staff.approve.gone": "កូដនេះលែងមានសុពលភាពទៀតហើយ។",
  "staff.approve.refused": "គណនីនេះមិនអាចយកម៉ាស៊ីនគិតលុយបានទេ។",
  "staff.approve.retry": "ព្យាយាមម្តងទៀត",

  "staff.approve.someoneElse": "អ្នកផ្សេង",
  "staff.approve.pickEmployee": "ដាក់នរណាម្នាក់ចូលម៉ាស៊ីនគិតលុយនេះ",
  "staff.approve.search": "ស្វែងរក",
  "staff.approve.confirmFor": "ដាក់ {name} ចូលម៉ាស៊ីនគិតលុយនេះ?",
  "staff.approve.assigned": "{name} នឹងចូលក្នុងម៉ាស៊ីនគិតលុយក្នុងរយៈពេលប៉ុន្មានវិនាទីទៀត។",
  "staff.approve.assignRefused": "មិនអាចដាក់អ្នកនោះចូលម៉ាស៊ីនគិតលុយបានទេ។",

  "diner.title": "បញ្ជាទិញ",
  "diner.scanPrompt": "ស្កេនកូដនៅលើតុរបស់អ្នក ដើម្បីមើលម៉ឺនុយ។",
  "diner.takeaway": "យកទៅ",
  "diner.closed": "ការបញ្ជាទិញត្រូវបានបិទនៅពេលនេះ។",
  "diner.all": "ទាំងអស់",
  "diner.soldOut": "អស់ស្តុក",
  "voice.newOrder": "មានការបញ្ជាទិញថ្មី។",
  "diner.sold": "លក់បាន {count}",
  "diner.sending": "កំពុងផ្ញើ…",
  "diner.order": "បញ្ជាទិញ {count} · {total}",
  "diner.sendFailed": "មិនអាចផ្ញើបានទេ។",
  "diner.locating": "កំពុងពិនិត្យទីតាំងរបស់អ្នក…",
  "diner.location.denied":
    "ភោជនីយដ្ឋាននេះត្រូវការទីតាំងរបស់អ្នក ដើម្បីទទួលការបញ្ជាទិញ។ សូមអនុញ្ញាតឱ្យប្រើទីតាំង ហើយព្យាយាមម្តងទៀត។",
  "diner.location.unavailable":
    "យើងរកមិនឃើញទីតាំងរបស់អ្នកទេ។ សូមប្រាកដថាទីតាំងត្រូវបានបើក ហើយព្យាយាមម្តងទៀត។",
  "diner.chooseOne": "· ជ្រើសរើសមួយ",
  "diner.chooseRange": "· ជ្រើសរើស {min}–{max}",
  "diner.chooseUpTo": "· រហូតដល់ {max}",
  "diner.extras": "គ្រឿងបន្ថែម",
  "diner.noteLabel": "មានអ្វីចង់ប្រាប់យើងទេ?",
  "diner.notePlaceholder": "សូមកុំដាក់ម្ទេស",
  "diner.chooseGroup": "ជ្រើសរើស {group}",
  "diner.add": "បន្ថែម · {total}",
  "diner.yourNumber": "លេខរបស់អ្នក",
  "diner.status.pending": "បានផ្ញើទៅរៀបចំ",
  "diner.status.accepted": "បានទទួល",
  "diner.status.preparing": "កំពុងធ្វើ",
  "diner.status.served": "បានបម្រើ",
  "diner.discount": "បញ្ចុះតម្លៃ",
  "diner.service": "សេវា",
  "diner.total": "សរុប",
  "diner.paid": "បានបង់ហើយ សូមអរគុណ។",
  "diner.payFirst": "សូមបង់ប្រាក់ដើម្បីបញ្ជាក់ការបញ្ជាទិញរបស់អ្នក។",
  "diner.payAtCounter": "សូមបង់ប្រាក់នៅបញ្ជរនៅពេលអ្នករួចរាល់។",
  "diner.couponCode": "លេខកូដប័ណ្ណ",
  "diner.couponFailed": "លេខកូដនេះមិនដំណើរការទេ។",
  "diner.member.signIn": "ចូលគណនី",
  "diner.member.signInTitle": "សមាជិកភាពរបស់អ្នក",
  "diner.member.signInExplainer":
    "ចូលដោយប្រើលេខទូរសព្ទ ដើម្បីមើលពិន្ទុ សមតុល្យ និងគូប៉ុងរបស់អ្នក និងដើម្បីទទួលពិន្ទុលើវិក្កយបត្រនេះ។",
  "diner.member.phone": "លេខទូរសព្ទ",
  "diner.member.sendCode": "ផ្ញើលេខកូដឲ្យខ្ញុំ",
  "diner.member.codeSent": "យើងបានផ្ញើលេខកូដទៅ {phone}។",
  "diner.member.devCode":
    "ម៉ាស៊ីនមេនេះមិនបានកំណត់សេវា SMS ទេ ដូច្នេះលេខកូដត្រូវបានបង្ហាញនៅទីនេះ៖ {code}",
  "diner.member.code": "លេខកូដ",
  "diner.member.nameOptional": "ឈ្មោះរបស់អ្នក (បើអ្នកជាសមាជិកថ្មី)",
  "diner.member.differentNumber": "ប្រើលេខផ្សេង",
  "diner.member.failed": "មិនបានសម្រេចទេ។ សូមព្យាយាមម្តងទៀត។",
  "diner.member.title": "សមាជិកភាពរបស់អ្នក",
  "diner.member.number": "សមាជិក {number}",
  "diner.member.points": "ពិន្ទុ",
  "diner.member.points.short": "{points} ពិន្ទុ",
  "diner.member.pointsWorth": "មានតម្លៃ {amount} នៅទីនេះ",
  "diner.member.noPointsScheme": "ភោជនីយដ្ឋាននេះមិនមានប្រព័ន្ធពិន្ទុទេ។",
  "diner.member.pointsHistory": "ពិន្ទុថ្មីៗ",
  "diner.member.balance": "សមតុល្យ",
  "diner.member.held": "{amount} កំពុងត្រូវបានទុកសម្រាប់វិក្កយបត្របើក",
  "diner.member.pinNeeded": "អ្នកនឹងត្រូវបញ្ចូលលេខសម្ងាត់ពេលទូទាត់ដោយប្រើវា។",
  "diner.member.topUp": "បញ្ចូលទឹកប្រាក់",
  "diner.member.topUpAtCounter": "សូមសួរនៅបញ្ជរដើម្បីបញ្ចូលទឹកប្រាក់ — មិនទាន់អាចធ្វើពីទីនេះបានទេ។",
  "diner.member.getsYou": "ទទួលបាន {amount}",
  "diner.member.coupons": "គូប៉ុង",
  "diner.member.noCoupons": "អ្នកមិនទាន់មានគូប៉ុងទេ។",
  "diner.member.minSpend": "ចំណាយយ៉ាងតិច {amount}",
  "diner.member.coupon.issued": "រួចរាល់",
  "diner.member.coupon.used": "បានប្រើ",
  "diner.member.coupon.expired": "ផុតកំណត់",
  "diner.member.coupon.void": "លុបចោល",
  "diner.member.earnOnThisBill": "ចូលគណនីដើម្បីទទួលពិន្ទុលើវិក្កយបត្រនេះ។",
  "diner.member.addToThisBill": "បន្ថែម {name} ទៅវិក្កយបត្រនេះដើម្បីទទួលពិន្ទុ។",
  "diner.member.claim": "បន្ថែមខ្ញុំទៅវិក្កយបត្រនេះ",
  "diner.member.onThisBill": "{name} នៅលើវិក្កយបត្រនេះ។",
  "diner.apply": "អនុវត្ត",
  "diner.orderMore": "បញ្ជាទិញបន្ថែម",
  "diner.cancelled": "ការបញ្ជាទិញនេះត្រូវបានលុបចោល",
  "reports.title": "របាយការណ៍",
  "reports.description": "តួលេខពីការបម្រើ តាមថ្ងៃធ្វើការរបស់អ្នក។",
  "reports.pick": "របាយការណ៍",
  "reports.from": "ពី",
  "reports.to": "ដល់",
  "reports.consolidate": "បញ្ចូលរយៈពេលទាំងមូលជាមួយជួរ",
  "reports.groupBy": "ដាក់ជាក្រុមតាម",
  "reports.columns": "ជួរឈរ",
  "reports.export": "នាំចេញ",
  "reports.exported": "បានដាក់ជួរ។ វានឹងបង្ហាញក្នុងការនាំចេញ។",
  "reports.total": "សរុប",
  "reports.empty": "គ្មានចំណូលក្នុងរយៈពេលនេះទេ។",
  "reports.master": "របាយការណ៍មេ",
  "reports.itemCategory": "ទំនិញ និងប្រភេទ",
  "reports.paymentMethods": "មធ្យោបាយទូទាត់",
  "reports.hourlySales": "ការលក់តាមម៉ោង",
  "reports.discounts": "ការបញ្ចុះតម្លៃ",
  "reports.voids": "ការលុប",
  "reports.topSelling": "លក់ដាច់ជាងគេ",
  "reports.daily": "ប្រចាំថ្ងៃ",
  "reports.tables": "តុ",
  "reports.shifts": "វេន",
  "reports.credit": "ការជំពាក់",
  "reports.stock": "ចលនាស្តុក",
  "reports.column.initialStock": "ស្តុកដើមគ្រា",
  "reports.column.inbound": "ចូល",
  "reports.column.outbound": "ចេញ",
  "reports.column.endingStock": "ស្តុកចុងគ្រា",
  "reports.column.group": "ការបែងចែក",
  "reports.column.store": "ហាង",
  "reports.column.orders": "ការបញ្ជាទិញ",
  "reports.column.covers": "ចំនួនភ្ញៀវ",
  "reports.column.grossSales": "ចំណូលសរុប",
  "reports.column.discount": "បញ្ចុះតម្លៃ",
  "reports.column.campaignDiscount": "បញ្ចុះតម្លៃយុទ្ធនាការ",
  "reports.column.netExTax": "សុទ្ធមិនរាប់ពន្ធ",
  "reports.column.netSales": "ចំណូលសុទ្ធ",
  "reports.column.tax": "ពន្ធ",
  "reports.column.serviceCharge": "ថ្លៃសេវា",
  "reports.column.netIncTax": "សុទ្ធរួមពន្ធ",
  "reports.column.payments": "បានទទួល",
  "reports.column.refunds": "បានសងវិញ",
  "reports.column.averageOrder": "វិក្កយបត្រមធ្យម",
  "reports.column.averageCover": "មធ្យមក្នុងមួយភ្ញៀវ",
  "reports.column.quantity": "បរិមាណ",
  "reports.column.method": "មធ្យោបាយ",
  "reports.column.tenders": "ចំនួនទូទាត់",
  "reports.column.taken": "បានទទួល",
  "reports.column.net": "សុទ្ធ",
  "reports.column.hour": "ម៉ោង",
  "reports.column.orderNo": "លេខបញ្ជាទិញ",
  "reports.column.date": "កាលបរិច្ឆេទ",
  "reports.column.who": "អ្នកណា",
  "reports.column.what": "អ្វី",
  "reports.column.detail": "ព័ត៌មានលម្អិត",
  "reports.column.reason": "មូលហេតុ",
  "reports.column.item": "ទំនិញ",
  "reports.column.rankByQuantity": "ចំណាត់ថ្នាក់តាមបរិមាណ",
  "reports.column.rankByRevenue": "ចំណាត់ថ្នាក់តាមចំណូល",
  "reports.column.table": "តុ",
  "reports.column.turnTime": "នាទីក្នុងមួយវេន",
  "reports.column.shift": "វេន",
  "reports.column.openedBy": "បើកដោយ",
  "reports.column.closedBy": "បិទដោយ",
  "reports.column.float": "ប្រាក់ដើម",
  "reports.column.expectedCash": "ថតគួរមាន",
  "reports.column.countedCash": "បានរាប់",
  "reports.column.difference": "ភាពខុសគ្នា",
  "reports.column.customer": "អតិថិជន",
  "reports.column.amount": "ចំនួនទឹកប្រាក់",
  "reports.column.settled": "បានទូទាត់",
  "reports.column.outstanding": "នៅជំពាក់",
  "reports.column.ageDays": "ចំនួនថ្ងៃបើក",
  "reports.column.status": "ស្ថានភាព",
  "reports.hourlySalesDetails": "ការលក់តាមម៉ោង និងតាមមុខម្ហូប",
  "reports.newItems": "មុខម្ហូបថ្មី",
  "reports.coupons": "គូប៉ុង",
  "reports.discountPromos": "យុទ្ធនាការបញ្ចុះតម្លៃ",
  "reports.sales": "ការលក់តាមបុគ្គលិក",
  "reports.items": "មុខម្ហូប និងការខាតបង់",
  "reports.storeCosts": "ចំណាយរបស់ហាង",
  "reports.itemCosts": "ថ្លៃដើមមុខម្ហូប",
  "reports.profit": "ប្រាក់ចំណេញ",
  "reports.tipReporting": "របាយការណ៍ប្រាក់រង្វាន់ (8027)",
  "reports.tipsByOccupation": "ប្រាក់រង្វាន់តាមមុខរបរ",
  "reports.labourHours": "ម៉ោងធ្វើការ",
  "reports.column.grossReceipts": "ចំណូលសរុប",
  "reports.column.chargedTips": "ប្រាក់រង្វាន់តាមកាត",
  "reports.column.chargeReceipts": "បង្កាន់ដៃកាតដែលមានប្រាក់រង្វាន់",
  "reports.column.cashTips": "ប្រាក់រង្វាន់ជាសាច់ប្រាក់",
  "reports.column.directlyTipped": "បង់ឲ្យអ្នកទទួលដោយផ្ទាល់",
  "reports.column.indirectlyTipped": "បង់ឲ្យអ្នកទទួលដោយប្រយោល",
  "reports.column.eightPercent": "៨% នៃចំណូលសរុប",
  "reports.column.job": "មុខងារ",
  "reports.column.occupationCode": "លេខកូដមុខរបរ",
  "reports.column.tipStatus": "ស្ថានភាពប្រាក់រង្វាន់",
  "reports.column.businessDate": "ថ្ងៃធ្វើអាជីវកម្ម",
  "reports.column.openEntries": "មិនទាន់ចេញ",
  "reports.column.minutes": "នាទី",
  "reports.unattributed": "គ្មានបុគ្គលិក",
  "reports.column.averageAmount": "ទឹកប្រាក់មធ្យម",
  "reports.column.addedOn": "បានបន្ថែម",
  "reports.column.labels": "ស្លាក",
  "reports.column.quantityShare": "ចំណែកនៃបរិមាណ",
  "reports.column.amountShare": "ចំណែកនៃការលក់",
  "reports.column.coupon": "គូប៉ុង",
  "reports.column.issued": "បានចេញ",
  "reports.column.redeemed": "បានប្រើ",
  "reports.column.expired": "ផុតកំណត់",
  "reports.column.redemptionRate": "អត្រាការប្រើប្រាស់",
  "reports.column.orderAmount": "សរុបវិក្កយបត្រ",
  "reports.column.discountShare": "ការបញ្ចុះតម្លៃធៀបនឹងវិក្កយបត្រ",
  "reports.column.promo": "យុទ្ធនាការ",
  "reports.column.returnedQuantity": "បានប្រគល់មកវិញ",
  "reports.column.voidedQuantity": "បានលុបចោល",
  "reports.column.wastageRate": "អត្រាខាតបង់",
  "reports.column.received": "បានទទួល",
  "reports.column.supplyCost": "ថ្លៃសម្ភារៈ",
  "reports.column.marketingCost": "ទីផ្សារ",
  "reports.column.laborCost": "ប្រាក់ឈ្នួល",
  "reports.column.administrativeCost": "ចំណាយរដ្ឋបាល",
  "reports.column.adHocCost": "ចំណាយម្ដងម្កាល",
  "reports.column.totalCosts": "ចំណាយសរុប",
  "reports.column.profit": "ប្រាក់ចំណេញ",
  "reports.column.grossMargin": "រង្វាស់ចំណេញ",
  "reports.column.openingCash": "សាច់ប្រាក់ដើមវេន",
  "reports.column.salesQty": "បរិមាណលក់",
  "reports.column.averageCost": "ថ្លៃដើមមធ្យម",
  "reports.column.originalPaid": "មុនបញ្ចុះតម្លៃ",
  "reports.column.actualPaid": "ក្រោយបញ្ចុះតម្លៃ",
  "reports.column.marginBeforeDiscount": "រង្វាស់ចំណេញមុនបញ្ចុះតម្លៃ",
  "reports.column.marginAfterDiscount": "រង្វាស់ចំណេញក្រោយបញ្ចុះតម្លៃ",
  "reports.column.creditTaken": "ជាប់ជំពាក់",
  "reports.column.expense": "បានចំណាយចេញ",
  "reports.column.profitBalance": "សមតុល្យចំណេញ",
  "costs.title": "ចំណាយរបស់ហាង",
  "costs.description": "អ្វីដែលអាជីវកម្មចំណាយ ជាមួយនឹងអ្វីដែលវាទទួលបាន។",
  "costs.add": "បន្ថែមចំណាយ",
  "costs.edit": "កែចំណាយ",
  "costs.date": "ថ្ងៃធ្វើអាជីវកម្ម",
  "costs.type": "សម្រាប់អ្វី",
  "costs.amount": "ចំនួនទឹកប្រាក់",
  "costs.note": "កំណត់សម្គាល់",
  "costs.category": "ក្រុម",
  "costs.enteredBy": "បញ្ចូលដោយ",
  "costs.empty": "គ្មានអ្វីត្រូវបានបញ្ចូលសម្រាប់ដំណាក់កាលនេះទេ។",
  "costs.deleteTitle": "លុបធាតុចំណាយនេះមែនទេ?",
  "costs.deleteBody": "របាយការណ៍ចំណាយរបស់ហាងនឹងលែងរាប់វា។ ចំណូលរបស់ហាងនៅថ្ងៃនោះមិនផ្លាស់ប្ដូរទេ។",
  "costs.record": "កំណត់ត្រា",
  "costs.category.marketing": "ទីផ្សារ",
  "costs.category.labor": "ប្រាក់ឈ្នួល",
  "costs.category.administrative": "ចំណាយរដ្ឋបាល",
  "costs.category.ad_hoc": "ចំណាយម្ដងម្កាល",
  "costs.type.platformFee": "ថ្លៃវេទិកា",
  "costs.type.advertising": "ថ្លៃផ្សាយពាណិជ្ជកម្ម",
  "costs.type.marketingDiscounts": "ការបញ្ចុះតម្លៃលើកទឹកចិត្ត",
  "costs.type.deliveryFee": "ថ្លៃដឹកជញ្ជូន",
  "costs.type.serviceFee": "ថ្លៃសេវា",
  "costs.type.salary": "ប្រាក់បៀវត្ស",
  "costs.type.salesCommission": "កម្រៃជើងសារលក់",
  "costs.type.overtime": "ថ្លៃធ្វើការលើសម៉ោង",
  "costs.type.insurance": "ថ្លៃធានារ៉ាប់រង",
  "costs.type.benefitsTraining": "អត្ថប្រយោជន៍ និងការបណ្ដុះបណ្ដាល",
  "costs.type.accommodation": "ប្រាក់ឧបត្ថម្ភកន្លែងស្នាក់នៅ",
  "costs.type.transportation": "ប្រាក់ឧបត្ថម្ភធ្វើដំណើរ",
  "costs.type.travel": "ប្រាក់ឧបត្ថម្ភបេសកកម្ម",
  "costs.type.rent": "ថ្លៃជួល",
  "costs.type.propertyFee": "ថ្លៃគ្រប់គ្រងអគារ",
  "costs.type.water": "ថ្លៃទឹក",
  "costs.type.electricity": "ថ្លៃភ្លើង",
  "costs.type.gas": "ថ្លៃឧស្ម័ន",
  "costs.type.renovation": "ការតុបតែង គិតរំលោះ",
  "costs.type.equipmentPurchase": "ការទិញឧបករណ៍",
  "costs.type.equipmentRepair": "ការជួសជុលឧបករណ៍",
  "costs.type.depreciation": "រំលោះឧបករណ៍",
  "costs.type.adHoc": "ចំណាយម្ដងម្កាល",
  "exports.title": "ការនាំចេញ",
  "exports.description": "ឯកសារដែលអ្នកបានស្នើ និងស្ថានភាពរបស់វា។",
  "exports.report": "របាយការណ៍",
  "exports.period": "រយៈពេល",
  "exports.rows": "ជួរ",
  "exports.requestedBy": "ស្នើដោយ",
  "exports.requested": "បានស្នើសុំ",
  "exports.download": "ទាញយក",
  "exports.expiresOn": "អាចទាញយកបានដល់ {date}",
  "exports.expired": "លែងមានទៀតហើយ។ សូមស្នើសុំម្ដងទៀត។",
  "exports.record": "ការនាំចេញ",
  "exports.completed": "បានបញ្ចប់",
  "exports.availability": "ភាពអាចរកបាន",
  "exports.failure": "មូលហេតុនៃការបរាជ័យ",
  "exportStatus.queued": "កំពុងរង់ចាំ",
  "exportStatus.running": "កំពុងដំណើរការ",
  "exportStatus.ready": "រួចរាល់",
  "exportStatus.failed": "បរាជ័យ",

  "dashboard.title": "ថ្ងៃនេះ",
  "dashboard.description": "ដំណើរការថ្ងៃនេះ ធៀបនឹងថ្ងៃដដែលសប្តាហ៍មុន។",
  "dashboard.updated": "បានធ្វើបច្ចុប្បន្នភាព",
  "dashboard.refresh": "ធ្វើបច្ចុប្បន្នភាពឥឡូវ",
  "dashboard.vsLastWeek": "ធៀបនឹងថ្ងៃដដែលសប្តាហ៍មុន",
  "dashboard.byMethod": "តាមមធ្យោបាយទូទាត់",
  "dashboard.ranking": "លក់ដាច់ជាងគេ",
  "dashboard.daily": "តាមថ្ងៃ",
  "dashboard.money.totalSales": "ចំណូលសរុប",
  "dashboard.money.received": "បានទទួល",
  "dashboard.money.credit": "ជំពាក់",
  "dashboard.money.unpaid": "នៅជំពាក់",
  "dashboard.money.discount": "ការបញ្ចុះតម្លៃ",
  "dashboard.money.refund": "បានសងវិញ",
  "dashboard.money.cancelled": "បានលុបចោល",
  "dashboard.counts.orders": "ការបញ្ជាទិញ",
  "dashboard.counts.receivedOrders": "បានបង់",
  "dashboard.counts.creditOrders": "ជំពាក់",
  "dashboard.counts.unpaidOrders": "នៅបើក",
  "dashboard.counts.discountOrders": "បានបញ្ចុះតម្លៃ",
  "dashboard.counts.refundOrders": "បានសងវិញ",
  "dashboard.counts.cancelledOrders": "បានលុបចោល",

  "auth.signIn": "ចូលប្រើ",
  "auth.signOut": "ចាកចេញ",
  "auth.email": "អ៊ីមែល",
  "auth.password": "ពាក្យសម្ងាត់",
  "auth.signingIn": "កំពុងចូល…",
  "auth.invalidCredentials": "អ៊ីមែល និងពាក្យសម្ងាត់មិនត្រឹមត្រូវទេ។",
  "auth.title": "ចូលប្រើ TOVI",
  "auth.subtitle": "ការិយាល័យខាងក្រោយ",
  "auth.showPassword": "បង្ហាញពាក្យសម្ងាត់",
  "auth.operatorTitle": "ចូលប្រើ TOVI Operator",
  "auth.operatorSubtitle": "សម្រាប់បុគ្គលិកប្រព័ន្ធតែប៉ុណ្ណោះ",
  "auth.operatorAccountTitle": "គណនីនេះជាកម្មសិទ្ធិរបស់ផ្ទាំងគ្រប់គ្រងប្រតិបត្តិករ។",
  "auth.operatorAccountBody": "សូមចូលប្រើដោយប្រើគណនីអ្នកជំនួញ ដើម្បីប្រើការិយាល័យខាងក្រោយ។",
  "auth.merchantAccountTitle": "គណនីនេះជាកម្មសិទ្ធិរបស់ការិយាល័យខាងក្រោយ។",
  "auth.merchantAccountBody": "សូមចូលប្រើដោយប្រើគណនីប្រតិបត្តិករ ដើម្បីប្រើផ្ទាំងគ្រប់គ្រងនេះ។",
  "auth.accountUnusableTitle": "គណនីនេះមិនអាចប្រើផ្ទាំងគ្រប់គ្រងនេះបានទេ។",
  "auth.accountUnusableBody":
    "វាអាចត្រូវបានផ្អាក ឬមិនទាន់រៀបចំរួចរាល់។ សូមឱ្យអ្នកគ្រប់គ្រងគណនីពិនិត្យមើល។",

  "shell.store": "ហាង",
  "shell.role": "តួនាទី",
  "shell.language": "ភាសា",
  "shell.loading": "កំពុងផ្ទុក…",
  "shell.openNav": "បើកម៉ឺនុយរុករក",
  "shell.closeNav": "បិទម៉ឺនុយរុករក",
  "shell.notifications": "ការជូនដំណឹង",
  "shell.account": "ម៉ឺនុយគណនី",
  "shell.venue": "ទីតាំងបច្ចុប្បន្ន",
  "shell.settings": "ការកំណត់",
  "shell.emptyTitle": "មិនទាន់មានអ្វីនៅទីនេះទេ",
  "shell.emptyBody":
    "នេះគឺជាផ្ទាំងបន្ទាប់ពីការចូលប្រើ។ ម៉ឺនុយ ការបញ្ជាទិញ និងរបាយការណ៍នឹងមកដល់នៅដំណាក់កាលក្រោយ។",

  "error.generic": "មានបញ្ហាកើតឡើង។",
  "error.notFound": "រកមិនឃើញ។",
  "error.forbidden": "អ្នកមិនមានសិទ្ធិធ្វើការនេះទេ។",
  "error.notEntitled": "វាមិនស្ថិតក្នុងគម្រោងរបស់អ្នកទេ។",
  "error.quotaExceeded": "អ្នកបានឈានដល់ដែនកំណត់របស់អ្នកហើយ។",

  /**
   * A refusal a merchant or a cashier actually meets, keyed by the `reason` the
   * API sends beside the code (`packages/shared/src/http/error-reasons.ts`).
   * The four keys above are the generic sentences; these are the specific ones,
   * and `resolveErrorMessage` prefers them over the server's English.
   */
  "error.inventoryForbidden": "អ្នកមិនមានសិទ្ធិចូលប្រើស្តុកនោះទេ។",
  "error.capabilityNotDelegated": "ការិយាល័យកណ្តាលមិនបានបើកមុខងារនេះសម្រាប់ហាងនេះទេ។",
  "error.rateLimited": "ព្យាយាមច្រើនដងពេក — សូមព្យាយាមម្តងទៀតក្នុងពេលបន្តិចទៀត។",
  "error.signInRequired": "សូមចូលគណនីដើម្បីបន្ត។",
  "error.accountDisabled": "គណនីនេះត្រូវបានបិទ។",
  "error.accountNoStore": "គណនីនេះមិនត្រូវបានភ្ជាប់ទៅហាងណាមួយទេ។",
  "error.storeNotAllowed": "អ្នកមិនអាចធ្វើការនៅហាងនោះទេ។",
  "error.pinRequired": "សូមចូលនៅម៉ាស៊ីនគិតលុយនេះដើម្បីបន្ត។",
  "error.pinSessionExpired": "ការចូលរបស់អ្នកនៅម៉ាស៊ីនគិតលុយនេះបានផុតកំណត់ហើយ។ សូមស្កេនម្តងទៀត។",
  "error.pinMismatch": "លេខ PIN មិនដូចគ្នាទេ។",
  "error.terminalRequired": "មានតែស្ថានីយប៉ុណ្ណោះដែលអាចកំណត់អត្តសញ្ញាណអ្នកគិតលុយបាន។",
  "error.grantNotRecognised": "ការចូលនេះបានផុតកំណត់ហើយ។ សូមស្កេនកូដម្តងទៀត។",
  "error.deviceCodeNotPending": "កូដនេះមិនអាចត្រូវបានអនុម័តជំនួសអ្នកផ្សេងបានទេ។",
  "error.alreadyClockedIn": "បានចូលបញ្ជីរួចហើយ។",
  "error.notClockedIn": "មិនទាន់បានចូលបញ្ជីទេ។",
  "error.clockedInElsewhere": "កំពុងចូលបញ្ជីនៅហាងផ្សេង មិនមែននៅទីនេះទេ។",
  "error.unitInUse": "មានសម្ភារៈស្តុកតាមឯកតានេះ។",
  "error.unitSeeded": "ឯកតាដើមមិនអាចលុបបានទេ គ្រាន់តែលាក់ដោយការតម្រៀប។",
  "error.unitSelfConversion": "ឯកតាមួយបម្លែងទៅខ្លួនឯងមួយទល់មួយ។",
  "error.unitDimensionMismatch": "ឯកតាទាំងពីរនេះវាស់វត្ថុខុសគ្នា ដូច្នេះមិនអាចបម្លែងបានទេ។",
  "error.supplyCategoryHasChildren": "សូមសម្អាតប្រភេទរងជាមុនសិន។",
  "error.supplyCategoryInUse": "មានសម្ភារៈស្ថិតក្នុងប្រភេទនេះ។",
  "error.supplyCategorySelfParent": "ប្រភេទមួយមិនអាចផ្ទុកខ្លួនឯងបានទេ។",
  "error.supplyCategoryTooDeep": "ប្រភេទមានត្រឹមពីរកម្រិត មិនអាចជ្រៅជាងនេះទេ។",
  "error.brandInUse": "មានសម្ភារៈប្រើម៉ាកនេះ។",
  "error.supplyHasStock": "នៅមានស្តុកសម្ភារៈនេះ។ សូមកាត់ចេញជាមុនសិន។",
  "error.supplyNumberTaken": "លេខនេះត្រូវបានប្រើរួចហើយ។",
  "error.barcodeTaken": "បាកូដនេះជារបស់សម្ភារៈផ្សេងរួចហើយ។",
  "error.menuCategoryInUse": "សូមផ្លាស់ទី ឬលុបមុខម្ហូបក្នុងប្រភេទនេះជាមុនសិន។",
  "error.itemNumberTaken": "មានមុខម្ហូបផ្សេងប្រើលេខកូដនេះរួចហើយ។",
  "error.tableAreaInUse": "សូមផ្លាស់ទី ឬលុបតុក្នុងតំបន់នេះជាមុនសិន។",
  "error.stationHasPrinters": "សូមផ្លាស់ប្តូរម៉ាស៊ីនបោះពុម្ពរបស់ស្ថានីយនេះទៅកន្លែងផ្សេងជាមុនសិន។",
  "error.stationHasItems": "សូមប្តូរផ្លូវមុខម្ហូបរបស់ស្ថានីយនេះទៅកន្លែងផ្សេងជាមុនសិន។",
  "error.printerHasItems": "មុខម្ហូបខ្លះបោះពុម្ពតែទៅម៉ាស៊ីននេះ។ សូមប្តូរផ្លូវវាជាមុនសិន។",
  "error.printerNeedsAddress": "សូមដាក់អាសយដ្ឋានឱ្យម៉ាស៊ីនបោះពុម្ពនេះមុននឹងភ្ជាប់។",
  "error.printerNeedsStation": "ម៉ាស៊ីនបោះពុម្ពស្ថានីយត្រូវការស្ថានីយសម្រាប់បញ្ជូនមុខម្ហូបទៅ។",
  "error.paymentMethodBuiltIn": "វិធីបង់ប្រាក់នេះមានស្រាប់ ហើយមិនអាចលុបបានទេ។ សូមបិទវាជំនួសវិញ។",
  "error.tipPoolPaidOut": "ក្រុមចែកថ្លៃបម្រើនេះបានចែករួចហើយ។ សូមបិទវាជំនួសឱ្យការលុប។",
  "error.tableAlreadyHasBill": "តុនេះមានវិក្កយបត្របើករួចហើយ។",
  "error.billAlreadySettled": "វិក្កយបត្រនេះត្រូវបានទូទាត់រួចហើយ។",
  "error.billNotCheckedOut": "វិក្កយបត្រនេះមិនទាន់បានគិតលុយទេ។",
  "error.orderCancelled": "ការបញ្ជាទិញនេះត្រូវបានលុបចោល។",
  "error.settledBillNeedsReverse": "សូមបញ្ច្រាសការគិតលុយមុននឹងកែវិក្កយបត្រដែលបានទូទាត់។",
  "error.moneyTakenUseRefund": "បានទទួលប្រាក់រួចហើយ។ សូមសងប្រាក់វិញ ឬបញ្ច្រាសជំនួសវិញ។",
  "error.nothingTakenYet": "មិនទាន់បានទទួលប្រាក់ទេ។",
  "error.nothingLeftToRefund": "គ្មានអ្វីនៅសល់ដើម្បីសងវិញទេ។",
  "error.refundExceedsTaken": "នេះលើសពីចំនួនដែលបានទទួល។",
  "error.paymentExceedsOutstanding": "នេះលើសពីចំនួនដែលវិក្កយបត្រនៅជំពាក់។",
  "error.paymentMethodUnavailable": "វិធីបង់ប្រាក់នេះមិនអាចប្រើនៅទីនេះទេ។",
  "error.paymentProviderNotInstalled": "វិធីបង់ប្រាក់នេះយោងទៅអ្នកផ្តល់សេវាដែលមិនបានដំឡើង។",
  "error.paymentRailPending": "ការបង់ប្រាក់នេះមិនទាន់រួចរាល់ទេ សូមព្យាយាមម្តងទៀតក្នុងពេលបន្តិចទៀត។",
  "error.paymentAsyncUnsupported":
    "វិធីបង់ប្រាក់នេះទូទាត់ក្រោយពេលអ្នកបង់ធ្វើសកម្មភាព ដែលម៉ាស៊ីនគិតលុយនេះមិនទាន់អាចរង់ចាំបានទេ។",
  "error.discountExceedsBill": "ការបញ្ចុះតម្លៃមិនអាចលើសពីវិក្កយបត្រទេ។",
  "error.returnExceedsOrdered": "ចំនួនដែលប្រគល់មកវិញច្រើនជាងចំនួនដែលបានបញ្ជាទិញ។",
  "error.takingsCounted":
    "ចំណូលថ្ងៃនោះត្រូវបានរាប់រួចហើយ។ ថ្លៃបម្រើអាចកែបានតែមុនថ្ងៃបញ្ចប់ប៉ុណ្ណោះ។",
  "error.tipNotSupportedOnTender": "ការទូទាត់ប្រភេទនេះមិនអាចបន្ថែមថ្លៃបម្រើបានទេ។",
  "error.mergeIntoSelf": "ការបញ្ជាទិញមិនអាចបញ្ចូលទៅក្នុងខ្លួនឯងបានទេ។",
  "error.mergeSourcePaid": "វិក្កយបត្រនោះបានទទួលប្រាក់រួចហើយ។",
  "error.splitNoSeats": "គ្មានបន្ទាត់ណាមួយក្នុងវិក្កយបត្រនេះកំណត់តាមកៅអីទេ។",
  "error.splitSingleSeat": "បន្ទាត់ទាំងអស់ស្ថិតលើកៅអីតែមួយ ដូច្នេះគ្មានអ្វីត្រូវបែងចែកទេ។",
  "error.splitLeavesEmptyBill": "ការបែងចែកបន្ទាត់ទាំងអស់ចេញនឹងធ្វើឱ្យវិក្កយបត្រនៅទទេ។",
  "error.courseNothingWaiting": "គ្មានអ្វីក្នុងវគ្គនោះកំពុងរង់ចាំធ្វើទេ។",
  "error.giftCardCodeRequired": "កាតអំណោយណាមួយ? ត្រូវការលេខកូដដើម្បីប្រើវា។",
  "error.giftCardVoided": "កាតនេះត្រូវបានលុបចោល។",
  "error.giftCardExpired": "កាតនេះបានផុតកំណត់។",
  "error.giftCardCurrencyMismatch": "កាតនេះជារូបិយប័ណ្ណផ្សេង។",
  "error.giftCardInsufficient": "កាតនេះមិនមានចំនួនច្រើនម្ល៉េះទេ។",
  "error.memberRequired": "សូមបញ្ជាក់សមាជិកមុននឹងប្រើសមតុល្យរបស់គេ។",
  "error.memberBlocked": "សមាជិកនេះត្រូវបានទប់ស្កាត់។",
  "error.memberBalanceInsufficient": "នេះលើសពីសមតុល្យរបស់សមាជិក។",
  "error.memberPointsInsufficient": "នេះលើសពីពិន្ទុដែលសមាជិកមាន។",
  "error.tabClosed": "គណនីជំពាក់នេះបានបិទរួចហើយ។",
  "error.tabNothingLeft": "គ្មានអ្វីនៅសល់លើគណនីជំពាក់នេះទេ។",
  "error.tabRepayExceeds": "នេះលើសពីចំនួនដែលគណនីជំពាក់នៅជំពាក់។",
  "error.tabRepaidWithTab": "គណនីជំពាក់មិនអាចសងដោយគណនីជំពាក់ផ្សេងបានទេ។",
  "error.couponSwitchedOff": "គូប៉ុងនេះត្រូវបានបិទ។",
  "error.couponUsed": "គូប៉ុងនេះត្រូវបានប្រើរួចហើយ។",
  "error.couponExpired": "គូប៉ុងនេះបានផុតកំណត់។",
  "error.couponNotYetValid": "គូប៉ុងនេះមិនទាន់អាចប្រើបានទេ។",
  "error.couponOtherMember": "គូប៉ុងនេះជារបស់សមាជិកដែលមិននៅលើវិក្កយបត្រនេះ។",
  "error.couponBillEmpty": "មិនទាន់មានអ្វីនៅលើវិក្កយបត្រនេះទេ។",
  "error.couponBelowMinimum": "វិក្កយបត្រនេះមិនដល់ចំនួនអប្បបរមារបស់គូប៉ុងទេ។",
  "error.orderLocationRequired": "ភោជនីយដ្ឋាននេះត្រូវការទីតាំងរបស់អ្នក ដើម្បីទទួលការបញ្ជាទិញ។",
  "error.orderTooFar": "អ្នកនៅឆ្ងាយពីភោជនីយដ្ឋានពេក មិនអាចបញ្ជាទិញបានទេ។",
  "error.shiftAlreadyOpen": "មានវេនបើករួចហើយ។ សូមបិទវាមុននឹងបើកវេនថ្មី។",
  "error.shiftAlreadyClosed": "វេននេះត្រូវបានបិទរួចហើយ។",
  "error.noShiftOpen": "គ្មានវេនបើកទេ។",
  "error.shiftTenderNothingTaken": "វេននេះមិនទទួលបានប្រាក់សោះលើវិធីទូទាត់មួយក្នុងចំណោមទាំងនោះទេ។",
  "error.lastRoleEditor": "នេះជាតួនាទីចុងក្រោយដែលអាចកែសម្រួលតួនាទីបាន។",
  "error.photoNotJpeg": "នោះមិនមែនជារូបភាព JPEG ទេ។",
  "error.photoTooLarge": "រូបថតនោះធំពេក។",
  "error.passwordForSelf": "អ្នកមិនអាចកំណត់ពាក្យសម្ងាត់ផ្ទាល់ខ្លួននៅទីនេះបានទេ។",
  "error.employeeOutranksYou":
    "អ្នកនេះអាចធ្វើអ្វីដែលតួនាទីរបស់អ្នកមិនអាចធ្វើបាន ដូច្នេះអ្នកមិនអាចកំណត់ពាក្យសម្ងាត់សម្រាប់គាត់បានទេ។",
  "error.roleOutranksYou": "អ្នកមិនអាចផ្តល់តួនាទីមួយដែលអាចធ្វើបានច្រើនជាងតួនាទីរបស់អ្នកបានទេ។",

  "operator.shell.console": "ផ្ទាំងគ្រប់គ្រងប្រតិបត្តិករ",
  "operator.shell.noRole": "គ្មានតួនាទី",

  "operator.nav.overview": "ទិដ្ឋភាពរួម",
  "operator.nav.merchants": "អ្នកជំនួញ",
  "operator.nav.catalog": "បញ្ជីផលិតផល",
  "operator.nav.finance": "ហិរញ្ញវត្ថុ",
  "operator.nav.platform": "វេទិកា",

  "operator.field.merchant": "អ្នកជំនួញ",
  "operator.field.merchants": "អ្នកជំនួញ",
  "operator.field.plan": "គម្រោង",
  "operator.field.status": "ស្ថានភាព",
  "operator.field.country": "ប្រទេស",
  "operator.field.partner": "ដៃគូ",
  "operator.field.code": "លេខកូដ",
  "operator.field.scope": "វិសាលភាព",
  "operator.field.price": "តម្លៃ",
  "operator.field.tier": "កម្រិត",
  "operator.field.expires": "ផុតកំណត់",
  "operator.field.stores": "ហាង",
  "operator.field.transactions": "ប្រតិបត្តិការ",
  "operator.field.permanent": "អចិន្ត្រៃយ៍",
  "operator.field.perMonth": "{price}/ខែ",
  "operator.field.monthly": "ប្រចាំខែ",
  "operator.field.yearly": "ប្រចាំឆ្នាំ",

  "operator.dashboard.title": "ផ្ទាំងគ្រប់គ្រង",
  "operator.dashboard.period": "{start} ដល់ {end}",
  "operator.dashboard.loadFailed": "មិនអាចផ្ទុកផ្ទាំងគ្រប់គ្រងបានទេ។",
  "operator.dashboard.new": "អ្នកជំនួញថ្មី",
  "operator.dashboard.newHint": "បានបន្ថែមក្នុងអំឡុងពេលនេះ",
  "operator.dashboard.cumulative": "សរុបបូកបញ្ចូល",
  "operator.dashboard.cumulativeHint": "ចំនួនសរុបក្នុងបញ្ជី",
  "operator.dashboard.active": "សកម្ម",
  "operator.dashboard.activeHint": "មានទឹកប្រាក់ប្រតិបត្តិការលើសពីសូន្យ",
  "operator.dashboard.activeRate": "អត្រាសកម្ម",
  "operator.dashboard.noPreviousPeriod": "គ្មានអំឡុងពេលមុន",
  "operator.dashboard.monthOnMonth": "{value} ធៀបនឹងខែមុន",
  "operator.dashboard.revenue": "ចំណូលពីការជាវ",
  "operator.dashboard.transactionsHint": "ការទូទាត់ថ្លៃជាវ",
  "operator.dashboard.transactionAmount": "ទឹកប្រាក់ប្រតិបត្តិការ",
  "operator.dashboard.pendingSettlement": "រង់ចាំទូទាត់សង",
  "operator.dashboard.settled": "បានទូទាត់សង",
  "operator.dashboard.allTime": "គ្រប់ពេលវេលា",
  "operator.dashboard.cumulativeRevenue": "ចំណូលសរុបបូកបញ្ចូល",
  "operator.dashboard.pendingPlusSettled": "រង់ចាំបូកនឹងបានទូទាត់សង",

  "operator.merchants.title": "អ្នកជំនួញ",
  "operator.merchants.description":
    "អ្នកជំនួញទាំងអស់នៅលើវេទិកា ព្រមទាំងគម្រោង និងស្ថានភាពជាវបច្ចុប្បន្នរបស់ពួកគេ។",
  "operator.merchants.empty": "គ្មានអ្នកជំនួញត្រូវនឹងការស្វែងរកនេះទេ។",
  "operator.merchants.search": "ស្វែងរកអ្នកជំនួញ",
  "operator.merchants.subscription": "ការជាវ",
  "operator.merchants.notProvisioned": "មិនទាន់បានរៀបចំ",
  "operator.merchants.anySubscription": "ការជាវទាំងអស់",
  "operator.merchants.anyStatus": "ស្ថានភាពទាំងអស់",
  "operator.merchants.new": "អ្នកជំនួញថ្មី",
  "operator.merchants.loadFailed": "មិនអាចផ្ទុកអ្នកជំនួញនេះបានទេ។",
  "operator.merchants.created": "{country} · បានបង្កើត {date}",
  "operator.merchants.signContract": "ចុះកិច្ចសន្យា",
  "operator.merchants.profile": "ព័ត៌មានទូទៅ",
  "operator.merchants.pilot": "អ្នកជំនួញសាកល្បង",
  "operator.merchants.channelPartner": "ដៃគូចែកចាយ",
  "operator.merchants.direct": "ផ្ទាល់",
  "operator.merchants.bdStaff": "បុគ្គលិកអភិវឌ្ឍន៍ជំនួញ",
  "operator.merchants.afterSalesStaff": "បុគ្គលិកសេវាក្រោយលក់",
  "operator.merchants.settlementTimezone": "ល្វែងម៉ោងសម្រាប់ការទូទាត់",
  "operator.merchants.internalRemarks": "កំណត់សម្គាល់ផ្ទៃក្នុង",
  "operator.merchants.subscriptions": "ការជាវ",
  "operator.merchants.perMonthExpires": "{price} ក្នុងមួយខែ · ផុតកំណត់ {date}",
  "operator.merchants.manage": "គ្រប់គ្រង",
  "operator.merchants.nothingProvisioned":
    "មិនទាន់បានរៀបចំអ្វីទេ។ ចុះកិច្ចសន្យាដើម្បីលក់គម្រោងជូនអ្នកជំនួញនេះ។",
  "operator.merchants.contracts": "កិច្ចសន្យា",
  "operator.merchants.contractSigned": "{plan} · បានចុះហត្ថលេខា {date}",
  "operator.merchants.noContracts": "មិនទាន់មានកិច្ចសន្យាទេ។",
  "operator.merchants.users": "អ្នកប្រើការិយាល័យគ្រប់គ្រង",
  "operator.merchants.createFailed": "មិនអាចបង្កើតអ្នកជំនួញបានទេ។",
  "operator.merchants.create": "បង្កើតអ្នកជំនួញ",
  "operator.merchants.creating": "កំពុងបង្កើត…",
  "operator.merchants.name": "ឈ្មោះអ្នកជំនួញ",
  "operator.merchants.countryHint": "ហាងដំបូងត្រូវបានរៀបចំតាមទីផ្សារនេះ៖ {currencies}, {locales}។",
  "operator.merchants.countryHintEmpty": "កំណត់ទម្រង់ទីផ្សារដែលហាងដំបូងត្រូវរៀបចំតាម។",
  "operator.merchants.currencyPair": "{first} និង {second}",
  "operator.merchants.selectMarket": "ជ្រើសរើសទីផ្សារ",
  "operator.merchants.firstStore": "ហាងដំបូង",
  "operator.merchants.storeName": "ឈ្មោះហាង",
  "operator.merchants.webAddress": "អាសយដ្ឋានគេហទំព័រ",
  "operator.merchants.webAddressHint": "ប្រើដោយគេហទំព័របញ្ជាទិញតាម QR។ អក្សរតូច លេខ និងសញ្ញាដក។",
  "operator.merchants.ownerUsername": "ឈ្មោះអ្នកប្រើរបស់ម្ចាស់",
  "operator.merchants.ownerName": "ឈ្មោះម្ចាស់",
  "operator.merchants.ownerEmail": "អ៊ីមែលរបស់ម្ចាស់",
  "operator.merchants.ownerPassword": "ពាក្យសម្ងាត់របស់ម្ចាស់",
  "operator.merchants.ownerPasswordHint":
    "យ៉ាងតិច ១២ តួអក្សរ។ ប្រគល់វាដោយសុវត្ថិភាព ព្រោះវាមិនបង្ហាញម្តងទៀតទេ។",

  "operator.contracts.title": "កិច្ចសន្យា",
  "operator.contracts.description":
    "ចុះហត្ថលេខាពីទំព័ររបស់អ្នកជំនួញ។ កិច្ចសន្យាដែលបានដាក់ស្នើមិនអាចកែបានទេ — ការផ្លាស់ប្តូរគឺជាការបន្តរយៈពេល ការតម្លើងគម្រោង ឬកិច្ចសន្យាថ្មី។",
  "operator.contracts.empty": "គ្មានកិច្ចសន្យាទេ។",
  "operator.contracts.number": "កិច្ចសន្យា",
  "operator.contracts.signed": "បានចុះហត្ថលេខា",
  "operator.contracts.term": "រយៈពេល",
  "operator.contracts.termValue": "{months} ខែ ដល់ {date}",
  "operator.contracts.total": "សរុប",
  "operator.contracts.signTitle": "ចុះកិច្ចសន្យាសម្រាប់ {merchant}",
  "operator.contracts.locked":
    "កិច្ចសន្យាដែលបានចុះហត្ថលេខាមិនអាចកែបានទេ។ ការផ្លាស់ប្តូរក្រោយមកគឺជាការបន្តរយៈពេល ការតម្លើងគម្រោង ឬកិច្ចសន្យាថ្មី។",
  "operator.contracts.sign": "ចុះហត្ថលេខា",
  "operator.contracts.signing": "កំពុងចុះហត្ថលេខា…",
  "operator.contracts.signFailed": "មិនអាចចុះកិច្ចសន្យាបានទេ។",
  "operator.contracts.planVersion": "កំណែគម្រោង",
  "operator.contracts.choosePlan": "ជ្រើសរើសគម្រោង",
  "operator.contracts.planOption": "{name} — {price}/ខែ",
  "operator.contracts.billedTo": "គិតថ្លៃលើ",
  "operator.contracts.billedToHint": "កិច្ចសន្យាខ្សែសង្វាក់ចែករំលែកកូតាឲ្យគ្រប់ហាងក្នុងក្រុម។",
  "operator.contracts.oneStore": "ហាងតែមួយ",
  "operator.contracts.wholeChain": "ខ្សែសង្វាក់ទាំងមូល",
  "operator.contracts.store": "ហាង",
  "operator.contracts.cycle": "វដ្តគិតថ្លៃ",
  "operator.contracts.yearlyAnnual": "ប្រចាំឆ្នាំ គិតថ្លៃម្តងក្នុងមួយឆ្នាំ",
  "operator.contracts.duration": "រយៈពេលគិតជាខែ",
  "operator.contracts.signDate": "ថ្ងៃចុះហត្ថលេខា",
  "operator.contracts.signDateHint": "ការជាវចាប់ផ្តើមនៅថ្ងៃនេះ។",
  "operator.contracts.trialMonths": "ចំនួនខែសាកល្បងឥតគិតថ្លៃ",
  "operator.contracts.trialMonthsHint":
    "បន្ថែមលើរយៈពេលកិច្ចសន្យា។ គម្រោងនឹងត្រឡប់មកវិញដោយស្វ័យប្រវត្តិនៅពេលការសាកល្បងបញ្ចប់។",
  "operator.contracts.discount": "បញ្ចុះតម្លៃ",
  "operator.contracts.discountHint": "គិតជាឯកតាតូចនៃ {currency}។",
  "operator.contracts.discountHintUnknown": "គិតជាឯកតាតូចនៃរូបិយប័ណ្ណរបស់គម្រោង។",
  "operator.contracts.estimate": "ប៉ាន់ស្មានសរុប {total}។ ម៉ាស៊ីនមេគណនាតម្លៃឡើងវិញពេលដាក់ស្នើ។",

  "operator.subscriptions.title": "ការជាវ",
  "operator.subscriptions.description":
    "អ្វីដែលអ្នកជំនួញនីមួយៗកាន់កាប់បច្ចុប្បន្ន និងរូបភាពដែលវាត្រូវបានលក់ក្រោមនោះ។",
  "operator.subscriptions.empty": "គ្មានការជាវទេ។",
  "operator.subscriptions.loadFailed": "មិនអាចផ្ទុកការជាវនេះបានទេ។",
  "operator.subscriptions.heading": "{merchant} — {plan}",
  "operator.subscriptions.subheading": "កម្រិត {tier} · {scope}",
  "operator.subscriptions.invalidate": "លុបចោលសុពលភាព",
  "operator.subscriptions.renew": "បន្តរយៈពេល",
  "operator.subscriptions.changePlan": "ប្តូរគម្រោង",
  "operator.subscriptions.addon": "កម្មវិធីបន្ថែម",
  "operator.subscriptions.term": "រយៈពេល",
  "operator.subscriptions.storedStatus": "ស្ថានភាពដែលរក្សាទុក",
  "operator.subscriptions.datesDisagree": "(កាលបរិច្ឆេទបញ្ជាក់ផ្សេង)",
  "operator.subscriptions.starts": "ចាប់ផ្តើម",
  "operator.subscriptions.trialEnds": "ការសាកល្បងបញ្ចប់",
  "operator.subscriptions.termLength": "ប្រវែងរយៈពេល",
  "operator.subscriptions.months": "{months} ខែ",
  "operator.subscriptions.priceSnapshot": "តម្លៃដែលបានថតទុក",
  "operator.subscriptions.perMonth": "{price} ក្នុងមួយខែ",
  "operator.subscriptions.quotaSnapshot": "កូតាដែលបានថតទុក",
  "operator.subscriptions.unmetered": "គ្មានដែនកំណត់",
  "operator.subscriptions.quotaWithExtra": "{allowed} ({limit} + {extra} បានទិញ)",
  "operator.subscriptions.features": "មុខងារ ({count})",
  "operator.subscriptions.featurePermanent": "អចិន្ត្រៃយ៍",
  "operator.subscriptions.addons": "កម្មវិធីបន្ថែម",
  "operator.subscriptions.noAddons": "មិនទាន់បានទិញទេ។",
  "operator.subscriptions.addonQuota": "{resource} × {quantity}",
  "operator.subscriptions.addonUntil": "រហូតដល់ {date}",
  "operator.subscriptions.renewTitle": "បន្តរយៈពេលការជាវ",
  "operator.subscriptions.addonTitle": "ទិញកម្មវិធីបន្ថែម",
  "operator.subscriptions.apply": "អនុវត្ត",
  "operator.subscriptions.applying": "កំពុងអនុវត្ត…",
  "operator.subscriptions.applyFailed": "មិនអាចអនុវត្តការផ្លាស់ប្តូរបានទេ។",
  "operator.subscriptions.priceIt": "គណនាតម្លៃ",
  "operator.subscriptions.priceFailed": "មិនអាចគណនាតម្លៃការផ្លាស់ប្តូរបានទេ។",
  "operator.subscriptions.monthsLabel": "ចំនួនខែ",
  "operator.subscriptions.renewCycleHint":
    "ការបន្តមុនកំណត់បន្ថែមពីថ្ងៃផុតកំណត់បច្ចុប្បន្ន ដូច្នេះរយៈពេលដែលនៅសល់មិនបាត់ទេ។",
  "operator.subscriptions.newPlan": "គម្រោងថ្មី",
  "operator.subscriptions.newPlanHint":
    "តែកម្រិតស្មើ ឬខ្ពស់ជាង។ ការបន្ថយកម្រិតមិនត្រូវបានអនុញ្ញាតទេ។",
  "operator.subscriptions.planTierOption": "{name} (កម្រិត {tier})",
  "operator.subscriptions.charged": "គិតថ្លៃ {amount} សម្រាប់រយៈពេលដែលនៅសល់",
  "operator.subscriptions.credited": "ជូនវិញ {amount} ពីគម្រោងបច្ចុប្បន្ន",
  "operator.subscriptions.net": "សុទ្ធ {amount}",
  "operator.subscriptions.addonType": "ប្រភេទកម្មវិធីបន្ថែម",
  "operator.subscriptions.extraQuota": "កូតាបន្ថែម",
  "operator.subscriptions.valueAddedFeature": "មុខងារលក់ដាច់ដោយឡែក",
  "operator.subscriptions.resource": "ធនធាន",
  "operator.subscriptions.extraUnits": "ឯកតាបន្ថែម",
  "operator.subscriptions.feature": "មុខងារ",
  "operator.subscriptions.featureHint": "គិតថ្លៃតាមចំនួនថ្ងៃដែលនៅសល់នៃរយៈពេលមេ។",
  "operator.subscriptions.chooseFeature": "ជ្រើសរើសមុខងារ",
  "operator.subscriptions.invalidateTitle": "លុបចោលសុពលភាពការជាវរបស់ {merchant}",
  "operator.subscriptions.invalidateWarning":
    "នេះមិនអាចត្រឡប់វិញបានទេ។ អ្នកជំនួញនឹងបាត់បង់គ្រប់មុខងារ និងកូតាភ្លាមៗ រួមទាំងអ្វីដែលបានទិញដាច់ ហើយការជាវមិនអាចបន្តឲ្យរស់ឡើងវិញបានទេ។",
  "operator.subscriptions.reason": "មូលហេតុ",
  "operator.subscriptions.reasonHint": "កត់ត្រាទុកលើការជាវនេះ។ យ៉ាងតិចដប់តួអក្សរ។",
  "operator.subscriptions.requestCode": "ស្នើសុំលេខកូដ",
  "operator.subscriptions.codeFailed": "មិនអាចស្នើសុំលេខកូដបានទេ។",
  "operator.subscriptions.invalidating": "កំពុងលុបចោលសុពលភាព…",
  "operator.subscriptions.invalidateFailed": "មិនអាចលុបចោលសុពលភាពបានទេ។",
  "operator.subscriptions.verificationCode": "លេខកូដផ្ទៀងផ្ទាត់",
  "operator.subscriptions.codeHintDev": "កំណែអភិវឌ្ឍន៍៖ លេខកូដត្រូវបានបំពេញជូនស្រាប់។",
  "operator.subscriptions.codeHintSent": "បានផ្ញើទៅបណ្តាញដែលអ្នកបានចុះឈ្មោះ។",

  "operator.plans.title": "កំណែគម្រោង",
  "operator.plans.description":
    "តម្លៃ កូតា និងសមាជិកភាពមុខងារគឺជាទិន្នន័យ។ ការកែវាមិនប្តូរអ្វីដែលការជាវដែលបានចុះហត្ថលេខារួចបានទិញនោះទេ។",
  "operator.plans.empty": "គ្មានកំណែគម្រោងទេ។",
  "operator.plans.search": "ស្វែងរកគម្រោង",
  "operator.plans.yearlyPerMonth": "ប្រចាំឆ្នាំ គិតជាមួយខែ",
  "operator.plans.annualSaving": "ការសន្សំប្រចាំឆ្នាំ",
  "operator.plans.quotas": "កូតា",
  "operator.plans.features": "មុខងារ",

  "operator.features.title": "មុខងារ",
  "operator.features.description":
    "កំណត់ត្រាពាណិជ្ជកម្មនៃបញ្ជីផលិតផល។ លេខកូដខ្លួនវាជារចនាសម្ព័ន្ធ — វាជាអ្វីដែលច្រកត្រួតពិនិត្យរបស់ API ពិនិត្យ — ដូច្នេះវាមិនអាចកែនៅទីនេះទេ។",
  "operator.features.empty": "គ្មានមុខងារទេ។",
  "operator.features.search": "ស្វែងរកមុខងារ",
  "operator.features.feature": "មុខងារ",
  "operator.features.group": "ក្រុម",
  "operator.features.grades": "លក់តាមកម្រិត",
  "operator.features.ungraded": "បើក ឬបិទ",
  "operator.features.valueAdded": "លក់ដាច់ដោយឡែក",

  "operator.hardware.title": "ឧបករណ៍",
  "operator.hardware.description": "អាចដាក់បញ្ចូលក្នុងកិច្ចសន្យា។",
  "operator.hardware.empty": "គ្មានឧបករណ៍ទេ។",
  "operator.hardware.search": "ស្វែងរកឧបករណ៍",
  "operator.hardware.name": "ឧបករណ៍",
  "operator.hardware.model": "ម៉ូដែល",

  "operator.transactions.title": "ប្រតិបត្តិការ",
  "operator.transactions.description":
    "ការទូទាត់ថ្លៃជាវ — មិនមែនការលក់របស់ភោជនីយដ្ឋានទេ។ នេះជាបញ្ជីគណនេយ្យនៅពីក្រោយប្លុកចំណូលនៃផ្ទាំងគ្រប់គ្រង។",
  "operator.transactions.empty": "គ្មានប្រតិបត្តិការទេ។",
  "operator.transactions.serial": "លេខរៀង",
  "operator.transactions.product": "ផលិតផល",
  "operator.transactions.amount": "ទឹកប្រាក់",
  "operator.transactions.paid": "បានបង់",
  "operator.transactions.settlement": "ការទូទាត់សង",
  "operator.transactions.reconciled": "បានផ្ទៀងផ្ទាត់",
  "operator.transactions.markReconciled": "សម្គាល់ថាបានផ្ទៀងផ្ទាត់",

  "operator.partners.title": "ដៃគូចែកចាយ",
  "operator.partners.description":
    "អ្នកលក់បន្តដែលទទួលបានកម្រៃជើងសារពីថ្លៃជាវដែលអ្នកជំនួញរបស់ពួកគេបង់។",
  "operator.partners.empty": "គ្មានដៃគូចែកចាយទេ។",
  "operator.partners.search": "ស្វែងរកដៃគូ",
  "operator.partners.partnerId": "លេខសម្គាល់ដៃគូ",
  "operator.partners.commission": "កម្រៃជើងសារ",
  "operator.partners.bonus": "ប្រាក់រង្វាន់ចុះកិច្ចសន្យាលើកដំបូង",
  "operator.partners.settles": "ទូទាត់សង",
  "operator.partners.settlesValue": "{from} → {to}",

  "operator.settlements.title": "ការទូទាត់សង",
  "operator.settlements.description":
    "កម្រៃជើងសារដែលវេទិកាជំពាក់អ្នកលក់បន្តរបស់ខ្លួន។ ការបញ្ចប់វគ្គមួយត្រូវការសវនករដែលបានតែងតាំង និងលេខកូដផ្ទៀងផ្ទាត់ ហើយគ្មានវិធីលុបវាចោលទេ។",
  "operator.settlements.empty": "គ្មានវគ្គទូទាត់សងទេ។",
  "operator.settlements.number": "ការទូទាត់សង",
  "operator.settlements.period": "អំឡុងពេល",
  "operator.settlements.periodValue": "{start} – {end}",
  "operator.settlements.gross": "ទឹកប្រាក់ប្រតិបត្តិការ",
  "operator.settlements.rate": "អត្រា",
  "operator.settlements.due": "ត្រូវបង់",
  "operator.settlements.paid": "បានបង់",
  "operator.settlements.auditor": "សវនករ",

  "operator.referrals.title": "ការណែនាំ",
  "operator.referrals.description": "អ្នកជំនួញដែលត្រូវបានណែនាំពីក្នុងម៉ាស៊ីនរបស់អ្នកជំនួញផ្សេងទៀត។",
  "operator.referrals.empty": "មិនទាន់មានការណែនាំទេ។",
  "operator.referrals.business": "អាជីវកម្ម",
  "operator.referrals.contact": "អ្នកទំនាក់ទំនង",
  "operator.referrals.phone": "ទូរស័ព្ទ",
  "operator.referrals.referredBy": "ណែនាំដោយ",
  "operator.referrals.received": "បានទទួល",

  "operator.team.title": "ក្រុមការងារ",
  "operator.team.description": "បុគ្គលិកវេទិកា និងគណនីដៃគូចែកចាយ។",
  "operator.team.empty": "គ្មានគណនីប្រតិបត្តិករទេ។",
  "operator.team.name": "ឈ្មោះ",
  "operator.team.username": "ឈ្មោះអ្នកប្រើ",
  "operator.team.email": "អ៊ីមែល",
  "operator.team.role": "តួនាទី",
  "operator.team.scope": "វិសាលភាព",
  "operator.team.ownPartner": "អ្នកជំនួញរបស់ដៃគូខ្លួនឯង",
  "operator.team.platform": "វេទិកា",
  "operator.team.search": "ស្វែងរកក្រុមការងារ",
  "operator.team.new": "ប្រតិបត្តិករថ្មី",
  "operator.team.edit": "កែសម្រួលប្រតិបត្តិករ",
  "operator.team.record": "កំណត់ត្រា",
  "operator.team.password": "ពាក្យសម្ងាត់",
  "operator.team.passwordHint": "យ៉ាងហោចណាស់ដប់ពីរតួអក្សរ។",
  "operator.team.setPassword": "កំណត់ពាក្យសម្ងាត់",
  "operator.team.setPasswordHint":
    "ដាក់ចេញពីគ្រប់វគ្គទាំងអស់ រួមទាំងអ្នកខ្លួនឯង ប្រសិនបើអ្នកកំពុងកំណត់ពាក្យសម្ងាត់ផ្ទាល់ខ្លួនរបស់អ្នកឡើងវិញ។",
  "operator.team.deleteTitle": "លុបប្រតិបត្តិករនេះ?",
  "operator.team.deleteBody":
    "ពួកគេលែងអាចចូលបានទៀត ហើយឈ្មោះអ្នកប្រើ និងអ៊ីមែលរបស់ពួកគេនឹងទំនេរឡើងវិញសម្រាប់គណនីថ្មី។ ឈ្មោះរបស់ពួកគេនៅតែមាននៅលើអ្នកជំនួញដែលពួកគេបានចុះហត្ថលេខា និងគ្រប់ជួរសវនកម្មដែលពួកគេបានសរសេរ។",

  "operator.roles.title": "តួនាទី",
  "operator.roles.description":
    "តួនាទីប្រតិបត្តិករ។ អាចផ្តល់តែសិទ្ធិប្រតិបត្តិករប៉ុណ្ណោះ — សិទ្ធិរបស់អ្នកជំនួញនៅទីនេះនឹងជាការផ្តល់ដែលគ្មានអ្វីពិនិត្យឡើយ។",
  "operator.roles.empty": "គ្មានតួនាទីទេ។",
  "operator.roles.role": "តួនាទី",
  "operator.roles.seeded": "បានបង្កើតស្រាប់",
  "operator.roles.members": "សមាជិក",
  "operator.roles.permissions": "សិទ្ធិ",
  "operator.roles.granted": "បានផ្តល់ {count}",
  "operator.roles.new": "តួនាទីថ្មី",
  "operator.roles.name": "ឈ្មោះ",
  "operator.roles.note": "កំណត់ចំណាំ",
  "operator.roles.deleteTitle": "លុបតួនាទីនេះមែនទេ?",
  "operator.roles.deleteBody":
    "គ្មាននរណាម្នាក់អាចត្រូវបានផ្តល់តួនាទីនេះទៀតឡើយ ហើយសិទ្ធិរបស់វានឹងត្រូវដកចេញ។ លេខកូដរបស់វានឹងអាចប្រើឡើងវិញបាន។",
  "operator.roles.noneGranted": "គ្មានសិទ្ធិណាមួយត្រូវបានផ្តល់ទេ។",
  "operator.roles.catalogueFailed":
    "មិនអាចផ្ទុកបញ្ជីសិទ្ធិបានទេ ដូច្នេះសិទ្ធិនីមួយៗត្រូវបានបង្ហាញតាមកូនសោររបស់វា។",
  "operator.roles.reassignPrompt":
    "បច្ចុប្បន្នកាន់កាប់ដោយ {count}។ ផ្ទេរពួកគេទៅតួនាទីផ្សេង មុននឹងលុប។",
  "operator.roles.reassignTo": "ផ្ទេរពួកគេទៅ",
  "operator.roles.chooseRole": "ជ្រើសរើសតួនាទី",
  "operator.roles.reassignConfirm": "ផ្ទេរ ហើយលុប",

  "operator.audit.title": "កំណត់ត្រាសវនកម្ម",
  "operator.audit.description":
    "រាល់សកម្មភាពប្រតិបត្តិករដែលធ្វើឲ្យប្រាក់ផ្លាស់ទី ឬប្តូរតម្លៃ។ បន្ថែមតែប៉ុណ្ណោះ — គ្មានច្រកសរសេរទេ។",
  "operator.audit.empty": "មិនទាន់មានកំណត់ត្រាទេ។",
  "operator.audit.when": "ពេលណា",
  "operator.audit.who": "នរណា",
  "operator.audit.action": "សកម្មភាព",
  "operator.audit.entity": "អង្គភាព",
  "operator.audit.changedFields": "វាលដែលបានប្តូរ",

  "chainStatus.active": "កំពុងដំណើរការ",
  "chainStatus.suspended": "ព្យួរ",
  "chainStatus.invalidated": "លុបចោលសុពលភាព",

  "storeStatus.active": "កំពុងដំណើរការ",
  "storeStatus.suspended": "ព្យួរ",
  "storeStatus.closed": "បានបិទ",

  "userStatus.active": "កំពុងដំណើរការ",
  "userStatus.disabled": "បានបិទដំណើរការ",

  "partnerStatus.active": "កំពុងដំណើរការ",
  "partnerStatus.suspended": "ព្យួរ",

  "planStatus.active": "កំពុងដំណើរការ",
  "planStatus.invalidated": "បានដកចេញ",

  "contractStatus.draft": "សេចក្តីព្រាង",
  "contractStatus.submitted": "បានដាក់ស្នើ",
  "contractStatus.active": "កំពុងដំណើរការ",
  "contractStatus.completed": "បានបញ្ចប់",
  "contractStatus.cancelled": "បានលុបចោល",

  "billingScope.store": "ហាង",
  "billingScope.chain": "ខ្សែសង្វាក់",

  "productKind.plan": "គម្រោង",
  "productKind.addon": "កម្មវិធីបន្ថែម",
  "productKind.hardware": "ឧបករណ៍",
  "productKind.package": "កញ្ចប់",

  "transactionSettlementStatus.pending": "រង់ចាំ",
  "transactionSettlementStatus.settled": "បានទូទាត់សង",

  "partnerSettlementStatus.draft": "សេចក្តីព្រាង",
  "partnerSettlementStatus.pending_audit": "រង់ចាំសវនកម្ម",
  "partnerSettlementStatus.completed": "បានបញ្ចប់",
  "partnerSettlementStatus.failed": "បរាជ័យ",

  "referralStatus.new": "ថ្មី",
  "referralStatus.contacted": "បានទាក់ទង",
  "referralStatus.converted": "បានក្លាយជាអតិថិជន",
  "referralStatus.rejected": "បានបដិសេធ",

  "featureGroup.cashier": "ម៉ាស៊ីនគិតលុយ",
  "featureGroup.data": "របាយការណ៍",
  "featureGroup.store_settings": "ការកំណត់ហាង",
  "featureGroup.marketing": "ទីផ្សារ",
  "featureGroup.item_management": "ម៉ឺនុយ",
  "featureGroup.order_handling": "ការបញ្ជាទិញ",
  "featureGroup.settlement": "ការទូទាត់",
  "featureGroup.inventory": "ស្តុក",
  "featureGroup.reservation": "ការកក់",
  "featureGroup.chain": "ខ្សែសង្វាក់",

  "grade.basic": "មូលដ្ឋាន",
  "grade.premium": "ពិសេស",
  // Permission labels — role screens render the catalogue's key through here
  // rather than the catalogue's English `name`.
  "permission.items.read": "មើលមុខម្ហូប",
  "permission.items.write": "បង្កើត និងកែសម្រួលមុខម្ហូប",
  "permission.items.delete": "លុបមុខម្ហូប",
  "permission.items.price": "ប្តូរតម្លៃមុខម្ហូប",
  "permission.categories.read": "មើលប្រភេទ",
  "permission.categories.write": "បង្កើត និងកែសម្រួលប្រភេទ",
  "permission.categories.delete": "លុបប្រភេទ",
  "permission.modifiers.read": "មើលក្រុមលក្ខណៈ គ្រឿងបន្ថែម និងស្លាក",
  "permission.modifiers.write": "កែសម្រួលក្រុមលក្ខណៈ គ្រឿងបន្ថែម និងស្លាក",
  "permission.modifiers.delete": "លុបក្រុមលក្ខណៈ គ្រឿងបន្ថែម និងស្លាក",
  "permission.tables.read": "មើលតុ និងតំបន់",
  "permission.tables.write": "បង្កើត និងកែសម្រួលតុ និងតំបន់",
  "permission.tables.delete": "លុបតុ និងតំបន់",
  "permission.orders.read": "មើលការបញ្ជាទិញ",
  "permission.orders.write": "ទទួល និងកែប្រែការបញ្ជាទិញ",
  "permission.orders.export": "នាំចេញការបញ្ជាទិញ",
  "permission.orders.discount": "អនុវត្តការបញ្ចុះតម្លៃ",
  "permission.orders.void": "លុបចោលការបញ្ជាទិញ ឬទំនិញមួយមុខ",
  "permission.orders.refund": "សងប្រាក់វិញ",
  "permission.orders.reverse": "បញ្ច្រាសការទូទាត់ដែលបានបញ្ចប់",
  "permission.orders.tax_exempt": "ទូទាត់ការបញ្ជាទិញដោយលើកលែងពន្ធ",
  "permission.orders.tip_adjust": "កែប្រាក់រង្វាន់បន្ទាប់ពីទូទាត់",
  "permission.hr.read": "មើលការងារ និងកាតម៉ោង",
  "permission.hr.write": "គ្រប់គ្រងការងារ និងកែកាតម៉ោង",
  "permission.hr.clock": "កត់ម៉ោងចូល និងចេញ",
  "permission.hr.tip_finalise": "បញ្ចប់ការចែកប្រាក់រង្វាន់",
  "permission.chain.royalty": "កំណត់ភាគរយកម្រៃ និងដាក់ថ្លៃចោទ",
  "permission.reservations.read": "មើលការកក់ និងបញ្ជីរង់ចាំ",
  "permission.reservations.write": "ទទួល និងរៀបចំកន្លែងអង្គុយសម្រាប់ការកក់",
  "permission.giftcards.read": "ស្វែងរកកាតអំណោយ",
  "permission.giftcards.write": "ចេញ និងលុបចោលកាតអំណោយ",
  "permission.settlement.pay": "ទទួលការទូទាត់",
  "permission.settlement.drawer": "បើកថតលុយ",
  "permission.shifts.read": "មើលកំណត់ត្រាវេន",
  "permission.shifts.write": "បើក និងប្រគល់វេន",
  "permission.staff.read": "មើលបុគ្គលិក",
  "permission.staff.write": "បង្កើត និងកែសម្រួលបុគ្គលិក",
  "permission.staff.delete": "លុបបុគ្គលិក",
  "permission.roles.read": "មើលតួនាទី",
  "permission.roles.write": "បង្កើត និងកែសម្រួលតួនាទី",
  "permission.settings.read": "មើលការកំណត់ហាង",
  "permission.settings.store": "កែសម្រួលព័ត៌មានហាង",
  "permission.settings.tax": "កែសម្រួលច្បាប់ពន្ធ",
  "permission.settings.charges": "កែសម្រួលថ្លៃសេវា និងថ្លៃតុ",
  "permission.settings.currency": "កែសម្រួលរូបិយប័ណ្ណ និងអត្រាប្តូរប្រាក់",
  "permission.settings.payment_methods": "កែសម្រួលមធ្យោបាយទូទាត់",
  "permission.printers.read": "មើលម៉ាស៊ីនបោះពុម្ព និងកំណត់ត្រាបោះពុម្ព",
  "permission.printers.write": "កែសម្រួល ភ្ជាប់ និងផ្តាច់ម៉ាស៊ីនបោះពុម្ព",
  "permission.printers.reprint": "បោះពុម្ពឯកសារឡើងវិញ",
  "permission.terminals.pair": "ភ្ជាប់ម៉ាស៊ីនលក់",
  "permission.terminals.assign_staff": "ដាក់នរណាម្នាក់នៅលើម៉ាស៊ីនលក់",
  "permission.templates.read": "មើលទម្រង់វិក្កយបត្រ",
  "permission.templates.write": "កែសម្រួលទម្រង់វិក្កយបត្រ",
  "permission.banners.read": "មើលបដា",
  "permission.banners.write": "បង្កើត និងកែសម្រួលបដា",
  "permission.banners.delete": "លុបបដា",
  "permission.marketing.read": "មើលគូប៉ុង និងប្រូម៉ូសិន",
  "permission.marketing.write": "បង្កើត និងកែសម្រួលគូប៉ុង និងប្រូម៉ូសិន",
  "permission.members.read": "មើលសមាជិក",
  "permission.members.write": "បង្កើត និងកែសម្រួលសមាជិក",
  "permission.members.balance": "បញ្ចូលទឹកប្រាក់ និងកែសមតុល្យសមាជិក",
  "permission.members.points": "កែពិន្ទុសមាជិក",
  "permission.reports.read": "មើលរបាយការណ៍",
  "permission.reports.export": "នាំចេញរបាយការណ៍",
  "permission.reports.cost": "មើលរបាយការណ៍ថ្លៃដើម និងប្រាក់ចំណេញ",
  "permission.audit.read": "មើលកំណត់ត្រាប្រតិបត្តិការ",
  "permission.billing.read": "មើលការជាវ",
  "permission.billing.manage": "ចុះហត្ថលេខា បន្ត និងទិញកម្មវិធីបន្ថែម",
  "permission.chain.read": "មើលទិន្នន័យសរុបទាំងខ្សែសង្វាក់",
  "permission.chain.write": "កំណត់រចនាសម្ព័ន្ធហាងក្នុងខ្សែសង្វាក់",
  "permission.admin.merchants.read": "មើលអ្នកជំនួញ",
  "permission.admin.merchants.write": "បង្កើត និងកែសម្រួលអ្នកជំនួញ",
  "permission.admin.plans.read": "មើលកំណែគម្រោង និងមុខងារ",
  "permission.admin.plans.write": "កែសម្រួលកំណែគម្រោង និងមុខងារ",
  "permission.admin.contracts.read": "មើលកិច្ចសន្យា",
  "permission.admin.contracts.sign": "ចុះហត្ថលេខា និងបន្តកិច្ចសន្យា",
  "permission.admin.subscriptions.read": "មើលការជាវ",
  "permission.admin.subscriptions.write": "រៀបចំ ដំឡើងកម្រិត និងបន្តការជាវ",
  "permission.admin.subscriptions.invalidate": "លុបចោលសុពលភាពការជាវ",
  "permission.admin.hardware.read": "មើលឧបករណ៍",
  "permission.admin.hardware.write": "កែសម្រួលឧបករណ៍",
  "permission.admin.partners.read": "មើលដៃគូចែកចាយ",
  "permission.admin.partners.write": "កែសម្រួលដៃគូចែកចាយ",
  "permission.admin.settlements.read": "មើលការទូទាត់សងជាមួយដៃគូ",
  "permission.admin.settlements.complete": "បញ្ចប់ការទូទាត់សងជាមួយដៃគូ",
  "permission.admin.reconciliation.read": "មើលការផ្សះផ្សាគណនី",
  "permission.admin.team.read": "មើលក្រុមការងារវេទិកា",
  "permission.admin.team.write": "កែសម្រួលក្រុមការងារវេទិកា និងតួនាទីរបស់ពួកគេ",
  "permission.admin.dashboard.read": "មើលផ្ទាំងគ្រប់គ្រងវេទិកា",
  "permission.admin.referrals.read": "មើលការណែនាំ",
  "permission.admin.referrals.write": "ធ្វើបច្ចុប្បន្នភាពស្ថានភាព និងម្ចាស់នៃការណែនាំ",
  "permission.psi.warehouse.read": "មើលឃ្លាំង",
  "permission.psi.warehouse.write": "បង្កើត និងកែសម្រួលឃ្លាំង",
  "permission.psi.supply.read": "មើលសម្ភារៈ ឯកតា ប្រភេទ និងម៉ាក",
  "permission.psi.supply.write": "បង្កើត និងកែសម្រួលសម្ភារៈ ឯកតា ប្រភេទ និងម៉ាក",
  "permission.psi.supplier.read": "មើលអ្នកផ្គត់ផ្គង់",
  "permission.psi.supplier.write": "បង្កើត និងកែសម្រួលអ្នកផ្គត់ផ្គង់",
  "permission.psi.purchase.read": "មើលការទិញ",
  "permission.psi.purchase.write": "ចេញ និងកែសម្រួលការទិញ",
  "permission.psi.purchase.approve": "អនុម័ត ឬលុបចោលការទិញ",
  "permission.psi.inbound.read": "មើលប័ណ្ណចូលឃ្លាំង",
  "permission.psi.inbound.write": "ចេញ និងកែសម្រួលប័ណ្ណចូលឃ្លាំង",
  "permission.psi.inbound.approve": "អនុម័ត ឬបដិសេធប័ណ្ណចូលឃ្លាំង",
  "permission.psi.outbound.read": "មើលប័ណ្ណចេញឃ្លាំង",
  "permission.psi.outbound.write": "ចេញ និងកែសម្រួលប័ណ្ណចេញឃ្លាំង",
  "permission.psi.outbound.approve": "អនុម័តប័ណ្ណចេញឃ្លាំង",
  "permission.psi.count.read": "មើលការរាប់ស្តុក",
  "permission.psi.count.write": "ធ្វើការរាប់ស្តុក",
  "permission.psi.count.adjust": "ដាក់បញ្ចូលការកែតម្រូវស្តុក",
  "permission.psi.stock.read": "មើលសមតុល្យស្តុក និងជុំទំនិញ",
  "permission.psi.recipe.write": "ភ្ជាប់មុខម្ហូបទៅនឹងសម្ភារៈ",
  "permission.psi.requisition.approve": "ត្រួតពិនិត្យសំណើសុំទំនិញរបស់ហាងរង",

  "operationAction.billing.renew": "ការជាវត្រូវបានបន្តរយៈពេល",
  "operationAction.campaign.create": "យុទ្ធនាការត្រូវបានបង្កើត",
  "operationAction.campaign.delete": "យុទ្ធនាការត្រូវបានលុប",
  "operationAction.campaign.update": "យុទ្ធនាការត្រូវបានកែប្រែ",
  "operationAction.cancel": "ការកក់ត្រូវបានលុបចោល",
  "operationAction.chain.capabilities_changed": "សមត្ថភាពហាងត្រូវបានផ្លាស់ប្តូរ",
  "operationAction.chain.menu_pushed": "ម៉ឺនុយត្រូវបានផ្ញើទៅហាង",
  "operationAction.chain.requisition_approved": "សំណើសុំទំនិញត្រូវបានយល់ព្រម",
  "operationAction.chain.requisition_rejected": "សំណើសុំទំនិញត្រូវបានបដិសេធ",
  "operationAction.chain.store_added": "ហាងត្រូវបានបន្ថែមទៅខ្សែសង្វាក់",
  "operationAction.chain.user_access_changed": "សិទ្ធិចូលប្រើហាងត្រូវបានផ្លាស់ប្តូរ",
  "operationAction.cost.create": "ការចំណាយត្រូវបានបញ្ចូល",
  "operationAction.cost.delete": "ការចំណាយត្រូវបានលុប",
  "operationAction.cost.update": "ការចំណាយត្រូវបានកែប្រែ",
  "operationAction.coupon.issue": "គូប៉ុងត្រូវបានចេញ",
  "operationAction.coupon_template.copy": "គំរូគូប៉ុងត្រូវបានចម្លង",
  "operationAction.coupon_template.create": "គំរូគូប៉ុងត្រូវបានបង្កើត",
  "operationAction.coupon_template.update": "គំរូគូប៉ុងត្រូវបានកែប្រែ",
  "operationAction.create": "ការកក់ត្រូវបានបង្កើត",
  "operationAction.gift_card.issue": "កាតអំណោយត្រូវបានចេញ",
  "operationAction.gift_card.top_up": "កាតអំណោយត្រូវបានបញ្ចូលទឹកប្រាក់",
  "operationAction.gift_card.void": "កាតអំណោយត្រូវបានលុបចោល",
  "operationAction.inventory.inbound_approved": "ប័ណ្ណទំនិញចូលត្រូវបានយល់ព្រម",
  "operationAction.inventory.inbound_rejected": "ប័ណ្ណទំនិញចូលត្រូវបានបដិសេធ",
  "operationAction.inventory.outbound_issued": "ប័ណ្ណទំនិញចេញត្រូវបានចេញ",
  "operationAction.inventory.purchase_notified": "ការបញ្ជាទិញត្រូវបានផ្ញើទៅអ្នកផ្គត់ផ្គង់",
  "operationAction.inventory.purchase_raised": "ការបញ្ជាទិញត្រូវបានបង្កើត",
  "operationAction.inventory.purchase_received": "ការបញ្ជាទិញត្រូវបានទទួល",
  "operationAction.inventory.purchase_voided": "ការបញ្ជាទិញត្រូវបានលុបចោល",
  "operationAction.inventory.recipe_changed": "រូបមន្តមុខម្ហូបត្រូវបានផ្លាស់ប្តូរ",
  "operationAction.inventory.stock_adjusted": "ស្តុកត្រូវបានកែតម្រូវ",
  "operationAction.inventory.supplier_prices_changed": "តម្លៃអ្នកផ្គត់ផ្គង់ត្រូវបានផ្លាស់ប្តូរ",
  "operationAction.inventory.transfer_accepted": "ការផ្ទេរត្រូវបានទទួលយក",
  "operationAction.inventory.transfer_rejected": "ការផ្ទេរត្រូវបានបដិសេធ",
  "operationAction.item.batch": "មុខម្ហូបច្រើនត្រូវបានកែប្រែជាបណ្តុំ",
  "operationAction.item.create": "មុខម្ហូបត្រូវបានបន្ថែម",
  "operationAction.item.delete": "មុខម្ហូបត្រូវបានលុប",
  "operationAction.item.meal_set_changed": "សំណុំម្ហូបត្រូវបានផ្លាស់ប្តូរ",
  "operationAction.item.update": "មុខម្ហូបត្រូវបានកែប្រែ",
  "operationAction.job.create": "មុខតំណែងត្រូវបានបន្ថែម",
  "operationAction.job.delete": "មុខតំណែងត្រូវបានលុប",
  "operationAction.job.update": "មុខតំណែងត្រូវបានកែប្រែ",
  "operationAction.no_show": "ការកក់ត្រូវបានសម្គាល់ថាមិនមកដល់",
  "operationAction.notifications.update": "ការកំណត់ការជូនដំណឹងត្រូវបានកែប្រែ",
  "operationAction.payment_method.create": "មធ្យោបាយទូទាត់ត្រូវបានបន្ថែម",
  "operationAction.payment_method.delete": "មធ្យោបាយទូទាត់ត្រូវបានលុប",
  "operationAction.print.reprint": "បង្កាន់ដៃត្រូវបានបោះពុម្ពម្តងទៀត",
  "operationAction.printer.bind": "ម៉ាស៊ីនបោះពុម្ពត្រូវបានផ្សំ",
  "operationAction.printer.create": "ម៉ាស៊ីនបោះពុម្ពត្រូវបានបន្ថែម",
  "operationAction.printer.unbind": "ម៉ាស៊ីនបោះពុម្ពត្រូវបានផ្តាច់",
  "operationAction.promotion.create": "ប្រូម៉ូសិនត្រូវបានបង្កើត",
  "operationAction.promotion.delete": "ប្រូម៉ូសិនត្រូវបានលុប",
  "operationAction.promotion.update": "ប្រូម៉ូសិនត្រូវបានកែប្រែ",
  "operationAction.receipt_template.update": "ទម្រង់វិក្កយបត្រត្រូវបានកែប្រែ",
  "operationAction.role.create": "តួនាទីត្រូវបានបង្កើត",
  "operationAction.role.update": "តួនាទីត្រូវបានកែប្រែ",
  "operationAction.royalty.run": "ថ្លៃរ៉ូយ៉ាល់ទីត្រូវបានគណនា",
  "operationAction.royalty_charge.finalise": "ថ្លៃរ៉ូយ៉ាល់ទីត្រូវបានបញ្ចប់",
  "operationAction.royalty_scheme.create": "គម្រោងរ៉ូយ៉ាល់ទីត្រូវបានបង្កើត",
  "operationAction.royalty_scheme.set_stores": "ហាងក្នុងគម្រោងរ៉ូយ៉ាល់ទីត្រូវបានផ្លាស់ប្តូរ",
  "operationAction.royalty_scheme.update": "គម្រោងរ៉ូយ៉ាល់ទីត្រូវបានកែប្រែ",
  "operationAction.seat": "ភ្ញៀវត្រូវបានអញ្ជើញអង្គុយ",
  "operationAction.service_charge_rate.create": "អត្រាថ្លៃសេវាត្រូវបានបន្ថែម",
  "operationAction.service_charge_rate.delete": "អត្រាថ្លៃសេវាត្រូវបានលុប",
  "operationAction.service_charge_rate.update": "អត្រាថ្លៃសេវាត្រូវបានកែប្រែ",
  "operationAction.settings.delivery_providers_changed":
    "អ្នកផ្តល់សេវាដឹកជញ្ជូនត្រូវបានផ្លាស់ប្តូរ",
  "operationAction.settings.invoice_changed": "លេខរៀងវិក្កយបត្រត្រូវបានផ្លាស់ប្តូរ",
  "operationAction.settlement.drawer_opened": "ថតលុយត្រូវបានបើក",
  "operationAction.shift.close": "វេនត្រូវបានបិទ",
  "operationAction.specification_group.create": "ក្រុមលក្ខណៈត្រូវបានបង្កើត",
  "operationAction.specification_group.delete": "ក្រុមលក្ខណៈត្រូវបានលុប",
  "operationAction.specification_group.update": "ក្រុមលក្ខណៈត្រូវបានកែប្រែ",
  "operationAction.staff.create": "បុគ្គលិកត្រូវបានបន្ថែម",
  "operationAction.staff.delete": "បុគ្គលិកត្រូវបានលុប",
  "operationAction.staff.photo_delete": "រូបថតបុគ្គលិកត្រូវបានលុប",
  "operationAction.staff.photo_set": "រូបថតបុគ្គលិកត្រូវបានកំណត់",
  "operationAction.staff.reset_pin": "លេខសម្ងាត់បុគ្គលិកត្រូវបានកំណត់ឡើងវិញ",
  "operationAction.staff.take_till": "ម៉ាស៊ីនគិតលុយត្រូវបានយកដោយស្កេន",
  "operationAction.staff.assign_till": "ត្រូវបានដាក់លើម៉ាស៊ីនគិតលុយដោយអ្នកគ្រប់គ្រង",
  "operationAction.staff.update": "បុគ្គលិកត្រូវបានកែប្រែ",
  "operationAction.staff.set_password": "ពាក្យសម្ងាត់បុគ្គលិកត្រូវបានកំណត់",
  "operationAction.store_group.create": "ក្រុមហាងត្រូវបានបង្កើត",
  "operationAction.store_group.delete": "ក្រុមហាងត្រូវបានលុប",
  "operationAction.store_group.set_stores": "ហាងក្នុងក្រុមត្រូវបានផ្លាស់ប្តូរ",
  "operationAction.store_group.update": "ក្រុមហាងត្រូវបានកែប្រែ",
  "operationAction.store_profile.update": "ព័ត៌មានហាងត្រូវបានកែប្រែ",
  "operationAction.table_fee.update": "ថ្លៃតុត្រូវបានកែប្រែ",
  "operationAction.tax_rate.create": "អត្រាពន្ធត្រូវបានបន្ថែម",
  "operationAction.tax_rate.delete": "អត្រាពន្ធត្រូវបានលុប",
  "operationAction.tax_rate.update": "អត្រាពន្ធត្រូវបានកែប្រែ",
  "operationAction.tax_settings.update": "ការកំណត់ពន្ធត្រូវបានកែប្រែ",
  "operationAction.time_entry.correct": "កំណត់ត្រាម៉ោងធ្វើការត្រូវបានកែតម្រូវ",
  "operationAction.tip_distribution.finalise": "ការបែងចែកប្រាក់រង្វាន់ត្រូវបានបញ្ចប់",
  "operationAction.tip_pool.create": "ក្រុមប្រាក់រង្វាន់ត្រូវបានបង្កើត",
  "operationAction.tip_pool.delete": "ក្រុមប្រាក់រង្វាន់ត្រូវបានលុប",
  "operationAction.tip_pool.run": "ក្រុមប្រាក់រង្វាន់ត្រូវបានគណនា",
  "operationAction.tip_pool.set_jobs": "មុខតំណែងក្នុងក្រុមប្រាក់រង្វាន់ត្រូវបានផ្លាស់ប្តូរ",
  "operationAction.tip_pool.update": "ក្រុមប្រាក់រង្វាន់ត្រូវបានកែប្រែ",
  "operationAction.tip_settings.update": "ការកំណត់ប្រាក់រង្វាន់ត្រូវបានកែប្រែ",
  "operationAction.topping_template.create": "គំរូគ្រឿងបន្ថែមត្រូវបានបង្កើត",
  "operationAction.topping_template.delete": "គំរូគ្រឿងបន្ថែមត្រូវបានលុប",
  "operationAction.topping_template.update": "គំរូគ្រឿងបន្ថែមត្រូវបានកែប្រែ",
  "operationAction.topup_tier.create": "កម្រិតបញ្ចូលទឹកប្រាក់ត្រូវបានបង្កើត",
  "operationAction.topup_tier.delete": "កម្រិតបញ្ចូលទឹកប្រាក់ត្រូវបានលុប",
  "operationAction.trading_config.update": "ម៉ោងធ្វើអាជីវកម្មត្រូវបានកែប្រែ",
};

/**
 * Every key in this pack is machine-drafted and awaits native review. Listing
 * them explicitly rather than asserting "the pack is done" is the honest state:
 * a reviewer removes keys from here as they sign each one off.
 */
export const KM_AWAITING_REVIEW: readonly string[] = Object.keys(km).filter(
  (key) => key !== "app.name",
);
